import {RunOrderCard,type PadRun} from "@/components/RunOrderCard";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import StoreBalancePayment from '@/components/StoreBalancePayment';
import { lowDriverFeeMessage, recommendedDriverFee, roundTripDrivingMiles, type DriverAreaQuote } from '../../../../lib/location/driverRequestRecommendation';
import { formatPostcode } from '../../../../lib/location/selection';
import CashOrderFields from '@/components/CashOrderFields';
import CashReturnsPanel,{CashReturnShopPrompt} from '@/components/CashReturnsPanel';
import {EMPTY_CASH_ORDER,cashOrderPayload} from '../../../../lib/cash-handling/shared';
import MenuSellingDays from '@/components/MenuSellingDays';
import AgencyAgreementPanel from "@/components/AgencyAgreementPanel";
import { ShopCoverEditor } from "@/components/ShopCoverEditor";
import { offerExpiryFormDate, offerExpiryFormValue } from "../../../../lib/ordering-visuals/offerExpiryForm";
import { ChatImageComposer, MessageImages } from "@/components/MessageImages";
import {withCheckoutAttempt} from "@workspace/api-client-react";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useGetOrderCategories } from "@workspace/api-client-react";
import { useTown } from "@/hooks/use-town";
import { useRoute, useLocation, Link } from "wouter";
import DriverLocationMap from "@/components/DriverLocationMap";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete, MAPBOX_TOKEN } from "@/components/AddressAutocomplete";
import { Badge } from "@/components/ui/badge";
import { CheckoutUpsellsManager } from "@/components/CheckoutUpsellsManager";
import { ImageUpload } from "@/components/ImageUpload";
import { WhiteLabelHomepageEditor } from "@/components/WhiteLabelHomepageEditor";
import { SmsTopup } from "@/components/SmsTopup";
import {
  Store, Plus, Pencil, Trash2, CheckCircle2, Link2, ChevronRight,
  Package, Truck, ArrowLeft, Clock, Star, Tag, LayoutList, X, MessageSquare,
  Settings2, ChevronDown, ChevronUp, BarChart2, TrendingUp, Users, Banknote,
  Library, Link as LinkIcon, UserPlus, Shield, Crown, Mail, GripVertical,
  LoaderCircle, ShoppingCart, Search, FileText,
} from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}


function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

function MoneyInput({ value, onChange, placeholder = "0.00", className, disabled }: { value: number; onChange: (pence: number) => void; placeholder?: string; className?: string; disabled?: boolean }) {
  const [text, setText] = useState(() => value === 0 ? "" : (value / 100).toFixed(2));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(value === 0 ? "" : (value / 100).toFixed(2)); }, [value, focused]);
  return (
    <Input type="text" inputMode="decimal" placeholder={placeholder} className={className} disabled={disabled}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); const p = Math.round(parseFloat(text) * 100); onChange(isNaN(p) || p < 0 ? 0 : p); }} />
  );
}

/* A daypart minimum has three states and a money box can only show two of them.
   Blank means "not set — this window uses the base minimum"; an explicit 0 means
   "no minimum during this window" and overrides a higher base. Without this
   toggle the shop cannot say the second thing from the web portal at all. */
function NoMinimumToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none">
      <input type="checkbox" className="h-3 w-3" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      No minimum in this window
    </label>
  );
}

// ── Operating hours editor ────────────────────────────────────────────────────
type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
const DAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS: Record<DayKey, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
type DaySlot = { open: string; close: string };
type HoursMap = Partial<Record<DayKey, DaySlot | null>>;

function parseHoursMap(raw: string | null | undefined): HoursMap {
  if (!raw?.trim() || raw.trim()[0] !== "{") return {};
  try { const p = JSON.parse(raw); if (p && typeof p === "object" && !Array.isArray(p)) return p as HoursMap; } catch {}
  return {};
}

function HoursEditor({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  const parsed = parseHoursMap(value);
  function update(day: DayKey, field: "open" | "close" | "closed", val: string | boolean) {
    const next = { ...parsed };
    if (field === "closed") {
      next[day] = val ? null : { open: "09:00", close: "22:00" };
    } else {
      const existing = next[day] ?? { open: "09:00", close: "22:00" };
      if (!existing) return;
      next[day] = { ...existing, [field]: val as string };
    }
    // Always include all 7 days so the server never falls back to "missing key = open"
    const full = Object.fromEntries(DAY_KEYS.map((k) => [k, next[k] ?? null]));
    onChange(JSON.stringify(full));
  }
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
      <div className="hidden sm:grid sm:grid-cols-[100px_72px_1fr_1fr] bg-slate-50 px-3 py-2 border-b border-slate-200 text-xs font-semibold text-slate-500">
        <span>Day</span><span>Status</span><span>Opens</span><span>Last order</span>
      </div>
      {DAY_KEYS.map((day) => {
        const slot = parsed[day];
        const isSet = day in parsed;
        const isClosed = isSet && slot === null;
        const isOpen = isSet && slot !== null;
        return (
          <div key={day} className={`grid grid-cols-2 sm:grid-cols-[100px_72px_1fr_1fr] items-center px-3 py-2 border-b border-slate-100 last:border-0 gap-2 ${isClosed ? "bg-slate-50/60" : ""}`}>
            <span className="font-medium text-slate-700">{DAY_LABELS[day]}</span>
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={isOpen} onChange={(e) => update(day, "closed", !e.target.checked)} className="rounded" />
              <span className={`text-xs ${isOpen ? "text-emerald-600 font-medium" : "text-slate-400"}`}>{isOpen ? "Open" : "Closed"}</span>
            </label>
            {isOpen && slot ? (
              <>
                <input type="time" value={slot.open} onChange={(e) => update(day, "open", e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm w-full" />
                <input type="time" value={slot.close} onChange={(e) => update(day, "close", e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm w-full" />
              </>
            ) : (
              <>
                <span className="hidden sm:inline text-slate-300 text-xs">—</span>
                <span className="hidden sm:inline text-slate-300 text-xs">—</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
/** Format a weekly fee — null means platform default hasn't been fetched yet (show nothing). */
function fmtFee(p: number | null) { return p == null ? "…" : `£${(p / 100).toFixed(2)}`; }
function formatStatus(s: string) {
  return { pending: "Pending", confirmed: "Confirmed", preparing: "Preparing", ready: "Ready", out_for_delivery: "Out for delivery", delivered: "Delivered", cancelled: "Cancelled" }[s] ?? s;
}
function statusColor(s: string) {
  if (s === "delivered") return "bg-emerald-100 text-emerald-700";
  if (s === "cancelled") return "bg-red-100 text-red-600";
  if (s === "out_for_delivery") return "bg-blue-100 text-blue-700";
  if (s === "preparing" || s === "confirmed") return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

interface StoreData {
  id: number; name: string; slug: string | null; description: string | null;
  category: string; categories: string[]; town: string; address: string; phone: string | null; email: string | null;
  imageUrl: string | null; bannerUrl: string | null; coverMode?: string; coverAnimationUrl?: string | null; coverPosterUrl?: string | null; coverVideoUrl?: string | null;
  offersDelivery: boolean; offersCollection: boolean;
  minOrderPence: number; minOrderBreakfastPence: number | null; minOrderLunchPence: number | null; minOrderEveningPence: number | null;
  deliveryFeePence: number; deliveryTimeMins: number; collectionTimeMins: number;
  storeLatitude?: number | null; storeLongitude?: number | null; driverBonusPence?: number | null; externalDriverFeePence?: number | null;
  openingHours: string | null; stripeAccountId: string | null; stripeOnboardingComplete: boolean;
  serviceChargePence: number | null; bagChargePence: number | null;
  businessId?: number | null;
  isActive: boolean; approved: boolean;
  subscriptionId: string | null; subscriptionStatus: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  deliveryTowns: string[] | null;
  acceptsCash: boolean;
  acceptsGlobalCodes?: boolean;
  weeklyFeePence: number | null;
  useHubDrivers?: boolean;
  receiptLogoUrl?: string | null;
  receiptFooter?: string | null;
  staffRole?: "owner" | "manager" | "staff";
  // Sub-section mode. Optional because older cached responses may predate it,
  // and because the whole feature is off unless the shop explicitly turns it on.
  subcategoriesEnabled?: boolean;
}
interface DeliveryZone {
  id: number; storeId: number; postcodePrefix: string | null; maxRadiusKm: number | null;
  feePence: number; label: string | null;
}
interface MenuSection { id: number; storeId: number; name: string; description: string | null; sortOrder: number; parentSectionId: number | null; imageUrl?: string | null; upliftPercent?: number | null; upliftExcluded?: boolean; upsellOnly?: boolean; }
interface MenuItem { id: number; storeId: number; sectionId: number; name: string; description: string | null; pricePence: number; imageUrl: string | null; available: boolean; sortOrder: number; discountType: string | null; discountValue: number | null; discountLabel: string | null; calories: number | null; multiBuyQty: number | null; multiBuyPricePence: number | null; isEndToEnd: boolean | null; isHighValue: boolean; upliftPercent: number | null; upliftExcluded: boolean; }
interface MixMatchDeal { id: number; storeId: number; name: string; qty: number; pricePence: number; active: boolean; itemIds: number[]; }
interface StoreOffer { id: number; storeId: number; title: string; description: string | null; type: string; value: number; minOrderPence: number; freeItemMenuItemId: number | null; freeItemMode: string; freeItemCategoryId: number | null; freeItemPoolItemIds: number[]; freeItemPickCount: number; freeItemOptionVariant: string | null; code: string | null; isActive: boolean; validUntil: string | null; excludedItemIds: number[]; excludedSectionIds: number[]; excludedOptionIds: number[]; activeDays: number[]; includedItemIds: number[]; includedSectionIds: number[]; bogofBuyQty: number; bogofFreeQty: number; bogofStack: boolean; imageUrl?: string | null; carouselFeatured?: boolean; carouselRank?: number; }
const emptyOfferForm = () => ({ title: "", description: "", type: "percentage", value: 0, minOrderPence: 0, freeItemMenuItemId: 0, freeItemMode: "single", freeItemCategoryId: 0, freeItemPoolItemIds: [] as number[], freeItemPickCount: 1, freeItemOptionVariant: "", code: "", validUntil: "", excludedItemIds: [] as number[], excludedSectionIds: [] as number[], excludedOptionIds: [] as number[], activeDays: [] as number[], includedItemIds: [] as number[], includedSectionIds: [] as number[], bogofBuyQty: 2, bogofFreeQty: 1, bogofStack: true, imageUrl: "", carouselFeatured: false, carouselRank: 0 });
interface OptionChoice { id: number; name: string; pricePence: number; available: boolean; displayOrder: number; }
interface OptionGroup { id: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null; sharedGroupId: number | null; dependsOnOptionId: number | null; displayOrder?: number; choices: OptionChoice[]; }
type ItemOptionSummary = Record<number, { groupName: string; choices: { id: string; name: string }[] }[]>;

function ItemPicker({ items, sections, optionSummary, value, onChange, loading }: {
  items: MenuItem[];
  sections: MenuSection[];
  optionSummary: ItemOptionSummary;
  value: number;
  onChange: (id: number) => void;
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectedItem = items.find(i => i.id === value);
  const q = search.toLowerCase();
  const filtered = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items;

  // Build section name lookup; group items by their own sectionId so every item always appears
  const sectionNameMap = new Map(sections.map(s => [s.id, s.name]));
  const sectionOrder = sections.map(s => s.id);
  const bySection = new Map<number, MenuItem[]>();
  for (const item of filtered) {
    if (!bySection.has(item.sectionId)) bySection.set(item.sectionId, []);
    bySection.get(item.sectionId)!.push(item);
  }
  // Preserve the store's section order; unknown sectionIds go last
  const knownIds = sectionOrder.filter(id => bySection.has(id));
  const unknownIds = [...bySection.keys()].filter(id => !sectionOrder.includes(id));
  const orderedSectionIds = [...knownIds, ...unknownIds];

  return (
    <div ref={containerRef} className="relative">
      <div className="border border-input rounded-md flex items-center px-2 gap-1 bg-background focus-within:ring-1 focus-within:ring-ring">
        <Search size={13} className="text-slate-400 shrink-0" />
        <input
          type="text"
          placeholder={selectedItem ? selectedItem.name : "Search items…"}
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="flex-1 py-1.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
        />
        {selectedItem && (
          <button type="button" onClick={() => { onChange(0); setSearch(""); }} className="text-slate-400 hover:text-slate-600 shrink-0">
            <X size={13} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 w-full bg-white border border-input rounded-md shadow-lg max-h-64 overflow-y-auto mt-1">
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading items…</div>}
          {!loading && filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No items found</div>}
          {!loading && orderedSectionIds.map(sid => {
            const sItems = bySection.get(sid) ?? [];
            const sectionName = sectionNameMap.get(sid) ?? `Section ${sid}`;
            return (
              <div key={sid}>
                <div className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 sticky top-0 border-b border-slate-100">
                  {sectionName}
                </div>
                {sItems.map(item => {
                  const opts = optionSummary[item.id] ?? [];
                  const isSelected = value === item.id;
                  return (
                    <button key={item.id} type="button"
                      onClick={() => { onChange(item.id); setSearch(""); setOpen(false); }}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors ${isSelected ? "bg-teal-50" : ""}`}>
                      <div className={`text-sm font-medium ${isSelected ? "text-teal-700" : "text-slate-800"}`}>
                        {isSelected && "✓ "}{item.name}
                      </div>
                      {opts.map(g => (
                        <div key={g.groupName} className="text-xs text-slate-400 mt-0.5">
                          {g.groupName}: {g.choices.map(c => c.name).join(" · ")}
                        </div>
                      ))}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function groupSelectionLabel(min: number, max: number | null): string {
  const unlimited = max === null || max === 0;
  if (unlimited) {
    return min >= 1 ? `Required · min ${min}, unlimited` : "Optional · any number";
  }
  if (max === 1) return min >= 1 ? "Required · single choice" : "Optional · single choice";
  if (min === 0) return `Optional · up to ${max}`;
  if (min === max) return `Required · choose exactly ${min}`;
  return `Required · choose ${min}–${max}`;
}
interface SharedLibraryOption { id: number; name: string; pricePence: number; available?: boolean; }
interface SharedLibraryGroup { id: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null; options: SharedLibraryOption[]; }
interface OrderItem { id: number; name: string; pricePence: number; quantity: number; selectedOptions?: string | null; isFreeItem?: boolean; }
interface OrderRow {
  id: number; fulfilmentType: string; status: string; totalPence: number;
  deliveryAddress: string | null; scheduledFor: string | null;
  customerName: string | null; customerPhone: string | null; createdAt: string;
  items: OrderItem[]; driverMode?: string | null; paymentMethod?: string | null;
  estimatedReadyAt?: string | null; isMultiOrderChild?: boolean;
  /**
   * What the customer was actually charged — the figure on their receipt.
   *
   * Not derivable from totalPence plus the fee columns: on a card order the hub
   * fees are added on top, but on a cash order the customer hands over the order
   * total and the platform fee is owed by the shop instead. Adding the fees
   * blindly overstates every cash order. Null on older rows, hence the fallback.
   */
  customerPaidPence?: number | null;
}
interface DriverAssignment {
  id: number; driverId: number; driverName: string | null;
  driverPhone: string | null; vehicleType: string; claimedAt: string;
  pickedUpAt: string | null; deliveredAt: string | null;
  driverLat?: number | null; driverLng?: number | null;
}
interface AnalyticsSummary {
  totalOrders: number; totalRevenuePence: number; avgOrderValuePence: number;
  totalSalesPence?: number; totalKeepPence?: number;
  uniqueCustomers: number; deliveredOrders: number; cancelledOrders: number;
}
interface DailyPoint { date: string; orderCount: number; revenuePence: number; }
interface TopCustomer {
  customerId: number; name: string; phone: string | null;
  orderCount: number; totalSpendPence: number; lastOrderAt: string;
}
interface AnalyticsData {
  summary: AnalyticsSummary;
  daily: DailyPoint[];
  statusBreakdown: Record<string, number>;
  topCustomers: TopCustomer[];
  transactions: OrderRow[];
}

type ExternalDelivery = {cash?:{id:number;expectedPence:number;owingPence:number}|null;
  id: number;
  status: string;
  externalReference: string | null;
  destinationPostcode: string | null;
  deliveryAddress: string | null;
  deliveryFeePence: number;
  driverFuelSupplementPence: number | null;
  shopServiceFeePence: number | null;
  totalPence: number;
  createdAt: string;
  searchState: "searching" | "offer_sent" | "driver_assigned" | "picked_up" | "delivered" | "cancelled" | "no_driver_found";
  pendingOfferExpiresAt: string | null;
  assignment: { driverId: number; driverName: string | null; claimedAt: string; pickedUpAt: string | null; deliveredAt: string | null } | null;
};
type LedgerStatement = {
  periodStart: string;
  periodEnd: string;
  openingBalancePence: number;
  chargedPence: number;
  recoveredPence: number;
  closingBalancePence: number;
  rows: Array<{
    type: "driver_request" | "cash_platform_fee" | "manual_charge" | "recovery";
    description?: string;
    transactionId: number;
    date: string | null;
    orderId?: number | null;
    reference?: string | null;
    destinationPostcode?: string | null;
    driverName?: string | null;
    deliveryFeePence?: number;
    fuelSupplementPence?: number;
    hubFeePence?: number;
    amountPence: number;
  }>;
};

type AccountStatement = {
  transactions: Array<{ date: string; description: string; chargePence: number; creditPence: number; runningPence: number }>;
  cardFees: Array<{ orderId: number; date: string; feePence: number }>;
  cardFeesTotalPence: number;
  owedPence: number;
  creditPoolPence: number;
  netOwedToHubPence: number;
};

function dateOffset(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

function previousMonday(): string {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  now.setDate(now.getDate() - mondayOffset - 7);
  return now.toISOString().slice(0, 10);
}

function DriverRequestsPanel({ store }: { store: StoreData }) {
  const { toast } = useToast();

  // Hub order tick-list state
  const [hubOrders, setHubOrders] = useState<OrderRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [requestingHub, setRequestingHub] = useState(false);

  // External (non-hub) delivery form state
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [reference, setReference] = useState("");
  const [instructions, setInstructions] = useState("");
  const [cashOrder,setCashOrder]=useState({...EMPTY_CASH_ORDER});
  const requestKey=useRef(crypto.randomUUID());
  const requestLock=useRef(false);
  const [requestComplete, setRequestComplete] = useState<{ id: number; postcode: string } | null>(null);
  const [readyInMins, setReadyInMins] = useState(20); // when the order will be ready → driver's "ready ~HH:MM"
  const [quote, setQuote] = useState<DriverAreaQuote | null>(null);
  const [deliveryFeePence, setDeliveryFeePence] = useState(0);
  const [recommendBusy, setRecommendBusy] = useState(false);
  const [recommendError, setRecommendError] = useState("");
  const [roundTrip, setRoundTrip] = useState<number | null>(null);
  const [mileageBusy, setMileageBusy] = useState(false);
  const recommendationPostcode = formatPostcode(postcode);
  const fuel = quote?.fuelSupplementPence ?? store.driverBonusPence ?? 100;
  const hub = quote?.hubFeePence ?? store.externalDriverFeePence ?? 150;
  const [busy, setBusy] = useState(false);

  // Shared
  const [deliveries, setDeliveries] = useState<ExternalDelivery[]>([]);
  const [outstandingPence, setOutstandingPence] = useState(0);
  const [driverBalanceRevision, setDriverBalanceRevision] = useState(0);
  const driverAccountStoreRef = useRef(store.id); driverAccountStoreRef.current = store.id;
  const [periodStart, setPeriodStart] = useState(previousMonday);
  const [statement, setStatement] = useState<LedgerStatement | null>(null);
  const [statementForbidden, setStatementForbidden] = useState(false);

  const loadDeliveries = useCallback(async () => {
    const response = await fetch(`/api/order/stores/${store.id}/external-deliveries`, { headers: authHeaders() });
    if (!response.ok) return;
    const data = await response.json() as { items: ExternalDelivery[]; ledger: { outstandingPence: number } };
    if (driverAccountStoreRef.current !== store.id) return;
    setDeliveries(data.items ?? []);
    setOutstandingPence(data.ledger?.outstandingPence ?? 0);
    setDriverBalanceRevision(n => n + 1);
  }, [store.id]);

  const loadHubOrders = useCallback(async () => {
    const response = await fetch(`/api/order/stores/${store.id}/orders`, { headers: authHeaders() });
    if (!response.ok) return;
    const data = await response.json() as { items: OrderRow[] };
    const eligible = (data.items ?? []).filter((o) =>
      o.fulfilmentType === "delivery" &&
      o.driverMode !== "hub" &&
      !["delivered", "cancelled"].includes(o.status) &&
      o.paymentMethod === "card"
    );
    setHubOrders(eligible);
  }, [store.id]);

  useEffect(() => { void loadDeliveries(); void loadHubOrders(); }, [loadDeliveries, loadHubOrders]);

  useEffect(() => {
    setStatement(null);
    setStatementForbidden(false);
    void fetch(`/api/order/stores/${store.id}/ledger/statements/${periodStart}`, { headers: authHeaders() })
      .then(async (response) => {
        if (response.status === 403) { setStatementForbidden(true); return; }
        if (!response.ok) return;
        const data = await response.json() as { statement: LedgerStatement };
        if (driverAccountStoreRef.current === store.id) setStatement(data.statement);
      });
  }, [periodStart, store.id, driverBalanceRevision]);

  const refreshDriverRequestAccount = useCallback(() => {
    if (driverAccountStoreRef.current !== store.id) return;
    void loadDeliveries();
  }, [store.id, loadDeliveries]);

  function toggleOrder(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function requestDriversForOrders() {
    if (selectedIds.size === 0) return;
    setRequestingHub(true);
    let successCount = 0;
    for (const orderId of selectedIds) {
      const response = await fetch(`/api/order/stores/${store.id}/orders/${orderId}/request-platform-driver`, {
        method: "POST", headers: authHeaders(),
      });
      if (response.ok) {
        successCount++;
      } else {
        const data = await response.json() as { error?: string };
        toast({ title: data.error ?? `Could not request driver for order #${orderId}`, variant: "destructive" });
      }
    }
    setRequestingHub(false);
    setSelectedIds(new Set());
    if (successCount > 0) {
      toast({ title: successCount === 1 ? "Driver requested" : `${successCount} driver requests sent`, description: "Nearby drivers will be offered the job shortly." });
    }
    await Promise.all([loadHubOrders(), loadDeliveries()]);
  }

  useEffect(() => {
    setQuote(null); setDeliveryFeePence(0); setRecommendError("");
    if (!recommendationPostcode) { setRecommendBusy(false); return; }
    let current = true; const controller = new AbortController();
    setRecommendBusy(true);
    const timer = setTimeout(async () => {
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(`/api/order/stores/${store.id}/external-deliveries/quote`, {
          method: "POST", headers: authHeaders(), signal: controller.signal,
          body: JSON.stringify({ destinationPostcode: recommendationPostcode }),
        });
        const data = await response.json() as DriverAreaQuote & { error?: string };
        if (!response.ok) throw new Error(data.error ?? "Area pricing unavailable");
        const fee = recommendedDriverFee(data.deliveryFeePence);
        if (fee == null) throw new Error("No supported recommendation for this area");
        if (current) { setQuote(data); setDeliveryFeePence(fee); }
      } catch {
        if (current) setRecommendError("No recommendation available. You can set the delivery charge yourself (minimum £3).");
      } finally { clearTimeout(timeout); if (current) setRecommendBusy(false); }
    }, 400);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [store.id, recommendationPostcode]);

  useEffect(() => {
    setRoundTrip(null);
    if (!recommendationPostcode) { setMileageBusy(false); return; }
    let current = true; const controller = new AbortController(); setMileageBusy(true);
    const timer = setTimeout(() => {
      void roundTripDrivingMiles(store.storeLatitude, store.storeLongitude, recommendationPostcode, MAPBOX_TOKEN, controller.signal)
        .then(miles => { if (current) { setRoundTrip(miles); setMileageBusy(false); } });
    }, 400);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [store.id, store.storeLatitude, store.storeLongitude, recommendationPostcode]);

  async function requestDriver() {
    if (!recommendationPostcode || deliveryFeePence < 300 || recommendBusy || requestLock.current || requestComplete) return;
    requestLock.current=true;setBusy(true);
    try {
      const cash=cashOrderPayload(cashOrder);
      const response = await fetch(`/api/order/stores/${store.id}/external-deliveries`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          deliveryAddress: address,
          destinationPostcode: recommendationPostcode,
          deliveryFeePence,
          readyInMins,
          externalReference: reference,
          deliveryInstructions: instructions,
          idempotencyKey: requestKey.current,
          ...cash,
        }),
      });
      const data = await response.json() as { error?: string; order?: ExternalDelivery };
      if (!response.ok) { toast({ title: data.error ?? "Could not request driver", variant: "destructive" }); return; }
      if (!data.order?.id) throw new Error("Unable to confirm the request. Please retry without changing the details.");
      setRequestComplete({ id: data.order.id, postcode: recommendationPostcode });
      requestKey.current=crypto.randomUUID();setCashOrder({...EMPTY_CASH_ORDER});
      setAddress(""); setPostcode(""); setReference(""); setInstructions(""); setQuote(null); setDeliveryFeePence(0);
      await loadDeliveries().catch(() => { toast({ title: "Request saved", description: "The delivery list could not refresh. Your driver request is still complete." }); });
    } catch(e) { toast({title:(e as Error).message,variant:"destructive"}); } finally { requestLock.current=false;setBusy(false); }
  }

  async function cancelDriverRequest(delivery: ExternalDelivery) {
    if (!confirm(`Cancel driver request #${delivery.id}? Any driver offer or assignment will be withdrawn.`)) return;
    const response = await fetch(`/api/order/stores/${store.id}/external-deliveries/${delivery.id}/cancel`, { method: "POST", headers: authHeaders() });
    const data = await response.json() as { error?: string };
    if (!response.ok) { toast({ title: data.error ?? "Could not cancel request", variant: "destructive" }); return; }
    toast({ title: "Driver request cancelled" });
    await loadDeliveries();
  }

  const ORDER_STATUS_LABEL: Record<string, string> = { confirmed: "Confirmed", preparing: "Preparing", ready: "Ready for pickup" };

  return (
    <div className="space-y-6">
      <AlertDialog open={requestComplete !== null} onOpenChange={open => { if (!open) setRequestComplete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Your driver request is complete!</AlertDialogTitle>
            <AlertDialogDescription>
              Request #{requestComplete?.id} for {requestComplete?.postcode} has been received. We're looking for a driver. You don't need to request again for this delivery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogAction onClick={() => setRequestComplete(null)}>Got it</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* ── Section 1: tick-list of today's hub orders needing a driver ── */}
      {store.useHubDrivers && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-bold">Request a driver for your orders</h3>
              <p className="text-sm text-slate-500 mt-0.5">Tick the orders you need a driver for. A charge is added to your ledger per order.</p>
            </div>
            {selectedIds.size > 0 && (
              <Button disabled={requestingHub} onClick={() => void requestDriversForOrders()}>
                <Truck size={16} className="mr-2" />
                {requestingHub ? "Requesting..." : `Request driver${selectedIds.size > 1 ? "s" : ""} (${selectedIds.size})`}
              </Button>
            )}
          </div>
          {hubOrders.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No orders waiting for a driver right now.</p>
          ) : hubOrders.map((order) => {
            const selected = selectedIds.has(order.id);
            return (
              <label key={order.id} className={`flex items-center gap-4 p-4 border-b last:border-0 cursor-pointer transition-colors ${selected ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <input type="checkbox" className="w-4 h-4 rounded shrink-0" checked={selected} onChange={() => toggleOrder(order.id)} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{order.customerName ?? "Customer"}</p>
                  <p className="text-xs text-slate-500 truncate">{order.deliveryAddress ?? "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-sm">{pence(order.totalPence)}</p>
                  <p className="text-xs text-slate-500">{ORDER_STATUS_LABEL[order.status] ?? order.status}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {/* ── Section 2: external / non-hub form ── */}
      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-5">
        <div className="bg-card text-card-foreground border border-border rounded-xl p-5 space-y-4">
          <div>
            <h3 className="font-bold text-foreground">Order not through Ayrshire Hub?</h3>
            <p className="text-sm text-muted-foreground">Type the delivery details and we'll find a driver. The charge is added to your ledger.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-600 dark:text-slate-300" htmlFor="driver-request-postcode">1. Delivery postcode</label><Input id="driver-request-postcode" value={postcode} onChange={(event) => { if (formatPostcode(event.target.value) !== recommendationPostcode) { setQuote(null); setDeliveryFeePence(0); setRoundTrip(null); } setPostcode(event.target.value.toUpperCase()); }} placeholder="KA1 1AA" /></div>
            <div className="sm:col-span-2 space-y-2">
              <p className="text-sm font-semibold">2. Delivery charge</p>
              <div className="flex items-center justify-between gap-3 rounded-xl bg-muted p-3">
                <Button variant="outline" aria-label="Decrease delivery charge by 50 pence" disabled={recommendBusy || deliveryFeePence <= 300} onClick={() => setDeliveryFeePence(v => Math.max(300, v - 50))}>−</Button>
                <div className="text-center"><p className="text-3xl font-bold">{deliveryFeePence < 300 ? "£ —" : pence(deliveryFeePence)}</p><p className="text-xs text-muted-foreground">{deliveryFeePence < 300 ? "Minimum £3 · use + to set manually" : "to the driver"}</p></div>
                <Button variant="outline" aria-label="Increase delivery charge by 50 pence" disabled={recommendBusy || deliveryFeePence >= 5000} onClick={() => setDeliveryFeePence(v => Math.min(5000, Math.max(300, v + 50)))}>+</Button>
              </div>
              {recommendBusy ? <p className="text-sm text-muted-foreground">Finding the recommended fee…</p> : quote ? <><p className="text-sm">We recommend {pence(recommendedDriverFee(quote.deliveryFeePence) ?? 0)} for this area. You can adjust it.</p>{recommendedDriverFee(quote.deliveryFeePence) !== quote.deliveryFeePence && <p className="text-xs text-muted-foreground">Area fee {pence(quote.deliveryFeePence)}, rounded up to the existing 50p steps and £3 minimum.</p>}</> : recommendError ? <p className="text-sm text-muted-foreground">{recommendError}</p> : null}
              {quote&&lowDriverFeeMessage(deliveryFeePence,recommendedDriverFee(quote.deliveryFeePence),roundTrip)&&<p role="status" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">{lowDriverFeeMessage(deliveryFeePence,recommendedDriverFee(quote.deliveryFeePence),roundTrip)}</p>}
              {recommendationPostcode && <div className="rounded-lg border p-3"><p className="font-semibold">ROUND TRIP · {mileageBusy ? "Calculating…" : roundTrip == null ? "Mileage unavailable" : `${roundTrip.toFixed(1)} miles approx.`}</p><p className="text-xs text-muted-foreground">Shop → delivery postcode → shop. Estimated driving distance to the postcode area.</p></div>}
            </div>
            <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Full delivery address (optional)</label><AddressAutocomplete value={address} placeholder="House, street, town and postcode" onSelect={(addr, pc) => { setAddress(addr); if (pc && formatPostcode(pc) !== recommendationPostcode) { setPostcode(pc.toUpperCase()); setQuote(null); setDeliveryFeePence(0); setRoundTrip(null); } }} /></div>
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">When will it be ready?</label>
              <p className="text-xs text-muted-foreground mb-1">So the driver knows when to be at your shop.</p>
              <div className="flex flex-wrap gap-2">
                {[{ l: "20 min", m: 20 }, { l: "30 min", m: 30 }, { l: "45 min", m: 45 }, { l: "60 min", m: 60 }].map((opt) => (
                  <button key={opt.m} type="button" onClick={() => setReadyInMins(opt.m)}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${readyInMins === opt.m ? "bg-[#1e3a5f] border-[#1e3a5f] text-white" : "bg-white border-slate-200 text-slate-700"}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Your order reference</label><Input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Optional" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Driver instructions</label><Input value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Optional access or handover details" /></div>
          </div>
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-slate-500 dark:text-slate-300">Delivery</p><p className="font-bold">{pence(deliveryFeePence)}</p></div>
              <div><p className="text-slate-500 dark:text-slate-300">Fuel</p><p className="font-bold">{pence(fuel)}</p></div>
              <div><p className="text-slate-500 dark:text-slate-300">Hub fee</p><p className="font-bold">{pence(hub)}</p></div>
              <div><p className="text-slate-500 dark:text-slate-300">Added to ledger</p><p className="font-bold text-blue-700 dark:text-blue-300">{pence(deliveryFeePence + fuel + hub)}</p></div>
            </div>
          </div>
          <CashOrderFields value={cashOrder} onChange={setCashOrder}/>
          <Button className="w-full" disabled={busy || recommendBusy || !recommendationPostcode || deliveryFeePence < 300} onClick={() => void requestDriver()}><Truck size={16} className="mr-2" />{busy ? "Requesting…" : "Confirm and request driver"}</Button>
        </div>
        <div className="min-w-0"><StoreBalancePayment key={store.id} storeId={store.id} payNow collapsible refreshKey={driverBalanceRevision} fallbackBalancePence={outstandingPence} onUpdated={refreshDriverRequestAccount}/></div>
      </div>

      <CashReturnsPanel key={store.id} scope="shop" storeId={store.id} collapsedSummary/>
      {/* ── Section 3: active driver requests ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200"><h3 className="font-bold">Driver requests</h3></div>
        {deliveries.length === 0 ? <p className="p-5 text-sm text-slate-500">No external driver requests yet.</p> : deliveries.map((delivery) => (
          <div key={delivery.id} className="p-4 border-b last:border-0 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-semibold">#{delivery.id}{delivery.externalReference ? ` · ${delivery.externalReference}` : ""}</p><p className="text-xs text-slate-500">{delivery.destinationPostcode} · {new Date(delivery.createdAt).toLocaleString("en-GB")}</p><div className="flex items-center gap-1.5 mt-1 text-xs text-slate-600">{["searching", "offer_sent"].includes(delivery.searchState) && <LoaderCircle size={13} className="animate-spin text-blue-600" />}<span>{delivery.searchState === "offer_sent" ? "Job offered to a nearby driver" : delivery.searchState === "searching" ? "Searching for a nearby driver" : delivery.searchState === "driver_assigned" ? `${delivery.assignment?.driverName ?? "A driver"} accepted and is heading to the shop` : delivery.searchState === "picked_up" ? `${delivery.assignment?.driverName ?? "Driver"} has collected the delivery` : delivery.searchState === "no_driver_found" ? "No driver found - contact Ayrshire Hub or try again" : delivery.searchState === "delivered" ? "Delivered" : "Cancelled"}</span></div></div>
            {delivery.cash&&<div className="w-full"><CashReturnsPanel orderId={delivery.id} compact/></div>}
            <div className="text-right"><Badge className={statusColor(delivery.status)}>{formatStatus(delivery.status)}</Badge><p className="font-bold mt-1">{pence(delivery.totalPence)}</p>{["searching", "offer_sent", "driver_assigned", "no_driver_found"].includes(delivery.searchState) && <Button size="sm" variant="outline" className="mt-2 h-7 text-xs text-red-600 border-red-200" onClick={() => void cancelDriverRequest(delivery)}>Cancel request</Button>}</div>
          </div>
        ))}
      </div>

      {/* ── Section 5: weekly ledger statement ── */}
      {!statementForbidden && <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3">
          <div><h3 className="font-bold">Weekly ledger statement</h3><p className="text-xs text-slate-500">The same statement is emailed to the shop each week.</p></div>
          <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setPeriodStart(dateOffset(periodStart, -7))}>Previous</Button><Button size="sm" variant="outline" disabled={periodStart >= previousMonday()} onClick={() => setPeriodStart(dateOffset(periodStart, 7))}>Next</Button></div>
        </div>
        {!statement ? <p className="p-5 text-sm text-slate-500">Loading statement...</p> : <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border-b border-slate-200">
            {[['Opening owed', statement.openingBalancePence], ['New charges', statement.chargedPence], ['Recovered', statement.recoveredPence], ['Closing owed', statement.closingBalancePence]].map(([label, value]) => <div key={String(label)} className="bg-white p-4"><p className="text-xs text-slate-500">{label}</p><p className="text-xl font-bold">{pence(value as number)}</p></div>)}
          </div>
          <div className="p-4"><p className="text-sm font-semibold mb-3">{statement.periodStart} to {statement.periodEnd}</p>{statement.rows.length === 0 ? <p className="text-sm text-slate-500">No activity in this period.</p> : statement.rows.map((row) => <div key={row.transactionId} className="py-3 border-b last:border-0 flex justify-between gap-4 text-sm"><div><p className="font-semibold">{row.type === "recovery" ? (row.description ?? "Payment received") : row.type === "cash_platform_fee" ? `Cash order platform fee #${row.orderId}` : row.type === "manual_charge" ? `Manual charge #${row.orderId}` : `Driver request #${row.orderId}`}{row.destinationPostcode ? ` · ${row.destinationPostcode}` : ""}</p><p className="text-xs text-slate-500">{row.date ? new Date(row.date).toLocaleString("en-GB") : ""}{row.driverName ? ` · ${row.driverName}` : ""}{row.reference ? ` · ${row.reference}` : ""}</p>{row.type === "driver_request" && <p className="text-xs text-slate-500">Delivery {pence(row.deliveryFeePence ?? 0)} + fuel {pence(row.fuelSupplementPence ?? 0)} + Hub fee {pence(row.hubFeePence ?? 0)}</p>}</div><p className={`font-bold ${row.type === "recovery" ? "text-emerald-700" : ""}`}>{row.type === "recovery" ? "-" : "+"}{pence(row.amountPence)}</p></div>)}</div>
        </>}
      </div>}
    </div>
  );
}

function AccountStatementPanel({ store }: { store: StoreData }) {
  const [account, setAccount] = useState<AccountStatement | null>(null);
  useEffect(() => {
    void fetch(`/api/order/stores/${store.id}/account`, { headers: authHeaders() })
      .then(async (r) => { if (r.ok) setAccount(await r.json() as AccountStatement); });
  }, [store.id]);
  if (!account) return <div className="p-6 text-sm text-slate-500">Loading account statement…</div>;
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-bold">Account statement</h3>
          <p className="text-xs text-slate-500">Every charge and credit on your Ayrshire Hub account, with a running balance.</p>
        </div>
        <div className={`p-4 border-b ${account.netOwedToHubPence > 0 ? "bg-rose-50 border-rose-200" : account.netOwedToHubPence < 0 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
          <p className="text-sm font-semibold">
            {account.netOwedToHubPence > 0
              ? <>You owe Ayrshire Hub <span className="text-rose-700">{pence(account.netOwedToHubPence)}</span></>
              : account.netOwedToHubPence < 0
                ? <>You&apos;re in credit: <span className="text-emerald-700">{pence(-account.netOwedToHubPence)}</span> <span className="font-normal text-slate-500">(covers your future Ayrshire Hub fees)</span></>
                : <>Settled — nothing owed.</>}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Charge</th><th className="text-right px-4 py-2">Credit</th><th className="text-right px-4 py-2">Balance</th></tr>
            </thead>
            <tbody>
              {account.transactions.length === 0
                ? <tr><td colSpan={5} className="text-center text-slate-400 py-6">No account activity yet</td></tr>
                : account.transactions.map((t, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2 whitespace-nowrap text-slate-500">{new Date(t.date).toLocaleDateString("en-GB")}</td>
                    <td className="px-4 py-2">{t.description}</td>
                    <td className="px-4 py-2 text-right text-rose-700">{t.chargePence ? pence(t.chargePence) : ""}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{t.creditPence ? pence(t.creditPence) : ""}</td>
                    <td className="px-4 py-2 text-right font-semibold">{pence(t.runningPence)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {account.cardFees.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-slate-50/60">
            <p className="text-xs text-slate-500">Card-order platform fees are taken directly from your Stripe payout, so they&apos;re already settled and not part of the balance above: <span className="font-semibold text-slate-700">{account.cardFees.length} orders · {pence(account.cardFeesTotalPence)}</span> in fees.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Item Options Panel ────────────────────────────────────────────────────────

function ItemOptionsPanel({ storeId, itemId }: { storeId: number; itemId: number }) {
  const { toast } = useToast();
  const [groups, setGroups] = useState<OptionGroup[]>([]);
  const [library, setLibrary] = useState<SharedLibraryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [addGroupForm, setAddGroupForm] = useState<{ name: string; minSelections: number; maxSelections: number; dependsOnOptionId: number | null } | null>(null);
  const [addChoiceForms, setAddChoiceForms] = useState<Record<number, { name: string; pricePence: number }>>({});
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [editingCondition, setEditingCondition] = useState<number | null>(null);
  const dragGroupId = useRef<number | null>(null);
  const dragOverGroupId = useRef<number | null>(null);
  const [groupDragTarget, setGroupDragTarget] = useState<number | null>(null);
  const dragChoiceId = useRef<number | null>(null);
  const dragChoiceGroupId = useRef<number | null>(null);
  const dragOverChoiceId = useRef<number | null>(null);
  const [choiceDragTarget, setChoiceDragTarget] = useState<number | null>(null);
  const choiceDragAllowed = useRef(false);
  const dragGroupAllowed = useRef(false);
  const [groupChoiceDropTarget, setGroupChoiceDropTarget] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<number | null>(null);
  const [groupNameDraft, setGroupNameDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, libRes] = await Promise.all([
        fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups`, { headers: authHeaders() }),
        fetch(`/api/order/stores/${storeId}/shared-option-groups`, { headers: authHeaders() }),
      ]);
      const d = await groupsRes.json() as { groups: Array<{ id: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null; sharedGroupId: number | null; dependsOnOptionId: number | null; displayOrder?: number }>; choices: Array<{ id: number; groupId: number; name: string; pricePence: number; available: boolean; displayOrder: number }> };
      const lib = await libRes.json() as { groups: Array<{ id: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null }>; options: Array<{ id: number; sharedGroupId: number; name: string; pricePence: number }> };
      setGroups((d.groups ?? []).map((g) => ({ ...g, dependsOnOptionId: g.dependsOnOptionId ?? null, displayOrder: g.displayOrder ?? 0, choices: (d.choices ?? []).filter((c) => c.groupId === g.id).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) })));
      setLibrary((lib.groups ?? []).map(g => ({ ...g, options: (lib.options ?? []).filter(o => o.sharedGroupId === g.id) })));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [storeId, itemId]);

  useEffect(() => { void load(); }, [load]);

  async function addGroup() {
    if (!addGroupForm?.name.trim()) return;
    const { minSelections, maxSelections, dependsOnOptionId } = addGroupForm;
    const max = maxSelections === 0 ? null : maxSelections;
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        name: addGroupForm.name.trim(),
        required: minSelections >= 1,
        multiSelect: max === null || max > 1,
        minSelections,
        maxSelections: max,
        dependsOnOptionId: dependsOnOptionId ?? null,
      }),
    });
    if (r.ok) { setAddGroupForm(null); void load(); }
    else toast({ title: "Failed to add group", variant: "destructive" });
  }

  async function setCondition(groupId: number, dependsOnOptionId: number | null) {
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ dependsOnOptionId }),
    });
    if (r.ok) { setEditingCondition(null); void load(); }
    else toast({ title: "Failed to update condition", variant: "destructive" });
  }

  async function attachFromLibrary(sharedGroupId: number) {
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ sharedGroupId }),
    });
    if (r.ok) { setShowLibraryPicker(false); void load(); }
    else toast({ title: "Failed to attach group", variant: "destructive" });
  }

  async function deleteGroup(groupId: number) {
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) void load();
    else toast({ title: "Failed to delete group", variant: "destructive" });
  }

  async function addChoice(groupId: number) {
    const form = addChoiceForms[groupId];
    if (!form?.name.trim()) return;
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}/options`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ name: form.name.trim(), pricePence: form.pricePence ?? 0 }),
    });
    if (r.ok) { setAddChoiceForms((prev) => { const n = { ...prev }; delete n[groupId]; return n; }); void load(); }
    else toast({ title: "Failed to add choice", variant: "destructive" });
  }

  async function deleteChoice(groupId: number, optionId: number) {
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}/options/${optionId}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) void load();
    else toast({ title: "Failed to delete choice", variant: "destructive" });
  }

  async function reorderGroups(dragId: number, overId: number) {
    const sorted = [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const fromIdx = sorted.findIndex((g) => g.id === dragId);
    const toIdx = sorted.findIndex((g) => g.id === overId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved!);
    const updated = reordered.map((g, i) => ({ ...g, displayOrder: i * 10 }));
    setGroups(updated);
    try {
      await Promise.all(
        updated.map((g) =>
          fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${g.id}`, {
            method: "PATCH", headers: authHeaders(),
            body: JSON.stringify({ displayOrder: g.displayOrder }),
          })
        )
      );
    } catch {
      toast({ title: "Reorder failed", description: "Could not save group order. Please try again.", variant: "destructive" });
      void load();
    }
  }

  async function moveGroupUp(groupId: number) {
    const sorted = [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const idx = sorted.findIndex((g) => g.id === groupId);
    if (idx <= 0) return;
    await reorderGroups(groupId, sorted[idx - 1]!.id);
  }

  async function moveGroupDown(groupId: number) {
    const sorted = [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const idx = sorted.findIndex((g) => g.id === groupId);
    if (idx === -1 || idx >= sorted.length - 1) return;
    await reorderGroups(groupId, sorted[idx + 1]!.id);
  }

  async function reorderChoices(groupId: number, dragId: number, overId: number) {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const sorted = [...group.choices].sort((a, b) => a.displayOrder - b.displayOrder);
    const fromIdx = sorted.findIndex((c) => c.id === dragId);
    const toIdx = sorted.findIndex((c) => c.id === overId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved!);
    const updated = reordered.map((c, i) => ({ ...c, displayOrder: i * 10 }));
    setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, choices: updated } : g));
    await Promise.all(
      updated.map((c) =>
        fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}/options/${c.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({ displayOrder: c.displayOrder }),
        })
      )
    );
  }

  async function moveChoiceToGroup(choiceId: number, fromGroupId: number, toGroupId: number, beforeChoiceId?: number) {
    const fromGroup = groups.find((g) => g.id === fromGroupId);
    const toGroup = groups.find((g) => g.id === toGroupId);
    if (!fromGroup || !toGroup) return;
    const choice = fromGroup.choices.find((c) => c.id === choiceId);
    if (!choice) return;
    const toSorted = [...toGroup.choices].sort((a, b) => a.displayOrder - b.displayOrder);
    let newDisplayOrder: number;
    if (beforeChoiceId != null) {
      const idx = toSorted.findIndex((c) => c.id === beforeChoiceId);
      if (idx === 0) newDisplayOrder = (toSorted[0]?.displayOrder ?? 0) - 10;
      else newDisplayOrder = Math.floor(((toSorted[idx - 1]?.displayOrder ?? 0) + (toSorted[idx]?.displayOrder ?? 0)) / 2);
    } else {
      newDisplayOrder = ((toSorted[toSorted.length - 1]?.displayOrder ?? -10) + 10);
    }
    const moved = { ...choice, displayOrder: newDisplayOrder };
    setGroups((prev) => prev.map((g) => {
      if (g.id === fromGroupId) return { ...g, choices: g.choices.filter((c) => c.id !== choiceId) };
      if (g.id === toGroupId) return { ...g, choices: [...g.choices, moved].sort((a, b) => a.displayOrder - b.displayOrder) };
      return g;
    }));
    await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${fromGroupId}/options/${choiceId}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ groupId: toGroupId, displayOrder: newDisplayOrder }),
    });
  }

  async function saveGroupName(groupId: number, name: string) {
    const trimmed = name.trim();
    if (!trimmed) { setEditingGroupName(null); return; }
    const r = await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}/option-groups/${groupId}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ name: trimmed }),
    });
    if (r.ok) { setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, name: trimmed } : g)); }
    else toast({ title: "Failed to rename group", variant: "destructive" });
    setEditingGroupName(null);
  }

  if (loading) return <div className="p-3 text-xs text-slate-400">Loading options…</div>;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Options &amp; Modifiers</p>

      {groups.map((group) => {
        // Collect all options from all OTHER groups for conditional picker
        const sortedGroupIds = [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map(g => g.id);
        const groupIdx = sortedGroupIds.indexOf(group.id);
        const allOptions = groups.flatMap(g => g.id === group.id ? [] : g.choices.map(c => ({ ...c, groupName: g.name })));
        const conditionOption = group.dependsOnOptionId != null
          ? groups.flatMap(g => g.choices.map(c => ({ ...c, groupName: g.name }))).find(c => c.id === group.dependsOnOptionId)
          : null;
        return (
        <div
          key={group.id}
          className={`bg-white rounded-xl border p-3 space-y-2 transition-colors ${groupDragTarget === group.id ? "border-primary ring-1 ring-primary bg-primary/5" : groupChoiceDropTarget === group.id ? "border-emerald-400 ring-1 ring-emerald-400 bg-emerald-50" : "border-slate-100"}`}
          draggable
          onDragStart={(e) => { if (!dragGroupAllowed.current) { e.preventDefault(); return; } const t = e.target as Element; if (t.closest('[data-choice-row]')) { e.preventDefault(); return; } dragGroupId.current = group.id; }}
          onDragOver={(e) => { e.preventDefault(); if (dragChoiceId.current !== null && dragChoiceGroupId.current !== group.id) { setGroupChoiceDropTarget(group.id); } else if (dragGroupId.current !== null) { dragOverGroupId.current = group.id; setGroupDragTarget(group.id); } }}
          onDragLeave={() => { if (dragOverGroupId.current === group.id) { setGroupDragTarget(null); dragOverGroupId.current = null; } setGroupChoiceDropTarget(null); }}
          onDrop={(e) => { e.preventDefault(); setGroupDragTarget(null); setGroupChoiceDropTarget(null); if (dragGroupId.current != null && dragGroupId.current !== group.id) { void reorderGroups(dragGroupId.current, group.id); dragGroupId.current = null; } else if (dragChoiceId.current !== null && dragChoiceGroupId.current !== null && dragChoiceGroupId.current !== group.id) { void moveChoiceToGroup(dragChoiceId.current, dragChoiceGroupId.current, group.id); dragChoiceId.current = null; dragChoiceGroupId.current = null; } }}
          onDragEnd={() => { setGroupDragTarget(null); setGroupChoiceDropTarget(null); dragGroupId.current = null; dragGroupAllowed.current = false; }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="cursor-grab text-slate-300 hover:text-slate-500 transition-colors"
                title="Drag to reorder"
                onMouseDown={() => { dragGroupAllowed.current = true; }}
              >
                <GripVertical size={14} />
              </span>
              {group.sharedGroupId != null && (
                <span className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-medium border border-indigo-100">
                  <Library size={10} /> Shared
                </span>
              )}
              {editingGroupName === group.id ? (
                <input
                  autoFocus
                  className="font-semibold text-sm border border-primary rounded px-1.5 py-0.5 outline-none w-48"
                  value={groupNameDraft}
                  onChange={(e) => setGroupNameDraft(e.target.value)}
                  onBlur={() => void saveGroupName(group.id, groupNameDraft)}
                  onKeyDown={(e) => { if (e.key === "Enter") void saveGroupName(group.id, groupNameDraft); if (e.key === "Escape") setEditingGroupName(null); }}
                />
              ) : (
                <p
                  className="font-semibold text-sm text-slate-800 cursor-pointer hover:text-primary transition-colors"
                  title="Click to rename"
                  onClick={() => { if (group.sharedGroupId == null) { setEditingGroupName(group.id); setGroupNameDraft(group.name); } }}
                >{group.name}</p>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${group.minSelections >= 1 ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}>
                {groupSelectionLabel(group.minSelections ?? 0, group.maxSelections ?? null)}
              </span>
              {conditionOption && (
                <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full font-medium border border-amber-200">
                  ⚡ Shows when &ldquo;{conditionOption.name}&rdquo; selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); void moveGroupUp(group.id); }}
                disabled={groupIdx === 0}
                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                title="Move group up"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); void moveGroupDown(group.id); }}
                disabled={groupIdx === sortedGroupIds.length - 1}
                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                title="Move group down"
              >
                <ChevronDown size={14} />
              </button>
              <button
                onClick={() => setEditingCondition(editingCondition === group.id ? null : group.id)}
                className="text-slate-300 hover:text-amber-500 transition-colors"
                title="Set conditional display"
              >
                <Settings2 size={13} />
              </button>
              <button
                onClick={() => void deleteGroup(group.id)}
                className="text-slate-300 hover:text-red-500 transition-colors"
                title={group.sharedGroupId != null ? "Detach from this item" : "Delete group"}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          {editingCondition === group.id && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-amber-700">Conditional display — show this group only when a specific option is selected in another group</p>
              <select
                className="w-full border border-amber-200 rounded-md text-xs px-2 py-1.5 bg-white"
                value={group.dependsOnOptionId ?? ""}
                onChange={(e) => void setCondition(group.id, e.target.value === "" ? null : Number(e.target.value))}
              >
                <option value="">Always show (no condition)</option>
                {groups.filter(g => g.id !== group.id).map(g => (
                  <optgroup key={g.id} label={g.name}>
                    {g.choices.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.pricePence > 0 ? ` (+£${(c.pricePence/100).toFixed(2)})` : ""}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {allOptions.length === 0 && <p className="text-xs text-amber-600 italic">Add options to other groups first to use as conditions.</p>}
              <button onClick={() => setEditingCondition(null)} className="text-xs text-amber-600 hover:text-amber-800 dark:text-amber-300">Close</button>
            </div>
          )}

          <div className="ml-1 space-y-1">
            {group.choices.map((choice) => (
              <div
                key={choice.id}
                data-choice-row="true"
                className={`flex items-center justify-between rounded-lg px-3 py-1.5 transition-colors ${choiceDragTarget === choice.id ? "border border-primary ring-1 ring-primary bg-primary/5" : "bg-slate-50"}`}
                draggable={group.sharedGroupId == null}
                onDragStart={(e) => { if (!choiceDragAllowed.current) { e.preventDefault(); return; } e.stopPropagation(); dragChoiceId.current = choice.id; dragChoiceGroupId.current = group.id; }}
                onDragOver={(e) => { e.preventDefault(); if (dragGroupId.current !== null) return; e.stopPropagation(); dragOverChoiceId.current = choice.id; setChoiceDragTarget(choice.id); }}
                onDragLeave={(e) => { if (dragGroupId.current !== null) return; e.stopPropagation(); if (dragOverChoiceId.current === choice.id) { setChoiceDragTarget(null); dragOverChoiceId.current = null; } }}
                onDrop={(e) => { e.preventDefault(); if (dragGroupId.current !== null) return; e.stopPropagation(); setChoiceDragTarget(null); setGroupChoiceDropTarget(null); if (dragChoiceId.current != null && dragChoiceId.current !== choice.id) { if (dragChoiceGroupId.current === group.id) void reorderChoices(group.id, dragChoiceId.current, choice.id); else if (dragChoiceGroupId.current !== null) void moveChoiceToGroup(dragChoiceId.current, dragChoiceGroupId.current, group.id, choice.id); } dragChoiceId.current = null; dragChoiceGroupId.current = null; }}
                onDragEnd={(e) => { e.stopPropagation(); setChoiceDragTarget(null); dragChoiceId.current = null; dragChoiceGroupId.current = null; choiceDragAllowed.current = false; }}
              >
                <div className="flex items-center gap-2">
                  {group.sharedGroupId == null && (
                    <span
                      className="cursor-grab text-slate-300 hover:text-slate-500 transition-colors"
                      onMouseDown={() => { choiceDragAllowed.current = true; }}
                      title="Drag to reorder"
                    >
                      <GripVertical size={12} />
                    </span>
                  )}
                  <span className="text-sm text-slate-700">{choice.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{choice.pricePence > 0 ? `+£${(choice.pricePence / 100).toFixed(2)}` : "Free"}</span>
                  {group.sharedGroupId == null && (
                    <button onClick={() => void deleteChoice(group.id, choice.id)} className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                  )}
                </div>
              </div>
            ))}

            {group.sharedGroupId != null ? (
              <p className="text-xs text-indigo-500 italic mt-1 flex items-center gap-1">
                <Library size={10} /> Edit choices in the <span className="font-medium ml-0.5">Option Group Library</span> tab
              </p>
            ) : (
              addChoiceForms[group.id] !== undefined ? (
                <div className="flex gap-2 mt-1.5">
                  <Input
                    placeholder="Choice name" value={addChoiceForms[group.id]!.name}
                    onChange={(e) => setAddChoiceForms((prev) => ({ ...prev, [group.id]: { ...prev[group.id]!, name: e.target.value } }))}
                    className="h-7 text-xs flex-1"
                  />
                  <Input
                    type="number" min={0} step={0.01} placeholder="Price £"
                    value={addChoiceForms[group.id]!.pricePence / 100 || ""}
                    onChange={(e) => setAddChoiceForms((prev) => ({ ...prev, [group.id]: { ...prev[group.id]!, pricePence: Math.round(Number(e.target.value) * 100) } }))}
                    className="h-7 text-xs w-20"
                  />
                  <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 px-2" onClick={() => void addChoice(group.id)}>Add</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setAddChoiceForms((prev) => { const n = { ...prev }; delete n[group.id]; return n; })}>Cancel</Button>
                </div>
              ) : (
                <button
                  onClick={() => setAddChoiceForms((prev) => ({ ...prev, [group.id]: { name: "", pricePence: 0 } }))}
                  className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 mt-1"
                >
                  <Plus size={11} /> Add choice
                </button>
              )
            )}
          </div>
        </div>
        );
      })}

      {groups.length === 0 && <p className="text-xs text-slate-400 italic">No option groups yet — add one below.</p>}

      {showLibraryPicker && library.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1"><Library size={12} /> Pick from library</p>
            <button onClick={() => setShowLibraryPicker(false)} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>
          </div>
          {library.map(g => (
            <button key={g.id} onClick={() => void attachFromLibrary(g.id)}
              className="w-full text-left bg-white rounded-lg border border-indigo-100 px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
              <p className="text-sm font-medium text-slate-800">{g.name}</p>
              <p className="text-xs text-slate-400">{groupSelectionLabel(g.minSelections ?? 0, g.maxSelections ?? null)} · {g.options.length} option{g.options.length !== 1 ? "s" : ""}</p>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        {addGroupForm ? (
          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2 w-full">
            <Input placeholder="Group name (e.g. Select your sauce)" value={addGroupForm.name} onChange={(e) => setAddGroupForm({ ...addGroupForm, name: e.target.value })} className="h-8 text-sm" />
            <div className="flex gap-4 items-end flex-wrap">
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                Min selections
                <Input type="number" min={0} value={addGroupForm.minSelections} onChange={(e) => setAddGroupForm({ ...addGroupForm, minSelections: Math.max(0, Number(e.target.value)) })} className="h-7 w-20 text-xs" />
                <span className="text-slate-400">0 = optional</span>
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                Max selections
                <Input type="number" min={0} value={addGroupForm.maxSelections} onChange={(e) => setAddGroupForm({ ...addGroupForm, maxSelections: Math.max(0, Number(e.target.value)) })} className="h-7 w-20 text-xs" />
                <span className="text-slate-400">0 = unlimited</span>
              </label>
            </div>
            {groups.some(g => g.choices.length > 0) && (
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                Show only when… (optional)
                <select
                  className="border border-slate-200 rounded-md text-xs px-2 py-1.5 bg-white"
                  value={addGroupForm.dependsOnOptionId ?? ""}
                  onChange={(e) => setAddGroupForm({ ...addGroupForm, dependsOnOptionId: e.target.value === "" ? null : Number(e.target.value) })}
                >
                  <option value="">Always show</option>
                  {groups.map(g => (
                    <optgroup key={g.id} label={g.name}>
                      {g.choices.map(c => (
                        <option key={c.id} value={c.id}>{c.name}{c.pricePence > 0 ? ` (+£${(c.pricePence/100).toFixed(2)})` : ""}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="text-slate-400">This group only appears when the selected option is chosen</span>
              </label>
            )}
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90" onClick={() => void addGroup()} disabled={!addGroupForm.name.trim()}>Create Group</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddGroupForm(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => { setShowLibraryPicker(false); setAddGroupForm({ name: "", minSelections: 0, maxSelections: 1, dependsOnOptionId: null }); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80"
            >
              <Plus size={12} /> New group
            </button>
            {library.length > 0 && (
              <button
                onClick={() => { setAddGroupForm(null); setShowLibraryPicker((p) => !p); }}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <Library size={12} /> Attach from library
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Shared Option Group Library ──────────────────────────────────────────────

function SharedGroupLibrary({ storeId }: { storeId: number }) {
  const { toast } = useToast();
  const [groups, setGroups] = useState<SharedLibraryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [addGroupForm, setAddGroupForm] = useState<{ name: string; minSelections: number; maxSelections: number } | null>(null);
  const [addChoiceForms, setAddChoiceForms] = useState<Record<number, { name: string; pricePence: number }>>({});
  const [editingGroup, setEditingGroup] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editMin, setEditMin] = useState(0);
  const [editMax, setEditMax] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/order/stores/${storeId}/shared-option-groups`, { headers: authHeaders() });
      const d = await r.json() as { groups: Array<{ id: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null }>; options: Array<{ id: number; sharedGroupId: number; name: string; pricePence: number }> };
      setGroups((d.groups ?? []).map(g => ({ ...g, options: (d.options ?? []).filter(o => o.sharedGroupId === g.id) })));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { void load(); }, [load]);

  async function createGroup() {
    if (!addGroupForm?.name.trim()) return;
    const { minSelections, maxSelections } = addGroupForm;
    const max = maxSelections === 0 ? null : maxSelections;
    const r = await fetch(`/api/order/stores/${storeId}/shared-option-groups`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        name: addGroupForm.name.trim(),
        required: minSelections >= 1,
        multiSelect: max === null || max > 1,
        minSelections,
        maxSelections: max,
      }),
    });
    if (r.ok) { setAddGroupForm(null); void load(); }
    else toast({ title: "Failed to create group", variant: "destructive" });
  }

  async function saveGroupEdit(groupId: number) {
    if (!editName.trim()) return;
    const max = editMax === 0 ? null : editMax;
    await fetch(`/api/order/stores/${storeId}/shared-option-groups/${groupId}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({
        name: editName.trim(),
        required: editMin >= 1,
        multiSelect: max === null || max > 1,
        minSelections: editMin,
        maxSelections: max,
      }),
    });
    setEditingGroup(null);
    void load();
  }

  async function deleteGroup(groupId: number) {
    const r = await fetch(`/api/order/stores/${storeId}/shared-option-groups/${groupId}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) void load();
    else toast({ title: "Failed to delete group", variant: "destructive" });
  }

  async function addOption(groupId: number) {
    const form = addChoiceForms[groupId];
    if (!form?.name.trim()) return;
    const r = await fetch(`/api/order/stores/${storeId}/shared-option-groups/${groupId}/options`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ name: form.name.trim(), pricePence: form.pricePence ?? 0 }),
    });
    if (r.ok) { setAddChoiceForms((prev) => { const n = { ...prev }; delete n[groupId]; return n; }); void load(); }
    else toast({ title: "Failed to add option", variant: "destructive" });
  }

  async function deleteOption(groupId: number, optionId: number) {
    const r = await fetch(`/api/order/stores/${storeId}/shared-option-groups/${groupId}/options/${optionId}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) void load();
    else toast({ title: "Failed to delete option", variant: "destructive" });
  }

  if (loading) return <div className="text-sm text-slate-400 animate-pulse p-4">Loading library…</div>;

  return (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
        <p className="text-xs text-indigo-700 font-medium flex items-center gap-2">
          <Library size={13} />
          Create reusable option groups here — sauces, toppings, add-ons — then attach them to any product from the Menu tab. Editing a shared group updates it on every product that uses it.
        </p>
      </div>

      {groups.length === 0 && !addGroupForm && (
        <p className="text-sm text-slate-400 italic text-center py-6">No shared groups yet. Create your first one below.</p>
      )}

      {groups.map((group) => (
        <div key={group.id} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            {editingGroup === group.id ? (
              <div className="space-y-2 flex-1">
                <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-8 text-sm" autoFocus placeholder="Group name" />
                <div className="flex gap-4 items-end">
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Min selections
                    <Input type="number" min={0} value={editMin} onChange={e => setEditMin(Math.max(0, Number(e.target.value)))} className="h-7 w-20 text-xs" />
                    <span className="text-slate-400">0 = optional</span>
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Max selections
                    <Input type="number" min={0} value={editMax} onChange={e => setEditMax(Math.max(0, Number(e.target.value)))} className="h-7 w-20 text-xs" />
                    <span className="text-slate-400">0 = unlimited</span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 px-2" onClick={() => void saveGroupEdit(group.id)}>Save</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setEditingGroup(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap flex-1">
                <p className="font-semibold text-slate-800">{group.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${group.minSelections >= 1 ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}>
                  {groupSelectionLabel(group.minSelections ?? 0, group.maxSelections ?? null)}
                </span>
                <button onClick={() => { setEditingGroup(group.id); setEditName(group.name); setEditMin(group.minSelections ?? 0); setEditMax(group.maxSelections ?? 0); }} className="text-slate-300 hover:text-primary transition-colors ml-1"><Pencil size={12} /></button>
              </div>
            )}
            {editingGroup !== group.id && (
              <button onClick={() => void deleteGroup(group.id)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0"><Trash2 size={13} /></button>
            )}
          </div>

          <div className="space-y-1 ml-1">
            {group.options.map(opt => (
              <div key={opt.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5">
                <span className="text-sm text-slate-700">{opt.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{opt.pricePence > 0 ? `+£${(opt.pricePence / 100).toFixed(2)}` : "Free"}</span>
                  <button onClick={() => void deleteOption(group.id, opt.id)} className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                </div>
              </div>
            ))}

            {addChoiceForms[group.id] !== undefined ? (
              <div className="flex gap-2 mt-1.5">
                <Input
                  placeholder="Option name" value={addChoiceForms[group.id]!.name}
                  onChange={(e) => setAddChoiceForms(prev => ({ ...prev, [group.id]: { ...prev[group.id]!, name: e.target.value } }))}
                  className="h-7 text-xs flex-1"
                />
                <Input
                  type="number" min={0} step={0.01} placeholder="Price £"
                  value={addChoiceForms[group.id]!.pricePence / 100 || ""}
                  onChange={(e) => setAddChoiceForms(prev => ({ ...prev, [group.id]: { ...prev[group.id]!, pricePence: Math.round(Number(e.target.value) * 100) } }))}
                  className="h-7 text-xs w-20"
                />
                <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 px-2" onClick={() => void addOption(group.id)}>Add</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setAddChoiceForms(prev => { const n = { ...prev }; delete n[group.id]; return n; })}>Cancel</Button>
              </div>
            ) : (
              <button onClick={() => setAddChoiceForms(prev => ({ ...prev, [group.id]: { name: "", pricePence: 0 } }))}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 mt-1">
                <Plus size={11} /> Add option
              </button>
            )}
          </div>
        </div>
      ))}

      {addGroupForm ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <Input placeholder="Group name (e.g. Choose your sauce)" value={addGroupForm.name} onChange={(e) => setAddGroupForm({ ...addGroupForm, name: e.target.value })} className="h-8 text-sm" />
          <div className="flex gap-4 items-end">
            <label className="flex flex-col gap-1 text-xs text-slate-600">
              Min selections
              <Input type="number" min={0} value={addGroupForm.minSelections} onChange={(e) => setAddGroupForm({ ...addGroupForm, minSelections: Math.max(0, Number(e.target.value)) })} className="h-7 w-20 text-xs" />
              <span className="text-slate-400">0 = optional</span>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-600">
              Max selections
              <Input type="number" min={0} value={addGroupForm.maxSelections} onChange={(e) => setAddGroupForm({ ...addGroupForm, maxSelections: Math.max(0, Number(e.target.value)) })} className="h-7 w-20 text-xs" />
              <span className="text-slate-400">0 = unlimited</span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90" onClick={() => void createGroup()} disabled={!addGroupForm.name.trim()}>Create Group</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddGroupForm(null)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAddGroupForm({ name: "", minSelections: 0, maxSelections: 1 })}
          className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80">
          <Plus size={14} /> Create shared group
        </button>
      )}
    </div>
  );
}

// ─── Store Create / Edit Form ─────────────────────────────────────────────────
function StoreForm({ initial, onSave, onCancel }: {
  initial?: Partial<StoreData>;
  onSave: (d: Partial<StoreData>) => void;
  onCancel: () => void;
}) {
  const [coverBusy, setCoverBusy] = useState(false);
  const { towns: allTowns } = useTown();
  const TOWNS = allTowns.filter(t => t !== "All Towns");
  const { data: catData } = useGetOrderCategories();
  const STORE_CATS_FOOD = (catData?.items ?? []).filter((c) => c.type === "food").map((c) => c.name);
  const STORE_CATS_RETAIL = (catData?.items ?? []).filter((c) => c.type === "retail").map((c) => c.name);
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    categories: (initial?.categories?.length ? initial.categories : initial?.category ? [initial.category] : []) as string[],
    town: initial?.town ?? "Ayr",
    address: initial?.address ?? "",
    phone: initial?.phone ?? "",
    email: initial?.email ?? "",
    imageUrl: initial?.imageUrl ?? "",
    bannerUrl: initial?.bannerUrl ?? "",
    coverMode: initial?.coverMode ?? "image",
    coverAnimationUrl: initial?.coverAnimationUrl ?? null,
    coverPosterUrl: initial?.coverPosterUrl ?? null,
    coverVideoUrl: initial?.coverVideoUrl ?? null,
    offersDelivery: initial?.offersDelivery ?? true,
    offersCollection: initial?.offersCollection ?? true,
    minOrderPence: initial?.minOrderPence ?? 0,
    minOrderBreakfastPence: initial?.minOrderBreakfastPence ?? null,
    minOrderLunchPence: initial?.minOrderLunchPence ?? null,
    minOrderEveningPence: initial?.minOrderEveningPence ?? null,
    deliveryFeePence: initial?.deliveryFeePence ?? 0,
    deliveryTimeMins: initial?.deliveryTimeMins ?? 45,
    collectionTimeMins: initial?.collectionTimeMins ?? 20,
    openingHours: (() => {
      const raw = initial?.openingHours ?? "";
      if (!raw.trim() || raw.trim()[0] !== "{") {
        return JSON.stringify(Object.fromEntries(DAY_KEYS.map((k) => [k, null])));
      }
      return raw;
    })(),

    bagChargePence: initial?.bagChargePence ?? 0,
    deliveryTowns: (initial?.deliveryTowns ?? []) as string[],
    acceptsCash: initial?.acceptsCash ?? false,
    acceptsGlobalCodes: initial?.acceptsGlobalCodes ?? true,
  });

  return (
    <div className="space-y-4">
      {initial?.slug === "premier-dean-street" && <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
        <p className="font-bold">Shop website content</p>
        <p className="mt-1">Use the Branded homepage editor in Settings for website headlines and photos. Product and category photos are edited in Menu, and promotions in Offers.</p>
        <a className="mt-2 inline-block font-bold underline underline-offset-2" href="/premier/" target="_blank" rel="noopener noreferrer">View Premier website ↗</a>
      </div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">Store Name *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mario's Pizza" />
        </div>
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">
            Categories * <span className="text-slate-400 font-normal">(pick 1–3)</span>
          </label>
          <div className="mt-2 space-y-3">
            {/* Orphaned selections — saved values not present in the live category list */}
            {(() => {
              const allKnown = new Set([...STORE_CATS_FOOD, ...STORE_CATS_RETAIL]);
              const orphans = form.categories.filter((c) => !allKnown.has(c));
              if (!orphans.length) return null;
              return (
                <div className="flex flex-wrap gap-1.5">
                  {orphans.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, categories: form.categories.filter((x) => x !== c) })}
                      className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-200 text-slate-500 border-slate-300 line-through flex items-center gap-1"
                      title="Click to remove this outdated category"
                    >
                      {c} ×
                    </button>
                  ))}
                  <p className="w-full text-[11px] text-slate-400">↑ These saved categories no longer exist — click to remove them</p>
                </div>
              );
            })()}
            <div>
              <p className="text-[11px] font-semibold text-orange-500 uppercase tracking-wider mb-1.5">🍔 Food & Takeaways</p>
              <div className="flex flex-wrap gap-1.5">
                {STORE_CATS_FOOD.map((c) => {
                  const selected = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        if (selected) {
                          setForm({ ...form, categories: form.categories.filter((x) => x !== c) });
                        } else if (form.categories.length < 6) {
                          setForm({ ...form, categories: [...form.categories, c] });
                        }
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                        selected
                          ? "bg-orange-500 text-white border-orange-500"
                          : form.categories.length >= 6
                          ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                          : "bg-white text-slate-600 border-slate-200 hover:border-orange-300 hover:text-orange-600"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-1.5">🛒 Shops & Services</p>
              <div className="flex flex-wrap gap-1.5">
                {STORE_CATS_RETAIL.map((c) => {
                  const selected = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        if (selected) {
                          setForm({ ...form, categories: form.categories.filter((x) => x !== c) });
                        } else if (form.categories.length < 6) {
                          setForm({ ...form, categories: [...form.categories, c] });
                        }
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                        selected
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : form.categories.length >= 6
                          ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-600"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.categories.length === 0 && (
              <p className="text-xs text-red-500">Please select at least one category.</p>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Town *</label>
          <select value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background">
            {TOWNS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">Address *</label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" />
        </div>
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Brief description of your store" />
          <p className="mt-1 text-xs text-slate-500">Shown on your Ayrshire Hub shop listing and ordering page.</p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Phone</label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Email</label>
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">Store logo or square image</label>
          <ImageUpload value={form.imageUrl} onChange={(url) => setForm({ ...form, imageUrl: url ?? "" })} stockCategory="deal" />
        </div>
        <div className="col-span-full">
          <label className="text-xs font-medium text-slate-500">Cover image (wide)</label>
          <ImageUpload value={form.bannerUrl} onChange={(url) => setForm({ ...form, bannerUrl: url ?? "" })} stockCategory="deal" />
          <div className="mt-3"><ShopCoverEditor storeId={initial?.id} value={form} onChange={patch => setForm(current => ({ ...current, ...patch }))} onBusyChange={setCoverBusy} /></div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Fulfilment</p>
        <div className="flex gap-4 flex-wrap mb-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.offersDelivery} onChange={(e) => setForm({ ...form, offersDelivery: e.target.checked })} className="w-4 h-4 accent-primary" />
            <Truck size={14} /> Delivery
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.offersCollection} onChange={(e) => setForm({ ...form, offersCollection: e.target.checked })} className="w-4 h-4 accent-primary" />
            <Package size={14} /> Collection
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.acceptsCash} onChange={(e) => setForm({ ...form, acceptsCash: e.target.checked })} className="w-4 h-4 accent-primary" />
            <Banknote size={14} /> Accepts cash
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" title="Hub-wide promo codes run by Ayrshire Hub. You fund the discount, like your own codes.">
            <input type="checkbox" checked={form.acceptsGlobalCodes} onChange={(e) => setForm({ ...form, acceptsGlobalCodes: e.target.checked })} className="w-4 h-4 accent-primary" />
            <Tag size={14} /> Accept hub promo codes
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Delivery fee (£)</label>
            <MoneyInput value={form.deliveryFeePence} onChange={(p) => setForm({ ...form, deliveryFeePence: p })} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Delivery (mins)</label>
            <Input type="number" min={1} value={form.deliveryTimeMins} onChange={(e) => setForm({ ...form, deliveryTimeMins: Number(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Collection (mins)</label>
            <Input type="number" min={1} value={form.collectionTimeMins} onChange={(e) => setForm({ ...form, collectionTimeMins: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Minimum Order Value</p>
          <p className="text-xs text-slate-400 mb-3">Set a base minimum and optionally override it by time slot. Leave a slot blank to use the base minimum, or tick &ldquo;No minimum in this window&rdquo; to waive it for that time only. A base of £0 means no minimum spend at all.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Base (all times)</label>
              <MoneyInput value={form.minOrderPence} onChange={(p) => setForm({ ...form, minOrderPence: p })} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Breakfast (7–11am)</label>
              <MoneyInput value={form.minOrderBreakfastPence ?? 0} onChange={(p) => setForm({ ...form, minOrderBreakfastPence: p === 0 ? null : p })} disabled={form.minOrderBreakfastPence === 0} />
              <NoMinimumToggle checked={form.minOrderBreakfastPence === 0} onChange={(c) => setForm({ ...form, minOrderBreakfastPence: c ? 0 : null })} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Lunch (11am–3pm)</label>
              <MoneyInput value={form.minOrderLunchPence ?? 0} onChange={(p) => setForm({ ...form, minOrderLunchPence: p === 0 ? null : p })} disabled={form.minOrderLunchPence === 0} />
              <NoMinimumToggle checked={form.minOrderLunchPence === 0} onChange={(c) => setForm({ ...form, minOrderLunchPence: c ? 0 : null })} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Evening (3pm+)</label>
              <MoneyInput value={form.minOrderEveningPence ?? 0} onChange={(p) => setForm({ ...form, minOrderEveningPence: p === 0 ? null : p })} disabled={form.minOrderEveningPence === 0} />
              <NoMinimumToggle checked={form.minOrderEveningPence === 0} onChange={(c) => setForm({ ...form, minOrderEveningPence: c ? 0 : null })} />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-500 mb-2 block">
          Operating Hours <span className="text-slate-400 font-normal">— "Last order" is the cutoff; orders after this time are rejected</span>
        </label>
        <HoursEditor value={form.openingHours} onChange={(v) => setForm({ ...form, openingHours: v })} />
      </div>

      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Charges</p>
        <div className="max-w-xs">
          <label className="text-xs font-medium text-slate-500">Carrier bag charge (£)</label>
          <MoneyInput value={form.bagChargePence ?? 0} onChange={(p) => setForm({ ...form, bagChargePence: p })} />
        </div>
      </div>

      {form.offersDelivery && (
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Delivery Towns</p>
          <p className="text-xs text-slate-400 mb-3">Your store is registered in <strong>{form.town}</strong>. Select any additional towns you deliver to — your store will appear when customers browse those towns.</p>
          <div className="flex flex-wrap gap-2">
            {TOWNS.filter((t) => t !== form.town).map((t) => {
              const checked = form.deliveryTowns.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, deliveryTowns: checked ? form.deliveryTowns.filter((x) => x !== t) : [...form.deliveryTowns, t] })}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${checked ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button className="bg-primary hover:bg-primary/90" onClick={() => onSave({ ...form, category: form.categories[0] ?? "", categories: form.categories })} disabled={coverBusy || !form.name || !form.address || form.categories.length === 0}>
          Save Store
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── Delivery Zones Editor ────────────────────────────────────────────────────
type EditingZone = { id: number; feePence: number; label: string; maxRadiusKm: number | null };

function ZoneEditRow({
  zone, bg, onChange, onSave, onCancel,
}: {
  zone: EditingZone;
  bg: string;
  onChange: (z: EditingZone) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${bg} rounded-xl px-3 py-2`}>
      {zone.maxRadiusKm !== null && (
        <div className="flex items-center gap-1 shrink-0">
          <input
            type="number" min={0.1} step={0.1}
            value={zone.maxRadiusKm === 0 ? "" : (zone.maxRadiusKm / 1.60934).toFixed(1)}
            placeholder="miles"
            onChange={(e) => onChange({ ...zone, maxRadiusKm: e.target.value ? parseFloat(e.target.value) * 1.60934 : 0 })}
            onFocus={(e) => e.target.select()}
            className="w-16 text-sm border border-primary rounded px-1.5 py-1 outline-none"
          />
          <span className="text-xs text-slate-400">mi</span>
        </div>
      )}
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-xs text-slate-400">£</span>
        <input
          type="number" min={0} step={0.01}
          value={zone.feePence === 0 ? "" : (zone.feePence / 100).toFixed(2)}
          placeholder="0.00"
          onChange={(e) => onChange({ ...zone, feePence: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : 0 })}
          onFocus={(e) => e.target.select()}
          className="w-20 text-sm border border-primary rounded px-1.5 py-1 outline-none"
        />
      </div>
      <input
        value={zone.label}
        onChange={(e) => onChange({ ...zone, label: e.target.value })}
        placeholder="Label (optional)"
        className="flex-1 min-w-0 text-sm border border-slate-200 rounded px-1.5 py-1 outline-none focus:border-primary"
      />
      <button onClick={onSave} className="text-xs bg-primary text-white rounded px-2 py-1 hover:bg-primary/90 shrink-0">Save</button>
      <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-600 shrink-0">Cancel</button>
    </div>
  );
}

function DeliveryZonesEditor({ storeId }: { storeId: number }) {
  const { toast } = useToast();
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [zoneType, setZoneType] = useState<"radius" | "postcode">("radius");
  const [newZone, setNewZone] = useState({ postcodePrefix: "", maxRadiusMiles: "", feePence: 0, label: "" });
  const [editingZone, setEditingZone] = useState<EditingZone | null>(null);

  const loadZones = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/order/stores/${storeId}/zones`, { headers: authHeaders() });
      const d = await r.json() as { items: DeliveryZone[] };
      setZones(d.items ?? []);
    } finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { void loadZones(); }, [loadZones]);

  async function addZone() {
    const body: Record<string, unknown> = { feePence: newZone.feePence, label: newZone.label || undefined };
    if (zoneType === "postcode") {
      const prefix = newZone.postcodePrefix.trim().toUpperCase();
      if (!prefix) { toast({ title: "Enter a postcode prefix", variant: "destructive" }); return; }
      body["postcodePrefix"] = prefix;
    } else {
      const miles = Number(newZone.maxRadiusMiles);
      if (!miles || miles <= 0) { toast({ title: "Enter a valid radius in miles", variant: "destructive" }); return; }
      body["maxRadiusKm"] = miles * 1.60934;
    }
    const r = await fetch(`/api/order/stores/${storeId}/zones`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    if (!r.ok) {
      const data = await r.json().catch(() => ({})) as { error?: string };
      toast({ title: "Failed to add zone", description: data.error ?? "An unexpected error occurred.", variant: "destructive" });
      return;
    }
    setAdding(false);
    setNewZone({ postcodePrefix: "", maxRadiusMiles: "", feePence: 0, label: "" });
    await loadZones();
  }

  function zoneLabel(z: DeliveryZone) {
    if (z.postcodePrefix) {
      const p = z.postcodePrefix.trim().toUpperCase();
      return p.includes(" ") ? `Sector: ${p}` : `Postcode: ${p}`;
    }
    if (z.maxRadiusKm) return `Within ${(z.maxRadiusKm * 0.621371).toFixed(1)} miles`;
    return "Any";
  }

  async function deleteZone(id: number) {
    await fetch(`/api/order/zones/${id}`, { method: "DELETE", headers: authHeaders() });
    setZones((z) => z.filter((x) => x.id !== id));
  }

  async function saveZoneEdit() {
    if (!editingZone) return;
    const res = await fetch(`/api/order/zones/${editingZone.id}`, {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify({
        feePence: editingZone.feePence,
        label: editingZone.label || null,
        ...(editingZone.maxRadiusKm !== null ? { maxRadiusKm: editingZone.maxRadiusKm } : {}),
      }),
    });
    if (res.ok) {
      const updated = await res.json() as DeliveryZone;
      setZones((z) => z.map((x) => (x.id === editingZone.id ? { ...x, ...updated } : x)));
    }
    setEditingZone(null);
  }

  const radiusZones   = zones.filter((z) => !z.postcodePrefix).sort((a, b) => (a.maxRadiusKm ?? 0) - (b.maxRadiusKm ?? 0));
  const postcodeZones = zones.filter((z) =>  z.postcodePrefix);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground text-sm">Delivery Zones</h3>
        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => { setAdding(true); setZoneType("radius"); }}><Plus size={12} /> Add zone</Button>
      </div>
      <p className="text-xs text-slate-500">
        Set radius tiers from your store pin, then add postcode overrides for specific areas.
        Postcode overrides always take priority over radius.
      </p>

      {loading ? (
        <div className="py-4 flex justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" /></div>
      ) : zones.length === 0 && !adding ? (
        <p className="text-xs text-slate-400 text-center py-3">No zones set — using the flat delivery fee above.</p>
      ) : (
        <div className="space-y-3">
          {radiusZones.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Radius tiers</p>
              <div className="space-y-1.5">
                {radiusZones.map((z) => editingZone?.id === z.id ? (
                  <ZoneEditRow key={z.id} zone={editingZone} bg="bg-slate-50"
                    onChange={setEditingZone}
                    onSave={() => void saveZoneEdit()}
                    onCancel={() => setEditingZone(null)}
                  />
                ) : (
                  <div key={z.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800">{zoneLabel(z)}</span>
                      {z.label && <span className="text-slate-500 ml-2">({z.label})</span>}
                    </div>
                    <span className="font-bold text-primary">{z.feePence === 0 ? "Free" : `£${(z.feePence / 100).toFixed(2)}`}</span>
                    <button onClick={() => setEditingZone({ id: z.id, feePence: z.feePence, label: z.label ?? "", maxRadiusKm: z.maxRadiusKm ?? 0 })} className="text-slate-300 hover:text-primary" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => void deleteZone(z.id)} className="text-slate-300 hover:text-red-500" title="Delete"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {postcodeZones.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Postcode overrides</p>
              <div className="space-y-1.5">
                {postcodeZones.map((z) => editingZone?.id === z.id ? (
                  <ZoneEditRow key={z.id} zone={editingZone} bg="bg-blue-50 dark:bg-blue-900/20"
                    onChange={setEditingZone}
                    onSave={() => void saveZoneEdit()}
                    onCancel={() => setEditingZone(null)}
                  />
                ) : (
                  <div key={z.id} className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800">{zoneLabel(z)}</span>
                      {z.label && <span className="text-slate-500 ml-2">({z.label})</span>}
                    </div>
                    <span className="font-bold text-primary">{z.feePence === 0 ? "Free" : `£${(z.feePence / 100).toFixed(2)}`}</span>
                    <button onClick={() => setEditingZone({ id: z.id, feePence: z.feePence, label: z.label ?? "", maxRadiusKm: null })} className="text-slate-300 hover:text-primary" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => void deleteZone(z.id)} className="text-slate-300 hover:text-red-500" title="Delete"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {adding && (
        <div className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50">
          {/* Zone type toggle */}
          <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs font-semibold">
            <button
              className={`flex-1 py-1.5 transition-colors ${zoneType === "radius" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setZoneType("radius")}
            >Radius tier</button>
            <button
              className={`flex-1 py-1.5 transition-colors ${zoneType === "postcode" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setZoneType("postcode")}
            >Postcode override</button>
          </div>

          {zoneType === "radius" ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-500">Distance from store (miles)</label>
                <Input type="number" min={0} step={0.1} value={newZone.maxRadiusMiles} onChange={(e) => setNewZone({ ...newZone, maxRadiusMiles: e.target.value })} placeholder="e.g. 3" className="text-sm" />
                <p className="text-xs text-slate-400 mt-0.5">Requires store address with coordinates set</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-500">Postcode prefix or sector</label>
                <Input
                  value={newZone.postcodePrefix}
                  onChange={(e) => setNewZone({ ...newZone, postcodePrefix: e.target.value })}
                  placeholder="e.g. KA8 or KA8 4"
                  className="text-sm"
                />
                <p className="text-xs text-slate-400 mt-0.5">
                  <strong>KA8</strong> matches all KA8 postcodes · <strong>KA8 4</strong> matches only KA8 4xx (more specific, wins over KA8)
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Fee (£)</label>
              <Input type="number" min={0} step={0.01} value={newZone.feePence / 100} onChange={(e) => setNewZone({ ...newZone, feePence: Math.round(Number(e.target.value) * 100) })} className="text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Label (optional)</label>
              <Input value={newZone.label} onChange={(e) => setNewZone({ ...newZone, label: e.target.value })} placeholder="e.g. Prestwick" className="text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => void addZone()}>Add</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Store Management Panel ────────────────────────────────────────────────────
function StorePanel({ storeId, staffRole = "owner" }: { storeId: number; staffRole?: "owner" | "manager" | "staff" }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [store, setStore] = useState<StoreData | null>(null);
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [menuSearch, setMenuSearch] = useState("");
  const [mmSearch, setMmSearch] = useState("");
  const [mmAllItems, setMmAllItems] = useState<MenuItem[]>([]);
  const [mmAllItemsLoading, setMmAllItemsLoading] = useState(false);
  const [mmItemOptions, setMmItemOptions] = useState<ItemOptionSummary>({});
  const loadedSectionIdsRef = useRef<Set<number>>(new Set());
  const loadingSectionIdsRef = useRef<Set<number>>(new Set());
  const [loadingSectionIds, setLoadingSectionIds] = useState<Set<number>>(new Set());
  const [deliveryRuns,setDeliveryRuns]=useState<PadRun[]>([]);
  const runStoreRef=useRef(storeId);runStoreRef.current=storeId;
  useEffect(()=>{setDeliveryRuns([]);setDeliveryRunsError("");},[storeId]);
  const [deliveryRunsError,setDeliveryRunsError]=useState("");
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [tab, setTab] = useState<"menu" | "orders" | "analytics" | "offers" | "settings" | "stripe" | "library" | "staff" | "drivers" | "abandoned" | "sms">("menu");
  const [abandonedBaskets, setAbandonedBaskets] = useState<{ id: number; userId: number; customerName: string | null; items: Array<{ name: string; pricePence: number; quantity: number }>; estimatedSubtotalPence: number | null; abandonedAt: string }[]>([]);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [staffList, setStaffList] = useState<{ id: number; email: string; role: string; name: string | null; userId: number | null; inviteAcceptedAt: string | null; createdAt: string }[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"staff" | "manager">("staff");
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addSectionName, setAddSectionName] = useState("");
  // Sub-sections. `addSectionParentId` is 0 rather than null so it maps straight
  // onto a <select> value (selects only ever hand back strings).
  const [addSectionParentId, setAddSectionParentId] = useState(0);
  const [confirmSubcatsOff, setConfirmSubcatsOff] = useState(false);
  const [savingSubcats, setSavingSubcats] = useState(false);
  const [addItem, setAddItem] = useState<{ sectionId: number; name: string; description: string; pricePence: number; imageUrl: string; discountType: string; discountValue: number; discountLabel: string; calories: number | null; multiBuyQty: number; multiBuyPricePence: number; isEndToEnd: boolean; isHighValue: boolean } | null>(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);

  // Stripe reporting types + state
  type StripeBal = { available: { amount: number; currency: string }[]; pending: { amount: number; currency: string }[] };
  type StripePayout = { id: string; amount: number; currency: string; arrival_date: number; created: number; status: string; description: string | null };
  type RptOrder = { id: number; customerName: string | null; subtotalPence: number; bagChargePence: number | null; deliveryFeePence: number; leg2DeliveryFeePence: number | null; customerPlatformFeePence: number; customerHubDriverFeePence: number; driverFuelSupplementPence: number | null; driverMode: string | null; fulfilmentType: string; serviceChargePence: number | null; offerDiscountPence: number | null; totalPence: number; storeGrossProceedsPence: number | null; createdAt: string; status: string };
  type RptCharge = { chargeId: string; paymentIntentId: string | null; grossPence: number; stripeProcessingFeePence: number; applicationFeePence: number; deliveryFeePence: number; platformFeePence: number; hubDriverFeePence: number; fuelSupplementPence: number; netToStorePence: number; order: RptOrder | null };
  type RptPayout = { id: string; arrivalDate: string; createdDate: string; status: string; amountPence: number; description: string | null; charges: RptCharge[]; totalGrossPence: number; totalStripeProcessingFeePence: number; totalApplicationFeePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number; totalHubDriverFeePence: number; totalFuelSupplementPence: number; totalNetToStorePence: number; unmatchedCount: number };
  type RptWeek = { periodStart: string; periodEnd: string; orders: RptOrder[]; totalFoodRevenuePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number };
  type RptData = { store: { id: number; name: string; stripeAccountId: string }; fromDate: string; toDate: string; payouts: RptPayout[]; weeks: RptWeek[]; grandTotalGrossPence: number; grandTotalStripeProcessingFeePence: number; grandTotalApplicationFeePence: number; grandTotalDeliveryFeePence: number; grandTotalPlatformFeePence: number; grandTotalHubDriverFeePence: number; grandTotalFuelSupplementPence: number; grandTotalPayoutsPence: number; grandPeriodRevenuePence: number };
  type SinglePayout = { payoutId: string; amountPence: number; arrivalDate: number; status: string; charges: RptCharge[]; totalGrossPence: number; totalStripeProcessingFeePence: number; totalApplicationFeePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number; totalHubDriverFeePence: number; totalFuelSupplementPence: number; totalNetToStorePence: number; unmatchedCount: number };
  const [stripeOverview, setStripeOverview] = useState<{ balance: StripeBal; payouts: StripePayout[] } | null>(null);
  const [stripeOverviewLoading, setStripeOverviewLoading] = useState(false);
  const [expandedRecentPayoutId, setExpandedRecentPayoutId] = useState<string | null>(null);
  const [recentPayoutDetails, setRecentPayoutDetails] = useState<Map<string, SinglePayout>>(new Map());
  const [recentPayoutLoading, setRecentPayoutLoading] = useState<string | null>(null);
  const [payoutPage, setPayoutPage] = useState(0);
  const PAYOUT_PAGE_SIZE = 10;
  const [stripeReportFrom, setStripeReportFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; });
  const [stripeReportTo, setStripeReportTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [stripeReportDownloading, setStripeReportDownloading] = useState(false);
  const [stripeReportData, setStripeReportData] = useState<RptData | null>(null);
  const [stripeReportLoading, setStripeReportLoading] = useState(false);
  const [expandedPayoutId, setExpandedPayoutId] = useState<string | null>(null);
  /* "AH/Driver Fees" was a single number covering delivery, service fee, driver
     fee and fuel supplement. A shop querying a deduction could not see which
     part it was, so each row now opens to show every line separately. */
  const [expandedReportOrderId, setExpandedReportOrderId] = useState<number | null>(null);

  const [offers, setOffers] = useState<StoreOffer[]>([]);
  const [addOffer, setAddOffer] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<number | null>(null);
  const [poolSearch, setPoolSearch] = useState("");
  const [offerSaving, setOfferSaving] = useState(false);
  const [optionNames, setOptionNames] = useState<Array<{ name: string; optionIds: number[] }>>([]);

  // Cart recovery config
  type RecoveryRule = { minBasketPence: number; maxBasketPence: number | null; offerType: string; offerValue: number | null };
  const [recoveryConfig, setRecoveryConfig] = useState<{
    enabled: boolean;
    delayMinutes: number;
    channelPush: boolean;
    channelSms: boolean;
    channelEmail: boolean;
    rules: RecoveryRule[];
  }>({ enabled: false, delayMinutes: 30, channelPush: true, channelSms: true, channelEmail: true, rules: [] });
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoverySaving, setRecoverySaving] = useState(false);
  const [recoveryStats, setRecoveryStats] = useState<{ sent: number; clicked: number; converted: number } | null>(null);
  // SMS settings
  const [smsSettings, setSmsSettings] = useState<{
    id?: number; storeId?: number; enabled: boolean;
    orderConfirmationEnabled: boolean; orderStatusUpdatesEnabled: boolean;
    marketingEnabled: boolean; smsBalancePence: number;
    merchantNotificationMode: string; customerNotificationMode: string; alertPhoneE164: string | null;
  }>({ enabled: false, orderConfirmationEnabled: true, orderStatusUpdatesEnabled: true, marketingEnabled: false, smsBalancePence: 0, merchantNotificationMode: "off", customerNotificationMode: "off", alertPhoneE164: null });
  const [smsSellPricePence, setSmsSellPricePence] = useState(10);
  const [smsMinimumTopupPence, setSmsMinimumTopupPence] = useState(2000);
  const [smsMaximumDebtPence, setSmsMaximumDebtPence] = useState(10000);
  const [smsActiveSenderId, setSmsActiveSenderId] = useState<string | null>(null);
  const [smsSenderRequest, setSmsSenderRequest] = useState("");
  const [smsSenderItems, setSmsSenderItems] = useState<Array<{ id: number; senderId: string; status: string; rejectionReason: string | null }>>([]);
  const [smsRefresh, setSmsRefresh] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsCampaigns, setSmsCampaigns] = useState<Array<{ id: number; title: string; message: string; status: string; totalSent: number; actualCostPence: number; createdAt: string }>>([]);
  const [smsCampaignsLoading, setSmsCampaignsLoading] = useState(false);
  const [newSmsTitle, setNewSmsTitle] = useState("");
  const [newSmsMsg, setNewSmsMsg] = useState("");
  const [creatingSmscamp, setCreatingSmscamp] = useState(false);
  const [sendingSmsCampId, setSendingSmsCampId] = useState<number | null>(null);

  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [offerExpiryChanged, setOfferExpiryChanged] = useState(false);
  const [sponsorWeeks, setSponsorWeeks] = useState<1 | 2 | 4 | null>(null);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [expandedItemOptions, setExpandedItemOptions] = useState<number | null>(null);
  const [mixMatchDeals, setMixMatchDeals] = useState<MixMatchDeal[]>([]);
  const [addMixMatch, setAddMixMatch] = useState(false);
  const [mmForm, setMmForm] = useState({ name: "", qty: 2, pricePence: 0 });
  const [editingMmId, setEditingMmId] = useState<number | null>(null);
  const [mmEditForm, setMmEditForm] = useState({ name: "", qty: 2, pricePence: 0 });
  const [mmAssigning, setMmAssigning] = useState<number | null>(null);
  const [driverAssignments, setDriverAssignments] = useState<Record<number, DriverAssignment | null>>({});
  const [extendEtaOrderId, setExtendEtaOrderId] = useState<number | null>(null);
  const [driverChatOrderId, setDriverChatOrderId] = useState<number | null>(null);
  const [driverChatMessages, setDriverChatMessages] = useState<Array<{ id: number; senderUserId: number; body: string; attachmentIds?: string[]; createdAt: string; senderName: string | null }>>([]);
  const [driverChatInput, setDriverChatInput] = useState("");
  const [driverChatSending, setDriverChatSending] = useState(false);
  const driverChatPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ordersPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, navigate] = useLocation();
  const [editItem, setEditItem] = useState<{ id: number; sectionId: number; name: string; description: string; pricePence: number; imageUrl: string; discountType: string; discountValue: number; discountLabel: string; calories: number | null; multiBuyQty: number; multiBuyPricePence: number; isEndToEnd: boolean; isHighValue: boolean; upliftPercent: number | null; upliftExcluded: boolean } | null>(null);
  const [editSectionId, setEditSectionId] = useState<number | null>(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
  const dragSectionId = useRef<number | null>(null);
  const dragOverSectionId = useRef<number | null>(null);
  const [sectionDragTarget, setSectionDragTarget] = useState<number | null>(null);
  const dragItemId = useRef<number | null>(null);
  const dragItemSectionId = useRef<number | null>(null);
  const dragOverItemId = useRef<number | null>(null);
  const [itemDragTarget, setItemDragTarget] = useState<number | null>(null);
  const dragAllowedRef = useRef(false);

  const load = useCallback(async () => {
    const [storeRes, ordersRes, offersRes, mmRes, sectionsRes] = await Promise.all([
      fetch(`/api/order/stores/${storeId}`, { headers: authHeaders() }),
      fetch(`/api/order/stores/${storeId}/orders`, { headers: authHeaders() }),
      fetch(`/api/order/stores/${storeId}/offers`, { headers: authHeaders() }),
      fetch(`/api/order/stores/${storeId}/mix-match`, { headers: authHeaders() }),
      fetch(`/api/order/stores/${storeId}/menu-sections`, { headers: authHeaders() }),
    ]);
    const sectionsData = await sectionsRes.json() as { items?: MenuSection[] };
    if (!sectionsRes.ok || !Array.isArray(sectionsData.items)) {
      toast({ title: "Could not load menu categories. Please try again.", variant: "destructive" });
      return;
    }
    const storeData = await storeRes.json() as { store: StoreData; sections: MenuSection[]; items: MenuItem[] };
    const ordersData = await ordersRes.json() as { items: OrderRow[] };
    const offersData = await offersRes.json() as { items: StoreOffer[] };
    const mmData = await mmRes.json() as { items: MixMatchDeal[] };
    setStore(storeData.store);
    // Management must include off-day, unavailable and upsell-only categories.
    // The public store menu remains filtered for customers.
    const newSections: MenuSection[] = sectionsData.items;
    setSections(newSections);
    // Reset items — load lazily per section to avoid fetching thousands of items upfront
    setItems([]);
    setMmAllItems([]);
    setMmItemOptions({});
    loadedSectionIdsRef.current = new Set();
    loadingSectionIdsRef.current = new Set();
    setLoadingSectionIds(new Set());
    // Start all sections collapsed so images don't load until the user expands
    setCollapsedSections(new Set(newSections.map((s) => s.id)));
    const incoming = ordersData.items ?? [];
    setOrders(incoming);
    setOffers(offersData.items ?? []);
    setMixMatchDeals(mmData.items ?? []);
    void fetchDriverAssignments(incoming);
  }, [storeId, toast]);

  const loadSectionItems = useCallback(async (sectionId: number) => {
    if (loadedSectionIdsRef.current.has(sectionId) || loadingSectionIdsRef.current.has(sectionId)) return;
    loadingSectionIdsRef.current.add(sectionId);
    setLoadingSectionIds((prev) => new Set([...prev, sectionId]));
    try {
      const r = await fetch(`/api/order/stores/${storeId}/menu-items?sectionId=${sectionId}`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as { items: MenuItem[] };
      setItems((prev) => [...prev.filter((i) => i.sectionId !== sectionId), ...d.items]);
      loadedSectionIdsRef.current.add(sectionId);
    } finally {
      loadingSectionIdsRef.current.delete(sectionId);
      setLoadingSectionIds((prev) => { const next = new Set(prev); next.delete(sectionId); return next; });
    }
  }, [storeId]);

  const mmAllItemsLoadingRef = useRef(false);
  const loadAllItemsForPickers = useCallback(async () => {
    if (mmAllItemsLoadingRef.current) return;
    mmAllItemsLoadingRef.current = true;
    setMmAllItemsLoading(true);
    setMmAllItems([]);
    setMmItemOptions({});
    try {
      const [r, rOpts, rNames] = await Promise.all([
        fetch(`/api/order/stores/${storeId}/menu-items?limit=5000`, { headers: authHeaders() }),
        fetch(`/api/order/stores/${storeId}/item-option-summary`, { headers: authHeaders() }),
        fetch(`/api/order/stores/${storeId}/option-names`, { headers: authHeaders() }),
      ]);
      if (rNames.ok) { try { setOptionNames((await rNames.json() as { items: Array<{ name: string; optionIds: number[] }> }).items ?? []); } catch { /* */ } }
      if (!r.ok) return;
      const d = await r.json() as { items: MenuItem[] };
      setMmAllItems(d.items);
      if (rOpts.ok) {
        const opts = await rOpts.json() as ItemOptionSummary;
        setMmItemOptions(opts);
      }
    } finally {
      mmAllItemsLoadingRef.current = false;
      setMmAllItemsLoading(false);
    }
  }, [storeId]);

  // Lightweight poll used when the orders tab is active — only re-fetches
  // orders and driver assignments without touching menu/offers/store settings.
  const refreshOrders = useCallback(async () => {
    try {
      try {
        const runsResponse=await fetch(`/api/external-runs/mine?storeId=${storeId}`,{headers:authHeaders()});
        const runsData=await runsResponse.json();if(!runsResponse.ok)throw Error(runsData.error??'Could not refresh delivery jobs');
        if(runStoreRef.current===storeId){setDeliveryRuns(runsData.runs??[]);setDeliveryRunsError('');}
      } catch(e){if(runStoreRef.current===storeId)setDeliveryRunsError((e as Error).message);}
      const r = await fetch(`/api/order/stores/${storeId}/orders`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as { items: OrderRow[] };
      const incoming = d.items ?? [];
      setOrders(incoming);
      void fetchDriverAssignments(incoming);
    } catch { /* silent — polling will retry next tick */ }
  }, [storeId]);

  async function fetchDriverAssignments(orderList: OrderRow[]) {
    const hubOrders = orderList.filter(o => o.driverMode === "hub" && o.fulfilmentType === "delivery");
    if (hubOrders.length === 0) return;
    const results = await Promise.allSettled(
      hubOrders.map(async (o) => {
        const r = await fetch(`/api/orders/${o.id}/driver`, { headers: authHeaders() });
        const d = await r.json() as { assignment: DriverAssignment | null };
        return { orderId: o.id, assignment: d.assignment };
      })
    );
    const updates: Record<number, DriverAssignment | null> = {};
    for (const res of results) {
      if (res.status === "fulfilled") updates[res.value.orderId] = res.value.assignment;
    }
    setDriverAssignments(prev => ({ ...prev, ...updates }));
  }

  async function toggleHubDrivers() {
    if (!store) return;
    const newVal = !store.useHubDrivers;
    const r = await fetch(`/api/order/stores/${storeId}/driver-mode`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify({ useHubDrivers: newVal }),
    });
    if (r.ok) setStore({ ...store, useHubDrivers: newVal });
  }

  async function cancelDriverAssignment(orderId: number) {
    if (!confirm("Cancel the current driver assignment and re-broadcast this order to available drivers?")) return;
    const r = await fetch(`/api/orders/${orderId}/driver-assignment`, { method: "DELETE", headers: authHeaders() });
    if (!r.ok) {
      const d = await r.json() as { error?: string };
      toast({ title: "Error", description: d.error ?? "Could not cancel driver assignment", variant: "destructive" });
      return;
    }
    setDriverAssignments(prev => ({ ...prev, [orderId]: null }));
    void load();
  }

  async function openDriverChat(orderId: number) {
    setDriverChatOrderId(orderId);
    setDriverChatInput("");
    await loadDriverMessages(orderId);
    if (driverChatPollRef.current) clearInterval(driverChatPollRef.current);
    driverChatPollRef.current = setInterval(() => void loadDriverMessages(orderId), 5000);
  }

  function closeDriverChat() {
    if (driverChatPollRef.current) { clearInterval(driverChatPollRef.current); driverChatPollRef.current = null; }
    setDriverChatOrderId(null);
    setDriverChatMessages([]);
  }

  async function loadDriverMessages(orderId: number) {
    const r = await fetch(`/api/orders/${orderId}/driver-messages`, { headers: authHeaders() });
    if (!r.ok) return;
    const d = await r.json() as { items: Array<{ id: number; senderUserId: number; body: string; attachmentIds?: string[]; createdAt: string; senderName: string | null }> };
    setDriverChatMessages(d.items ?? []);
  }

  async function sendDriverMessage() {
    if (!driverChatOrderId || !driverChatInput.trim() || driverChatSending) return;
    const assignment = driverAssignments[driverChatOrderId];
    if (!assignment) return;
    setDriverChatSending(true);
    try {
      await fetch(`/api/orders/${driverChatOrderId}/driver-messages`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ body: driverChatInput.trim(), recipientUserId: assignment.driverId }),
      });
      setDriverChatInput("");
      await loadDriverMessages(driverChatOrderId);
    } finally { setDriverChatSending(false); }
  }

  useEffect(() => { void load(); }, [load]);

  const loadAnalytics = useCallback(async (days: number) => {
    setAnalyticsLoading(true);
    try {
      const r = await fetch(`/api/order/stores/${storeId}/analytics?days=${days}`, { headers: authHeaders() });
      const d = await r.json() as AnalyticsData;
      setAnalytics(d);
    } finally { setAnalyticsLoading(false); }
  }, [storeId]);

  useEffect(() => {
    if (tab === "analytics") void loadAnalytics(analyticsDays);
  }, [tab, analyticsDays, loadAnalytics]);

  useEffect(() => {
    if (tab === "staff") void loadStaff();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, storeId]);

  useEffect(() => {
    if (tab !== "offers" || !storeId) return;
    setRecoveryLoading(true);
    void (async () => {
      try {
        type Cfg = { enabled?: boolean; delayMinutes?: number; channelPush?: boolean; channelSms?: boolean; channelEmail?: boolean } | null;
        const r = await fetch(`/api/order/stores/${storeId}/recovery-config`, { headers: authHeaders() });
        const d = await r.json() as { config: Cfg; rules?: RecoveryRule[] };
        const cfg = d.config ?? {};
        setRecoveryConfig({
          enabled: cfg.enabled ?? false,
          delayMinutes: cfg.delayMinutes ?? 30,
          channelPush: cfg.channelPush ?? true,
          channelSms: cfg.channelSms ?? true,
          channelEmail: cfg.channelEmail ?? true,
          rules: (d.rules ?? []).map((rule) => ({
            minBasketPence: rule.minBasketPence,
            maxBasketPence: rule.maxBasketPence ?? null,
            offerType: rule.offerType,
            offerValue: rule.offerValue ?? null,
          })),
        });
      } catch { /* ignore */ } finally { setRecoveryLoading(false); }
    })();
  }, [tab, storeId]);

  useEffect(() => {
    if (tab !== "sms" || !storeId) return;
    setSmsLoading(true);
    setSmsCampaignsLoading(true);
    void (async () => {
      try {
        const [settingsRes, campsRes, senderRes] = await Promise.all([
          fetch(`/api/order/stores/${storeId}/sms-settings`, { headers: authHeaders() }),
          fetch(`/api/order/stores/${storeId}/sms-campaigns`, { headers: authHeaders() }),
          fetch(`/api/order/stores/${storeId}/sms-sender-ids`, { headers: authHeaders() }),
        ]);
        if (settingsRes.ok) {
          const d = await settingsRes.json() as { settings: typeof smsSettings; sellPerSmsPence: number; minimumTopupPence: number; maximumDebtPence: number; activeSenderId: string | null };
          setSmsSettings(d.settings);
          setSmsSellPricePence(d.sellPerSmsPence ?? 10);
          setSmsMinimumTopupPence(d.minimumTopupPence ?? 2000);
          setSmsMaximumDebtPence(d.maximumDebtPence ?? 10000);
          setSmsActiveSenderId(d.activeSenderId ?? null);
        }
        if (campsRes.ok) {
          const d = await campsRes.json() as { items: typeof smsCampaigns };
          setSmsCampaigns(d.items ?? []);
        }
        if (senderRes.ok) { const d = await senderRes.json() as { items: typeof smsSenderItems }; setSmsSenderItems(d.items ?? []); }
      } catch { /* ignore */ } finally { setSmsLoading(false); setSmsCampaignsLoading(false); }
    })();
  }, [tab, storeId, smsRefresh]);

  useEffect(() => {
    if (tab !== "abandoned" || !storeId) return;
    setAbandonedLoading(true);
    fetch(`/api/order/stores/${storeId}/abandoned-baskets`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d: { baskets: typeof abandonedBaskets }) => setAbandonedBaskets(d.baskets ?? []))
      .catch(() => {})
      .finally(() => setAbandonedLoading(false));
  }, [tab, storeId]);

  // Stripe live overview — loads when Payments tab is opened
  useEffect(() => {
    if (tab !== "stripe" || !storeId || !store?.stripeOnboardingComplete) return;
    setStripeOverviewLoading(true);
    setPayoutPage(0);
    fetch(`/api/order/stores/${storeId}/stripe/overview`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d ? setStripeOverview(d) : null)
      .catch(() => {})
      .finally(() => setStripeOverviewLoading(false));
  }, [tab, storeId, store?.stripeOnboardingComplete]);

  async function loadRecentPayoutDetail(payoutId: string) {
    if (!storeId) return;
    if (recentPayoutDetails.has(payoutId)) return; // already cached
    setRecentPayoutLoading(payoutId);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/stripe/payout/${payoutId}`, { headers: authHeaders() });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? `Error ${res.status}`); }
      const data = await res.json() as SinglePayout;
      setRecentPayoutDetails((prev) => new Map(prev).set(payoutId, data));
    } catch (e: unknown) {
      toast({ title: (e instanceof Error ? e.message : null) ?? "Failed to load payout detail", variant: "destructive" });
    } finally {
      setRecentPayoutLoading(null);
    }
  }

  async function downloadStripeReport() {
    if (!storeId) return;
    setStripeReportDownloading(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch(`/api/order/stores/${storeId}/stripe/reconciliation-report?from=${stripeReportFrom}&to=${stripeReportTo}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { const body = await res.json() as { error?: string }; throw new Error(body.error ?? `Error ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reconciliation-${store?.name.replace(/\s+/g, "-").toLowerCase() ?? storeId}-${stripeReportFrom}-to-${stripeReportTo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      toast({ title: (e instanceof Error ? e.message : null) ?? "Failed to generate report", variant: "destructive" });
    } finally {
      setStripeReportDownloading(false);
    }
  }

  async function loadStripeReport() {
    if (!storeId || !stripeReportFrom || !stripeReportTo) return;
    setStripeReportLoading(true);
    setStripeReportData(null);
    setExpandedPayoutId(null);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/stripe/reporting?from=${stripeReportFrom}&to=${stripeReportTo}`, { headers: authHeaders() });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? `Error ${res.status}`); }
      setStripeReportData(await res.json() as RptData);
    } catch (e: unknown) {
      toast({ title: (e instanceof Error ? e.message : null) ?? "Failed to load report", variant: "destructive" });
    } finally {
      setStripeReportLoading(false);
    }
  }

  // Live polling for orders and driver assignment status when the orders tab is open.
  useEffect(() => {
    if (tab !== "orders") {
      if (ordersPollRef.current) { clearInterval(ordersPollRef.current); ordersPollRef.current = null; }
      return;
    }
    void refreshOrders();
    ordersPollRef.current = setInterval(() => void refreshOrders(), 8000);
    return () => {
      if (ordersPollRef.current) { clearInterval(ordersPollRef.current); ordersPollRef.current = null; }
    };
  }, [tab, refreshOrders]);

  // Check Stripe / subscription callback
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("stripe_connected")) toast({ title: "Stripe account connected!" });
    if (p.get("stripe_pending")) toast({ title: "Stripe setup incomplete", description: "Please complete Stripe onboarding — click Connect Stripe to continue.", variant: "destructive" });
    if (p.get("stripe_refresh")) toast({ title: "Stripe link expired", description: "Click Connect Stripe to get a fresh link." });
    if (p.get("stripe_error")) toast({ title: "Stripe connection failed", description: p.get("stripe_error") ?? undefined, variant: "destructive" });
    const subSession=p.get('sub_session_id'),sponsorSession=p.get('sponsor_session_id');
    const session=subSession??sponsorSession;
    if(session){const kind=subSession?'subscription':'sponsor';void fetch(`/api/order/stores/${storeId}/${kind}/verify`,{method:'POST',headers:authHeaders(),body:JSON.stringify({sessionId:session})}).then(async response=>{const result=await response.json();if(!response.ok)throw Error(result.error??'Payment confirmation remains pending');sessionStorage.removeItem(`store-billing:${kind}:${storeId}`);toast({title:kind==='subscription'?'Subscription confirmed':'Sponsorship confirmed'});void load();}).catch(()=>toast({title:'Payment confirmation is pending',description:'Your original checkout is being reconciled. Do not pay again.',variant:'destructive'}));}

  }, [toast, storeId]);

  async function createMixMatchDeal() {
    if (!mmForm.name.trim() || !mmForm.qty || !mmForm.pricePence) return;
    const res = await fetch(`/api/order/stores/${storeId}/mix-match`, { method: "POST", headers: authHeaders(), body: JSON.stringify(mmForm) });
    const deal = await res.json() as Omit<MixMatchDeal, "itemIds">;
    setMixMatchDeals((prev) => [...prev, { ...deal, itemIds: [] }]);
    setMmForm({ name: "", qty: 2, pricePence: 0 });
    setAddMixMatch(false);
  }

  async function deleteMixMatchDeal(dealId: number) {
    await fetch(`/api/order/stores/${storeId}/mix-match/${dealId}`, { method: "DELETE", headers: authHeaders() });
    setMixMatchDeals((prev) => prev.filter((d) => d.id !== dealId));
  }

  async function saveMixMatchDeal(dealId: number) {
    if (!mmEditForm.name.trim() || !mmEditForm.qty || !mmEditForm.pricePence) return;
    const res = await fetch(`/api/order/stores/${storeId}/mix-match/${dealId}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ name: mmEditForm.name.trim(), qty: mmEditForm.qty, pricePence: mmEditForm.pricePence }),
    });
    if (!res.ok) return;
    const updated = await res.json() as MixMatchDeal;
    setMixMatchDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, ...updated } : d));
    setEditingMmId(null);
  }

  async function addItemToMixMatch(dealId: number, menuItemId: number) {
    await fetch(`/api/order/stores/${storeId}/mix-match/${dealId}/items`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ menuItemId }) });
    setMixMatchDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, itemIds: [...d.itemIds, menuItemId] } : d));
  }

  async function removeItemFromMixMatch(dealId: number, menuItemId: number) {
    await fetch(`/api/order/stores/${storeId}/mix-match/${dealId}/items/${menuItemId}`, { method: "DELETE", headers: authHeaders() });
    setMixMatchDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, itemIds: d.itemIds.filter((id) => id !== menuItemId) } : d));
  }

  async function saveStore(d: Partial<StoreData>) {
    const res = await fetch(`/api/order/stores/${storeId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(d) });
    const updated = await res.json() as StoreData;
    setStore(updated); setEditing(false);
    toast({ title: "Store updated" });
  }

  async function updateSection() {
    if (!editSectionId || !editSectionName.trim()) return;
    const res = await fetch(`/api/order/stores/${storeId}/menu-sections/${editSectionId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ name: editSectionName }) });
    const updated = await res.json() as MenuSection;
    setSections((prev) => prev.map((s) => s.id === updated.id ? updated : s));
    setEditSectionId(null);
    setEditSectionName("");
  }

  // Admin-only: set a category's uplift override (null percent = inherit shop; excluded = 0%).
  // Upsell-only category: hide all its items from the normal menu and offer them
  // as checkout upsells ("Fancy any of these?") at their normal price.
  async function setSectionUpsellOnly(sectionId: number, upsellOnly: boolean) {
    const res = await fetch(`/api/order/stores/${storeId}/menu-sections/${sectionId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ upsellOnly }) });
    if (!res.ok) { toast({ title: "Could not update category", variant: "destructive" }); return; }
    setSections((prev) => prev.map((s) => s.id === sectionId ? { ...s, upsellOnly } : s));
  }

  async function saveSectionUplift(sectionId: number, upliftPercent: number | null, upliftExcluded: boolean) {
    const res = await fetch(`/api/order/stores/${storeId}/menu-sections/${sectionId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ upliftPercent: upliftExcluded ? null : upliftPercent, upliftExcluded }) });
    if (!res.ok) return;
    const updated = await res.json() as MenuSection;
    setSections((prev) => prev.map((s) => s.id === updated.id ? updated : s));
  }

  async function updateItem() {
    if (!editItem) return;
    const body = {
      name: editItem.name,
      sectionId: editItem.sectionId,
      description: editItem.description || undefined,
      pricePence: editItem.pricePence,
      imageUrl: editItem.imageUrl || null,
      discountType: editItem.discountType || null,
      discountValue: editItem.discountValue || null,
      discountLabel: editItem.discountLabel || null,
      calories: editItem.calories ?? null,
      multiBuyQty: editItem.multiBuyQty || null,
      multiBuyPricePence: editItem.multiBuyPricePence || null,
      isEndToEnd: editItem.isEndToEnd,
      isHighValue: editItem.isHighValue,
      upliftPercent: editItem.upliftExcluded ? null : editItem.upliftPercent,
      upliftExcluded: editItem.upliftExcluded,
    };
    const res = await fetch(`/api/order/stores/${storeId}/menu-items/${editItem.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
    const updated = await res.json() as MenuItem;
    setItems((prev) => prev.map((i) => i.id === updated.id ? updated : i));
    setEditItem(null);
    toast({ title: "Item updated" });
  }

  /* ── Sub-sections ───────────────────────────────────────────────────────────
     The customer menu drills exactly ONE level — it shows a grid of tiles for a
     section's children, then that child's items (OrderStoreDetail.tsx ~2640,
     mobile order-store.tsx). A grandchild would therefore never be reachable, so
     every rule below exists to keep the tree exactly one level deep. Wording in
     the UI avoids "parent"/"child" — shopkeepers get "heading" and "sits under". */

  const subcategoriesEnabled = store?.subcategoriesEnabled === true;

  /** The heading a section is shown beneath, or null if it stands on its own.
   *  Returns null for a parent that has been deleted or is itself a sub-section,
   *  so a broken link degrades to "top level" rather than hiding the section. */
  function parentOf(s: MenuSection): MenuSection | null {
    if (!subcategoriesEnabled || s.parentSectionId == null || s.parentSectionId === s.id) return null;
    const p = sections.find((x) => x.id === s.parentSectionId);
    return p && p.parentSectionId == null ? p : null;
  }

  function childrenOf(parentId: number) {
    return sections.filter((s) => s.id !== parentId && parentOf(s)?.id === parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /** Sections that may be offered as a heading: top level only (one level deep)
   *  and never the section doing the choosing. Pass 0 when nothing is excluded. */
  function eligibleParents(forSectionId: number) {
    return sections.filter((s) => s.id !== forSectionId && s.parentSectionId == null).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /** Display order: each top-level section immediately followed by its own
   *  sub-sections, so the shape of the menu is visible without expanding
   *  anything. Anything with a dangling link is listed at top level, never lost. */
  function orderedSections() {
    const byOrder = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);
    if (!subcategoriesEnabled) return byOrder;
    const out: MenuSection[] = [];
    for (const s of byOrder) {
      if (parentOf(s)) continue;
      out.push(s, ...byOrder.filter((c) => c.id !== s.id && parentOf(c)?.id === s.id));
    }
    // Safety net: emit anything the grouping missed. A section nested two deep
    // — which the UI prevents but an import could still create — has a parent
    // that is itself a child, so neither loop above would reach it and it would
    // silently disappear from the shop's own menu editor. Better to show it at
    // the end, where they can see it and move it, than to hide it.
    const emitted = new Set(out.map((s) => s.id));
    for (const s of byOrder) if (!emitted.has(s.id)) out.push(s);
    return out;
  }

  async function toggleSubcategories(next: boolean) {
    setSavingSubcats(true);
    try {
      const res = await fetch(`/api/order/stores/${storeId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ subcategoriesEnabled: next }) });
      if (!res.ok) { toast({ title: "Could not save that setting", variant: "destructive" }); return; }
      // Full refetch: the mode changes how every section is grouped and ordered,
      // so re-reading the server's copy is safer than patching state by hand.
      await load();
      setConfirmSubcatsOff(false);
      toast({ title: next ? "Sub-sections turned on" : "Sub-sections turned off" });
    } finally { setSavingSubcats(false); }
  }

  async function setSectionParent(sectionId: number, parentSectionId: number | null) {
    const res = await fetch(`/api/order/stores/${storeId}/menu-sections/${sectionId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ parentSectionId }) });
    if (!res.ok) { toast({ title: "Could not move that section", variant: "destructive" }); return; }
    const updated = await res.json() as MenuSection;
    setSections((prev) => prev.map((s) => s.id === updated.id ? updated : s));
    const heading = parentSectionId == null ? null : sections.find((s) => s.id === parentSectionId);
    toast({ title: heading ? `"${updated.name}" now sits under "${heading.name}"` : `"${updated.name}" is now a section of its own` });
  }

  async function addSection() {
    if (!addSectionName.trim()) return;
    // Re-check the chosen heading here as well as in the picker: it is still top
    // level only, so a stale selection can never create a second level.
    const heading = subcategoriesEnabled ? sections.find((s) => s.id === addSectionParentId && s.parentSectionId == null) : undefined;
    const res = await fetch(`/api/order/stores/${storeId}/menu-sections`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ name: addSectionName, parentSectionId: heading?.id ?? null }) });
    const s = await res.json() as MenuSection;
    setSections((prev) => [...prev, s]);
    setAddSectionName("");
    setAddSectionParentId(0);
  }

  async function deleteSection(id: number) {
    // Deleting a heading would leave its sub-sections pointing at a dead row. They
    // would still render (parentOf() falls back to top level) but the stale link
    // would quietly reattach them if a new section reused the id, so clear it.
    const orphans = childrenOf(id);
    if (orphans.length && !confirm(`Delete "${sections.find((s) => s.id === id)?.name}"? Its ${orphans.length} sub-section${orphans.length !== 1 ? "s" : ""} will not be deleted — they become sections of their own.`)) return;
    await fetch(`/api/order/stores/${storeId}/menu-sections/${id}`, { method: "DELETE", headers: authHeaders() });
    await Promise.all(orphans.map((c) =>
      fetch(`/api/order/stores/${storeId}/menu-sections/${c.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ parentSectionId: null }) })
    ));
    setSections((prev) => prev.filter((s) => s.id !== id).map((s) => orphans.some((o) => o.id === s.id) ? { ...s, parentSectionId: null } : s));
    setItems((prev) => prev.filter((i) => i.sectionId !== id));
  }

  async function moveSectionOrder(id: number, direction: "up" | "down") {
    const sorted = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((s) => s.id === id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    const [aOrder, bOrder] = [a.sortOrder, b.sortOrder];
    await Promise.all([
      fetch(`/api/order/stores/${storeId}/menu-sections/${a.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ sortOrder: bOrder }) }),
      fetch(`/api/order/stores/${storeId}/menu-sections/${b.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ sortOrder: aOrder }) }),
    ]);
    setSections((prev) => prev.map((s) => s.id === a.id ? { ...s, sortOrder: bOrder } : s.id === b.id ? { ...s, sortOrder: aOrder } : s));
  }

  /** True when dropping the section currently being dragged onto `targetId` would
   *  do something. With sub-sections on the list is a tree, so a drag may only
   *  reorder within one group — dragging across groups would look like it
   *  re-homes the section, which is what the "Sits under" picker is for. */
  function canDropSectionOn(targetId: number) {
    const fromId = dragSectionId.current;
    if (fromId === null || fromId === targetId) return false;
    if (!subcategoriesEnabled) return true;
    const a = sections.find((s) => s.id === fromId);
    const b = sections.find((s) => s.id === targetId);
    return !!a && !!b && (parentOf(a)?.id ?? null) === (parentOf(b)?.id ?? null);
  }

  async function reorderSections(fromId: number, toId: number) {
    const from = sections.find((s) => s.id === fromId);
    if (!from) return;
    // Renumber only the group the dragged section belongs to. Flat mode has a
    // single group (every section), so this is the original behaviour untouched.
    const groupParentId = parentOf(from)?.id ?? null;
    const sorted = sections
      .filter((s) => !subcategoriesEnabled || (parentOf(s)?.id ?? null) === groupParentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const fromIdx = sorted.findIndex((s) => s.id === fromId);
    const toIdx = sorted.findIndex((s) => s.id === toId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const updates = reordered.map((s, i) => ({ ...s, sortOrder: i * 10 }));
    // Merge rather than replace — sections outside this group keep their order.
    setSections((prev) => prev.map((s) => updates.find((u) => u.id === s.id) ?? s));
    await Promise.all(updates.map((s) =>
      fetch(`/api/order/stores/${storeId}/menu-sections/${s.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ sortOrder: s.sortOrder }) })
    ));
  }

  async function reorderItems(fromId: number, toId: number, sectionId: number) {
    const sectionItems = [...items].filter((i) => i.sectionId === sectionId).sort((a, b) => a.sortOrder - b.sortOrder);
    const fromIdx = sectionItems.findIndex((i) => i.id === fromId);
    const toIdx = sectionItems.findIndex((i) => i.id === toId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...sectionItems];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const updates = reordered.map((item, i) => ({ ...item, sortOrder: i * 10 }));
    setItems((prev) => prev.map((i) => updates.find((u) => u.id === i.id) ?? i));
    await Promise.all(updates.map((item) =>
      fetch(`/api/order/stores/${storeId}/menu-items/${item.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ sortOrder: item.sortOrder }) })
    ));
  }

  async function createItem() {
    if (!addItem) return;
    const payload = {
      ...addItem,
      discountType: addItem.discountType || undefined,
      discountValue: addItem.discountValue || undefined,
      discountLabel: addItem.discountLabel || undefined,
      calories: addItem.calories ?? undefined,
      multiBuyQty: addItem.multiBuyQty || undefined,
      multiBuyPricePence: addItem.multiBuyPricePence || undefined,
    };
    const res = await fetch(`/api/order/stores/${storeId}/menu-items`, { method: "POST", headers: authHeaders(), body: JSON.stringify(payload) });
    const item = await res.json() as MenuItem;
    setItems((prev) => [...prev, item]);
    setAddItem(null);
    toast({ title: "Item added" });
  }

  function startAddOffer() {
    setOfferExpiryChanged(false);
    setEditingOfferId(null);
    setOfferForm(emptyOfferForm());
    setAddOffer(true);
    void loadAllItemsForPickers();
  }

  function startEditOffer(offer: StoreOffer) {
    setOfferExpiryChanged(false);
    setEditingOfferId(offer.id);
    setOfferForm({
      title: offer.title,
      description: offer.description ?? "",
      type: offer.type,
      value: offer.value,
      minOrderPence: offer.minOrderPence,
      freeItemMenuItemId: offer.freeItemMenuItemId ?? 0,
      freeItemMode: offer.freeItemMode ?? "single",
      freeItemCategoryId: offer.freeItemCategoryId ?? 0,
      freeItemPoolItemIds: offer.freeItemPoolItemIds ?? [],
      freeItemPickCount: offer.freeItemPickCount ?? 1,
      freeItemOptionVariant: offer.freeItemOptionVariant ?? "",
      code: offer.code ?? "",
      validUntil: offerExpiryFormDate(offer.validUntil),
      excludedItemIds: offer.excludedItemIds ?? [],
      excludedSectionIds: offer.excludedSectionIds ?? [],
      excludedOptionIds: offer.excludedOptionIds ?? [],
      activeDays: offer.activeDays ?? [],
      includedItemIds: offer.includedItemIds ?? [],
      includedSectionIds: offer.includedSectionIds ?? [],
      bogofBuyQty: (offer as any).bogofBuyQty ?? 2,
      bogofFreeQty: (offer as any).bogofFreeQty ?? 1,
      bogofStack: (offer as any).bogofStack !== false,
      imageUrl: (offer as any).imageUrl ?? "",
      carouselFeatured: (offer as any).carouselFeatured ?? false,
      carouselRank: (offer as any).carouselRank ?? 0,
    });
    setAddOffer(true);
    void loadAllItemsForPickers();
  }

  function closeOfferForm() {
    setOfferExpiryChanged(false);
    setAddOffer(false);
    setEditingOfferId(null);
    setOfferForm(emptyOfferForm());
    setPoolSearch("");
  }

  async function saveOffer() {
    const isFreeItem = offerForm.type === "free_item";
    const payload = {
      ...offerForm,
      description: offerForm.description.trim() || null,
      value: isFreeItem ? 0 : offerForm.value,
      minOrderPence: offerForm.minOrderPence,
      freeItemMenuItemId: (isFreeItem && offerForm.freeItemMode === "single") ? offerForm.freeItemMenuItemId : null,
      freeItemMode: isFreeItem ? offerForm.freeItemMode : "single",
      freeItemCategoryId: (isFreeItem && offerForm.freeItemMode === "category") ? offerForm.freeItemCategoryId : null,
      freeItemPoolItemIds: (isFreeItem && offerForm.freeItemMode === "pool") ? offerForm.freeItemPoolItemIds : [],
      freeItemPickCount: isFreeItem ? Math.max(1, offerForm.freeItemPickCount) : 1,
      freeItemOptionVariant: (isFreeItem && offerForm.freeItemMode === "single" && offerForm.freeItemOptionVariant) ? offerForm.freeItemOptionVariant : null,
      excludedItemIds: ["percentage", "fixed"].includes(offerForm.type) ? offerForm.excludedItemIds : [],
      excludedSectionIds: ["percentage", "fixed"].includes(offerForm.type) ? offerForm.excludedSectionIds : [],
      excludedOptionIds: ["percentage", "fixed", "bogof"].includes(offerForm.type) ? offerForm.excludedOptionIds : [],
      code: offerForm.code.trim() || null,
      validUntil: offerExpiryFormValue(offers.find(offer => offer.id === editingOfferId)?.validUntil, offerForm.validUntil, offerExpiryChanged, editingOfferId !== null),
      activeDays: offerForm.activeDays,
      includedItemIds: offerForm.includedItemIds,
      includedSectionIds: offerForm.includedSectionIds,
      ...(offerForm.type === "bogof" ? {
        bogofBuyQty: offerForm.bogofBuyQty,
        bogofFreeQty: offerForm.bogofFreeQty,
        bogofStack: offerForm.bogofStack,
      } : {}),
    };
    setOfferSaving(true);
    try {
      const res = await fetch(editingOfferId ? `/api/order/offers/${editingOfferId}` : `/api/order/stores/${storeId}/offers`, {
        method: editingOfferId ? "PATCH" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json() as StoreOffer & { error?: string };
      if (!res.ok) {
        toast({ title: "Could not save offer", description: data.error ?? "Please try again", variant: "destructive" });
        return;
      }
      setOffers((prev) => editingOfferId ? prev.map((offer) => offer.id === data.id ? data : offer) : [...prev, data]);
      closeOfferForm();
      toast({ title: editingOfferId ? "Offer updated" : "Offer created" });
    } catch {
      toast({ title: "Could not save offer", description: "Please check your connection and try again", variant: "destructive" });
    } finally {
      setOfferSaving(false);
    }
  }

  async function deleteOffer(id: number) {
    await fetch(`/api/order/offers/${id}`, { method: "DELETE", headers: authHeaders() });
    setOffers((prev) => prev.filter((o) => o.id !== id));
  }

  async function toggleOffer(offer: StoreOffer) {
    const res = await fetch(`/api/order/offers/${offer.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ isActive: !offer.isActive }) });
    const updated = await res.json() as StoreOffer;
    setOffers((prev) => prev.map((o) => (o.id === offer.id ? updated : o)));
  }

  async function sponsorStore(weeks: 1 | 2 | 4) {
    setSponsorLoading(true);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/sponsor`, {
        method: "POST",
        headers: authHeaders(),
        body: await withCheckoutAttempt({ weeks, successUrl: `${window.location.origin}/business/order-store/${storeId}?sponsored=1`, cancelUrl: window.location.href },`store-billing:sponsor:${storeId}`,sessionStorage,()=>crypto.randomUUID()),
      });
      const d = await res.json() as { url?: string; free?: boolean; store?: StoreData; error?: string; checkoutAttemptClosed?: boolean };
      if (d.checkoutAttemptClosed) sessionStorage.removeItem(`store-billing:sponsor:${storeId}`);
      if (!res.ok) throw Error(d.error ?? "Could not start sponsorship");
      if (d.free && d.store) {
        setStore(d.store);
        toast({ title: `Store featured for ${weeks} week${weeks > 1 ? "s" : ""}!` });
      } else if (d.url) {
        window.location.href = d.url;
      }
    } catch (error) {
      toast({title:error instanceof Error?error.message:"Could not start sponsorship",variant:"destructive"});
    } finally { setSponsorLoading(false); setSponsorWeeks(null); }
  }

  async function deleteItem(itemId: number) {
    await fetch(`/api/order/stores/${storeId}/menu-items/${itemId}`, { method: "DELETE", headers: authHeaders() });
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  }

  async function toggleAvailable(item: MenuItem) {
    const res = await fetch(`/api/order/stores/${storeId}/menu-items/${item.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ available: !item.available }) });
    const updated = await res.json() as MenuItem;
    setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
  }

  async function connectStripe() {
    setStripeConnectLoading(true);
    try {
      const res = await fetch("/api/order/stripe-connect/account-link", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ storeId, returnBase: window.location.origin }),
      });
      const d = await res.json() as { url?: string; error?: string };
      if (d.url) { window.location.href = d.url; }
      else toast({ title: d.error ?? "Failed to start Stripe setup", variant: "destructive" });
    } finally { setStripeConnectLoading(false); }
  }

  async function startSubscription() {
    setSubscribeLoading(true);
    try {
      const successUrl = `${window.location.origin}/business/order-store/${storeId}`;
      const cancelUrl = window.location.href;
      const res = await fetch(`/api/order/stores/${storeId}/subscription/checkout`, {
        method: "POST",
        headers: authHeaders(),
        body: await withCheckoutAttempt({successUrl,cancelUrl},`store-billing:subscription:${storeId}`,sessionStorage,()=>crypto.randomUUID()),
      });
      const d = await res.json() as { url?: string; free?: boolean; error?: string; checkoutAttemptClosed?: boolean };
      if (d.checkoutAttemptClosed) sessionStorage.removeItem(`store-billing:subscription:${storeId}`);
      if (!res.ok) throw Error(d.error ?? "Could not start subscription");
      if (d.free) {
        // Fee is £0 — store activated directly, reload to reflect new status
        toast({ title: "Your store is now live!" });
        setStore((s) => s ? { ...s, isActive: true, subscriptionStatus: "active" } : s);
      } else if (d.url) {
        window.location.href = d.url;
      } else {
        toast({ title: d.error ?? "Failed to start subscription", variant: "destructive" });
      }
    } catch (error) {
      toast({title:error instanceof Error?error.message:"Could not start subscription",variant:"destructive"});
    } finally { setSubscribeLoading(false); }
  }

  async function updateOrderStatus(orderId: number, status: string) {
    const res = await fetch(`/api/order/orders/${orderId}/status`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status }) });
    const data = await res.json() as OrderRow & { error?: string };
    if (!res.ok) throw new Error(data.error ?? "Failed to update order status");
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: data.status } : o)));
  }

  async function extendEta(orderId: number, extraMins: number) {
    const res = await fetch(`/api/order/orders/${orderId}/eta`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ extraMins }) });
    const data = await res.json() as OrderRow & { error?: string };
    if (!res.ok) throw new Error(data.error ?? "Failed to extend ETA");
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, estimatedReadyAt: data.estimatedReadyAt } : o)));
    setExtendEtaOrderId(null);
  }

  if (!store) return <div className="animate-pulse h-32 bg-slate-100 rounded-2xl" />;

  const tabs = [
    { id: "menu", label: "Menu", shortLabel: "Menu", icon: LayoutList },
    { id: "orders", label: `Orders (${orders.length})`, shortLabel: `Orders`, icon: Package },
    { id: "drivers", label: "Drivers & ledger", shortLabel: "Drivers", icon: Truck },
    { id: "analytics", label: "Analytics", shortLabel: "Stats", icon: BarChart2 },
    { id: "offers", label: "Offers", shortLabel: "Offers", icon: Tag },
    { id: "library", label: "Option Library", shortLabel: "Library", icon: Library },
    { id: "abandoned", label: `Abandoned Baskets${abandonedBaskets.length ? ` (${abandonedBaskets.length})` : ""}`, shortLabel: `Baskets${abandonedBaskets.length ? ` (${abandonedBaskets.length})` : ""}`, icon: ShoppingCart },
    { id: "settings", label: "Settings", shortLabel: "Settings", icon: Pencil },
    { id: "staff", label: "Staff", shortLabel: "Staff", icon: Users },
    { id: "stripe", label: "Finance", shortLabel: "Finance", icon: Banknote },
    { id: "sms", label: "SMS", shortLabel: "SMS", icon: MessageSquare },
  ] as const;

  async function loadStaff() {
    if (!storeId) return;
    setStaffLoading(true);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/staff`, { headers: authHeaders() });
      const d = await res.json() as { items: typeof staffList };
      setStaffList(d.items ?? []);
    } finally { setStaffLoading(false); }
  }

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!storeId || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/staff/invite`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (!res.ok) { const d = await res.json() as { error?: string }; toast({ title: d.error ?? "Failed to send invite", variant: "destructive" }); return; }
      setInviteEmail("");
      toast({ title: "Invite sent", description: `${inviteEmail.trim()} has been invited as ${inviteRole}.` });
      void loadStaff();
    } finally { setInviting(false); }
  }

  async function removeStaff(staffId: number) {
    if (!storeId || !confirm("Remove this staff member?")) return;
    await fetch(`/api/order/stores/${storeId}/staff/${staffId}`, { method: "DELETE", headers: authHeaders() });
    void loadStaff();
  }

  async function changeStaffRole(staffId: number, role: "staff" | "manager") {
    if (!storeId) return;
    await fetch(`/api/order/stores/${storeId}/staff/${staffId}`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    });
    void loadStaff();
  }

  return (
    <div className="space-y-6">
      <CashReturnShopPrompt key={store.id} storeId={store.id}/>
      {/* Store header */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-center gap-4">
          {store.imageUrl && <img src={store.imageUrl} alt={store.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-bold text-foreground text-lg">{store.name}</h2>
              <Badge className={store.approved ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"}>
                {store.approved ? "Approved" : "Pending approval"}
              </Badge>
              <Badge className={store.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                {store.isActive ? "Live" : "Inactive"}
              </Badge>
            </div>
            <p className="text-sm text-slate-500">{(store.categories?.length ? store.categories : [store.category]).join(" · ")} · {store.town} · {store.address}</p>
          </div>
          <div className="flex flex-col gap-2 items-end flex-shrink-0">
            {store.slug && (
              <Link href={`/order/${store.slug}`}>
                <Button variant="outline" size="sm" className="gap-1"><ChevronRight size={14} /> View store</Button>
              </Link>
            )}
            <Link href={`/business/order-pad${storeId ? `?storeId=${storeId}` : ""}`}>
              <Button variant="outline" size="sm" className="gap-1 border-primary/30 text-primary hover:bg-primary/5">
                <Package size={14} /> Order Pad
              </Button>
            </Link>
            <Link href={`/business/finance/${store.id}`}>
              <Button variant="outline" size="sm" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <TrendingUp size={14} /> Finance Centre
                <span className="ml-1 text-[9px] font-bold uppercase bg-amber-100 text-amber-800 rounded px-1 py-0.5">Beta</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Stripe connect banner — free stores or stores where fee not yet loaded (owner only) */}
        {staffRole === "owner" && store.approved && (store.weeklyFeePence === 0 || store.weeklyFeePence == null) && !store.stripeOnboardingComplete && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-primary text-sm">🎉 Your store is approved and ready to go live!</p>
                <p className="text-xs text-slate-600 mt-0.5">Register now to accept online payments through the existing Stripe setup. Your agreed menu price entitlement is protected, subject to separately agreed adjustments.</p>
              </div>
              <Button
                size="sm"
                className="bg-[#635bff] hover:bg-[#5851e0] text-white gap-2 font-semibold shrink-0"
                disabled={stripeConnectLoading}
                onClick={() => void connectStripe()}
              >
                <Link2 size={14} /> {stripeConnectLoading ? "Redirecting…" : "Register Now"}
              </Button>
            </div>
          </div>
        )}

        {/* Subscription payment banner — only when there IS a weekly fee */}
        {store.approved && store.subscriptionStatus !== "active" && store.subscriptionStatus !== "trialing" && store.weeklyFeePence != null && store.weeklyFeePence !== 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-primary text-sm">
                  {store.subscriptionStatus === "past_due" || store.subscriptionStatus === "unpaid"
                    ? "⚠️ Subscription payment failed — your store is paused"
                    : "🎉 Your store is approved and ready to go live!"}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {store.subscriptionStatus === "past_due" || store.subscriptionStatus === "unpaid"
                    ? `Please resubscribe${store.weeklyFeePence === 0 ? "" : ` at ${fmtFee(store.weeklyFeePence)}/week`} to reactivate your listing.`
                    : `${store.weeklyFeePence === 0 ? "Activate your store for free" : `Subscribe at ${fmtFee(store.weeklyFeePence)}/week`} to start taking orders.`}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 shrink-0"
                disabled={subscribeLoading}
                onClick={() => void startSubscription()}
              >
                {subscribeLoading
                  ? (store.weeklyFeePence === 0 ? "Activating…" : "Redirecting…")
                  : store.subscriptionStatus === "past_due" || store.subscriptionStatus === "unpaid"
                    ? `Resubscribe${store.weeklyFeePence === 0 ? "" : ` — ${fmtFee(store.weeklyFeePence)}/week`}`
                    : store.weeklyFeePence === 0
                      ? "Go Live — Free"
                      : `Subscribe & Go Live — ${fmtFee(store.weeklyFeePence)}/week`}
              </Button>
            </div>
          </div>
        )}

      {/* Featured / Sponsor strip */}
        {store.approved && (store.subscriptionStatus === "active" || store.subscriptionStatus === "trialing") && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            {sponsorWeeks ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">Feature your store at the top of search results</p>
                <div className="flex gap-2 flex-wrap">
                  {([1, 2, 4] as const).map((w) => (
                    <Button key={w} size="sm" onClick={() => void sponsorStore(w)} disabled={sponsorLoading}
                      className="bg-amber-500 hover:bg-amber-600 text-white">
                      {w} week{w > 1 ? "s" : ""}
                    </Button>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setSponsorWeeks(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setSponsorWeeks(1)}
                className="flex items-center gap-2 text-sm text-amber-700 hover:text-amber-900 dark:text-amber-300 font-semibold transition-colors">
                <Star size={15} className="text-amber-500" fill="currentColor" />
                Feature your store in search results
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-slate-100 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-sm font-semibold whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${tab === t.id ? "text-primary border-primary" : "text-slate-500 border-transparent hover:text-slate-700"}`}
          >
            <t.icon size={14} />
            <span className="sm:hidden">{t.shortLabel}</span>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Menu tab */}
      {tab === "drivers" && <DriverRequestsPanel store={store} />}

      {tab === "menu" && (
        <div className="space-y-6">
          <MenuSellingDays storeId={storeId} />
          {/* Sub-sections switch. This changes the whole menu, not one section, so
              it sits above the list. Stacks on a phone, sits side-by-side from sm. */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-foreground text-sm">Use sub-sections</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${subcategoriesEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {subcategoriesEnabled ? "On" : "Off"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Group sections under a heading — like Pizzas → 10 inch, 12 inch. Customers tap the heading first, then choose from inside it.
                </p>
              </div>
              <Button
                size="sm"
                variant={subcategoriesEnabled ? "outline" : undefined}
                disabled={savingSubcats}
                className={`min-h-[44px] w-full sm:w-auto flex-shrink-0 ${subcategoriesEnabled ? "" : "bg-primary hover:bg-primary/90"}`}
                onClick={() => { if (subcategoriesEnabled) setConfirmSubcatsOff(true); else void toggleSubcategories(true); }}
              >
                {savingSubcats ? "Saving…" : subcategoriesEnabled ? "Turn off" : "Turn on"}
              </Button>
            </div>
            {/* Say what will happen before it happens — and that it is reversible. */}
            {confirmSubcatsOff && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-3">
                <p className="text-xs text-amber-900 dark:text-amber-300 leading-relaxed">
                  Turn this off and customers will see every sub-section as an ordinary section, all in one long list.
                  {" "}<span className="font-semibold">Nothing is deleted.</span> Turn it back on and your groupings come back exactly as they are now.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button size="sm" variant="outline" disabled={savingSubcats}
                    className="min-h-[44px] w-full sm:w-auto text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => void toggleSubcategories(false)}>
                    Yes, turn off
                  </Button>
                  <Button size="sm" variant="outline" className="min-h-[44px] w-full sm:w-auto"
                    onClick={() => setConfirmSubcatsOff(false)}>
                    Keep sub-sections
                  </Button>
                </div>
              </div>
            )}
            {subcategoriesEnabled && !confirmSubcatsOff && (
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                Sub-sections go one level deep — a heading can't also sit under another heading. Pick the heading for each section below.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={menuSearch}
                onChange={(e) => { setMenuSearch(e.target.value); if (e.target.value) sections.forEach((s) => { void loadSectionItems(s.id); }); }}
                placeholder={`Search ${items.length} items…`}
                className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              {menuSearch && (
                <button onClick={() => setMenuSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={13} />
                </button>
              )}
            </div>
            {sections.length > 1 && !menuSearch && (
              <button
                onClick={() => {
                  if (collapsedSections.size === sections.length) {
                    // Expand all — load items for each section lazily
                    setCollapsedSections(new Set());
                    sections.forEach((s) => { void loadSectionItems(s.id); });
                  } else {
                    setCollapsedSections(new Set(sections.map((s) => s.id)));
                  }
                }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors whitespace-nowrap">
                {collapsedSections.size === sections.length ? "Expand all" : "Collapse all"}
              </button>
            )}
          </div>
          {menuSearch && (() => {
            const q = menuSearch.toLowerCase();
            const matched = items.filter((i) => i.name.toLowerCase().includes(q) || (i.description ?? "").toLowerCase().includes(q));
            return matched.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No items match "{menuSearch}"</p>
            ) : (
              <p className="text-xs text-slate-400">{matched.length} item{matched.length !== 1 ? "s" : ""} matching "{menuSearch}"</p>
            );
          })()}
          {!menuSearch && <div className="mb-4"><CheckoutUpsellsManager storeId={storeId} /></div>}
          {orderedSections().filter((section) => {
            if (!menuSearch) return true;
            const q = menuSearch.toLowerCase();
            return items.some((i) => i.sectionId === section.id && (i.name.toLowerCase().includes(q) || (i.description ?? "").toLowerCase().includes(q)));
          }).map((section) => {
          const sectionHeading = parentOf(section);
          const sectionKids = subcategoriesEnabled ? childrenOf(section.id) : [];
          const sectionOwnItems = items.filter((i) => i.sectionId === section.id).length;
          return (
            <div
              key={section.id}
              /* Sub-sections are indented with an accent rail so the shape of the
                 menu reads at a glance. Only 12px of indent on a phone — enough to
                 see, small enough that a 320px screen never scrolls sideways. */
              className={`bg-white rounded-2xl border p-5 transition-colors ${sectionDragTarget === section.id ? "border-primary/40 bg-primary/5" : "border-slate-100"} ${sectionHeading ? "ml-3 sm:ml-8 border-l-4 border-l-primary/30" : ""}`}
              draggable={editSectionId !== section.id}
              onDragStart={(e) => { if (!dragAllowedRef.current) { e.preventDefault(); return; } dragSectionId.current = section.id; }}
              onDragOver={(e) => { e.preventDefault(); if (!canDropSectionOn(section.id)) return; dragOverSectionId.current = section.id; setSectionDragTarget(section.id); }}
              onDragLeave={() => { if (dragOverSectionId.current === section.id) { setSectionDragTarget(null); dragOverSectionId.current = null; } }}
              onDrop={(e) => { e.preventDefault(); setSectionDragTarget(null); dragOverSectionId.current = null; const fromId = dragSectionId.current; if (fromId !== null && canDropSectionOn(section.id)) void reorderSections(fromId, section.id); dragSectionId.current = null; }}
              onDragEnd={() => { setSectionDragTarget(null); dragSectionId.current = null; dragAllowedRef.current = false; }}
            >
              {/* Wraps rather than overflows: the name plus the three action
                  buttons do not fit on one line on a 320px phone, and indented
                  sub-sections have 12px less room again. */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                {editSectionId === section.id ? (
                  <div className="flex items-center gap-2 flex-1 mr-2">
                    <Input
                      value={editSectionName}
                      onChange={(e) => setEditSectionName(e.target.value)}
                      className="h-8 text-sm font-semibold"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") void updateSection(); if (e.key === "Escape") { setEditSectionId(null); setEditSectionName(""); } }}
                    />
                    <Button size="sm" className="h-8 bg-primary hover:bg-primary/90 text-xs" onClick={() => void updateSection()} disabled={!editSectionName.trim()}>Save</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setEditSectionId(null); setEditSectionName(""); }}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0"
                      title="Drag to reorder"
                      onMouseDown={() => { dragAllowedRef.current = true; }}
                      onMouseUp={() => { dragAllowedRef.current = false; }}
                    >
                      <GripVertical size={16} />
                    </div>
                    <h3 className="font-bold text-foreground truncate max-w-[140px] sm:max-w-none">{section.name}</h3>
                    <button onClick={() => { setEditSectionId(section.id); setEditSectionName(section.name); }} className="text-slate-300 hover:text-slate-600 transition-colors" title="Rename category">
                      <Pencil size={13} />
                    </button>
                    <label className="flex items-center gap-1 ml-1 text-[10px] text-indigo-600 cursor-pointer" title="Upsell only: hide these items from the normal menu and offer them at checkout ('Fancy any of these?')">
                      <input type="checkbox" checked={section.upsellOnly ?? false} onChange={(e) => void setSectionUpsellOnly(section.id, e.target.checked)} className="w-3 h-3 accent-indigo-600" />
                      <span className="font-semibold uppercase">Upsell only</span>
                    </label>
                    {section.upsellOnly && <span className="text-[10px] text-indigo-500 italic">off menu → checkout</span>}
                    {user?.role === "admin" && (
                      <span className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-200" title="Category menu-price uplift override (admin only). Blank = inherit shop default.">
                        <span className="text-[10px] font-semibold text-violet-600 uppercase">Uplift</span>
                        <input
                          type="number" min={0} max={500} placeholder="inherit"
                          disabled={section.upliftExcluded ?? false}
                          defaultValue={section.upliftPercent ?? ""}
                          key={`${section.id}-${section.upliftPercent}-${section.upliftExcluded}`}
                          className="w-14 border border-violet-200 rounded px-1 py-0.5 text-xs text-center disabled:bg-slate-100"
                          onBlur={(e) => { const v = e.target.value === "" ? null : Math.max(0, Math.min(500, Math.round(Number(e.target.value)))); if (v !== (section.upliftPercent ?? null)) void saveSectionUplift(section.id, v, false); }}
                        />
                        <span className="text-[10px] text-slate-400">%</span>
                        <label className="flex items-center gap-0.5 text-[10px] text-slate-500 cursor-pointer" title="Exclude this category from uplift (0%)">
                          <input type="checkbox" checked={section.upliftExcluded ?? false} onChange={(e) => void saveSectionUplift(section.id, null, e.target.checked)} className="w-3 h-3 accent-violet-600" />
                          excl
                        </label>
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {collapsedSections.has(section.id) && (
                    loadingSectionIds.has(section.id)
                      ? <span className="text-xs text-slate-400 flex items-center gap-1"><LoaderCircle size={12} className="animate-spin" /> Loading…</span>
                      : items.filter((i) => i.sectionId === section.id).length > 0
                        ? <span className="text-xs text-slate-400 tabular-nums">{items.filter((i) => i.sectionId === section.id).length} items</span>
                        : null
                  )}
                  <button
                    onClick={() => {
                      const wasCollapsed = collapsedSections.has(section.id);
                      setCollapsedSections((prev) => { const next = new Set(prev); next.has(section.id) ? next.delete(section.id) : next.add(section.id); return next; });
                      if (wasCollapsed) void loadSectionItems(section.id);
                    }}
                    className="text-slate-300 hover:text-slate-600 transition-colors p-1" title={collapsedSections.has(section.id) ? "Expand" : "Collapse"}>
                    {collapsedSections.has(section.id) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    onClick={() => { setCollapsedSections((prev) => { const next = new Set(prev); next.delete(section.id); return next; }); void loadSectionItems(section.id); setAddItem({ sectionId: section.id, name: "", description: "", pricePence: 0, imageUrl: "", discountType: "", discountValue: 0, discountLabel: "", calories: null, multiBuyQty: 0, multiBuyPricePence: 0, isEndToEnd: false, isHighValue: false }); }}>
                    <Plus size={12} /> Add item
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-red-500 border-red-200 hover:bg-red-50"
                    onClick={() => void deleteSection(section.id)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>

              {/* Heading picker. Deliberately a native <select>: it is keyboard
                  accessible, renders as the OS picker on every phone, and adds no
                  popover of its own that could swallow the card's drag handlers.
                  Sits outside the collapse so the structure is always visible. */}
              {subcategoriesEnabled && editSectionId !== section.id && (
                <div className="mb-4 pb-4 border-b border-slate-100">
                  <label className="block">
                    <span className="block text-xs font-semibold text-slate-500 mb-1">Sits under</span>
                    <select
                      value={sectionKids.length > 0 ? "" : (sectionHeading?.id ?? "")}
                      disabled={sectionKids.length > 0}
                      onChange={(e) => void setSectionParent(section.id, e.target.value ? Number(e.target.value) : null)}
                      className="w-full sm:max-w-xs min-h-[44px] border border-input rounded-xl px-3 text-sm bg-background disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Nothing — this is a section of its own</option>
                      {eligibleParents(section.id).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </label>
                  {/* Explain the one-level rule where it bites, rather than just
                      greying the control out and leaving the shop guessing. */}
                  {sectionKids.length > 0 ? (
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                      This is a heading with {sectionKids.length} sub-section{sectionKids.length !== 1 ? "s" : ""} under it ({sectionKids.map((c) => c.name).join(", ")}), so it can't sit under another heading — sub-sections only go one level deep. Move those out first if you need to.
                    </p>
                  ) : eligibleParents(section.id).length === 0 ? (
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Add another section first and it can become a heading for this one.</p>
                  ) : null}
                  {/* Verified against the customer menu: once a section has
                      sub-sections, only the sub-section tiles are shown, so items
                      left in the heading itself become unorderable. Only warn once
                      the items are actually loaded, so it can never cry wolf. */}
                  {sectionKids.length > 0 && sectionOwnItems > 0 && (
                    <p className="text-xs text-amber-700 mt-1.5 leading-relaxed">
                      Careful: the {sectionOwnItems} item{sectionOwnItems !== 1 ? "s" : ""} sitting directly in this heading are hidden from customers — they only see the sub-sections. Move them into a sub-section so people can order them.
                    </p>
                  )}
                </div>
              )}

              {!collapsedSections.has(section.id) && (<>
              {/* Add item form */}
              {addItem?.sectionId === section.id && (
                <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
                  <Input placeholder="Item name *" value={addItem.name} onChange={(e) => setAddItem({ ...addItem, name: e.target.value })} />
                  <Input placeholder="Description (optional)" value={addItem.description} onChange={(e) => setAddItem({ ...addItem, description: e.target.value })} />
                  <div className="flex gap-3 flex-wrap items-center">
                    <div className="flex gap-2 items-center">
                      <span className="text-sm font-medium text-slate-600">Price £</span>
                      <Input type="number" min={0} step={0.01} placeholder="0.00" value={addItem.pricePence / 100 || ""} onChange={(e) => setAddItem({ ...addItem, pricePence: Math.round(Number(e.target.value) * 100) })} className="w-24" />
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-sm font-medium text-slate-600">Calories</span>
                      <Input type="number" min={0} placeholder="kcal" value={addItem.calories ?? ""} onChange={(e) => setAddItem({ ...addItem, calories: e.target.value ? Number(e.target.value) : null })} className="w-20" />
                    </div>
                  </div>
                  {/* Discount */}
                  <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Discount (optional)</p>
                    <div className="flex gap-2 flex-wrap items-center">
                      <select value={addItem.discountType} onChange={(e) => setAddItem({ ...addItem, discountType: e.target.value })}
                        className="border border-input rounded-md px-2 py-1.5 text-sm bg-background w-32">
                        <option value="">None</option>
                        <option value="percentage">% off</option>
                        <option value="fixed">£ off</option>
                      </select>
                      {addItem.discountType && (
                        <>
                          <Input type="number" min={0} step={addItem.discountType === "percentage" ? 1 : 0.01}
                            placeholder={addItem.discountType === "percentage" ? "e.g. 20" : "e.g. 0.50"}
                            value={addItem.discountValue / (addItem.discountType === "fixed" ? 100 : 1) || ""}
                            onChange={(e) => setAddItem({ ...addItem, discountValue: Math.round(Number(e.target.value) * (addItem.discountType === "fixed" ? 100 : 1)) })}
                            className="w-24" />
                          <Input placeholder="Label (e.g. 20% off!)" value={addItem.discountLabel}
                            onChange={(e) => setAddItem({ ...addItem, discountLabel: e.target.value })} className="w-36" />
                        </>
                      )}
                    </div>
                  </div>
                  {/* Multi-buy offer */}
                  <div className="border border-indigo-100 rounded-lg p-3 space-y-2 bg-indigo-50/40">
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Multi-buy offer (optional)</p>
                    <p className="text-xs text-slate-500">Sell in bulk — e.g. "3 cans for £2.00". Customers pay the bundle price automatically when they reach the quantity.</p>
                    <div className="flex gap-3 flex-wrap items-center">
                      <span className="text-sm font-medium text-slate-600">Buy</span>
                      <Input type="number" min={2} step={1} placeholder="qty e.g. 3"
                        value={addItem.multiBuyQty || ""}
                        onChange={(e) => setAddItem({ ...addItem, multiBuyQty: e.target.value ? Number(e.target.value) : 0 })}
                        className="w-24" />
                      <span className="text-sm font-medium text-slate-600">for £</span>
                      <Input type="number" min={0} step={0.01} placeholder="price e.g. 2.00"
                        value={addItem.multiBuyPricePence / 100 || ""}
                        onChange={(e) => setAddItem({ ...addItem, multiBuyPricePence: Math.round(Number(e.target.value) * 100) })}
                        className="w-28" />
                      {addItem.multiBuyQty > 0 && addItem.multiBuyPricePence > 0 && (
                        <span className="bg-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                          Preview: {addItem.multiBuyQty} for £{(addItem.multiBuyPricePence / 100).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  <ImageUpload value={addItem.imageUrl} onChange={(url) => setAddItem({ ...addItem, imageUrl: url ?? "" })} stockCategory="deal" />
                  <label className="flex items-center gap-2 cursor-pointer select-none rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <input type="checkbox" checked={addItem.isHighValue} onChange={(e) => setAddItem({ ...addItem, isHighValue: e.target.checked })} className="w-4 h-4 accent-amber-600" />
                    <span className="text-sm font-semibold text-amber-900 dark:text-amber-300">High Value Item</span>
                    <span className="text-xs text-amber-700">Delivery jobs will only be offered to Gold Trusted Drivers</span>
                  </label>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => void createItem()} disabled={!addItem.name || addItem.pricePence < 0 || Number.isNaN(addItem.pricePence)}>Add</Button>
                    <Button size="sm" variant="outline" onClick={() => setAddItem(null)}>Cancel</Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {[...items].filter((i) => {
                    if (i.sectionId !== section.id) return false;
                    if (!menuSearch) return true;
                    const q = menuSearch.toLowerCase();
                    return i.name.toLowerCase().includes(q) || (i.description ?? "").toLowerCase().includes(q);
                  }).sort((a, b) => a.sortOrder - b.sortOrder).map((item) => {
                  const discountedPence = item.discountType === "percentage" && item.discountValue
                    ? Math.round(item.pricePence * (1 - item.discountValue / 100))
                    : item.discountType === "fixed" && item.discountValue
                    ? item.pricePence - item.discountValue
                    : null;
                  const optionsOpen = expandedItemOptions === item.id;
                  const isEditing = editItem?.id === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border overflow-hidden transition-colors ${itemDragTarget === item.id ? "border-primary/40 bg-primary/5" : "border-slate-100"}`}
                      draggable={!isEditing}
                      onDragStart={(e) => { if (!dragAllowedRef.current) { e.preventDefault(); return; } dragItemId.current = item.id; dragItemSectionId.current = item.sectionId; }}
                      onDragOver={(e) => { e.preventDefault(); dragOverItemId.current = item.id; setItemDragTarget(item.id); }}
                      onDragLeave={() => { if (dragOverItemId.current === item.id) { setItemDragTarget(null); dragOverItemId.current = null; } }}
                      onDrop={(e) => { e.preventDefault(); setItemDragTarget(null); dragOverItemId.current = null; if (dragItemId.current !== null && dragItemId.current !== item.id && dragItemSectionId.current === item.sectionId) void reorderItems(dragItemId.current, item.id, item.sectionId); dragItemId.current = null; dragItemSectionId.current = null; }}
                      onDragEnd={() => { setItemDragTarget(null); dragItemId.current = null; dragItemSectionId.current = null; dragAllowedRef.current = false; }}
                    >
                      {isEditing && editItem ? (
                        <div className="bg-slate-50 p-4 space-y-3">
                          <p className="text-xs font-semibold text-primary uppercase tracking-wide">Editing: {item.name}</p>
                          <Input placeholder="Item name *" value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} />
                          <Input placeholder="Description (optional)" value={editItem.description} onChange={(e) => setEditItem({ ...editItem, description: e.target.value })} />
                          <div className="flex gap-2 items-center flex-wrap">
                            <span className="text-sm font-medium text-slate-600">Category</span>
                            <select
                              value={editItem.sectionId}
                              onChange={(e) => setEditItem({ ...editItem, sectionId: Number(e.target.value) })}
                              className="border border-input rounded-md px-2 py-1.5 text-sm bg-background max-w-full"
                            >
                              {orderedSections().map((s) => {
                                const parent = parentOf(s);
                                return (
                                  <option key={s.id} value={s.id}>
                                    {parent ? `${parent.name} › ${s.name}` : s.name}
                                  </option>
                                );
                              })}
                            </select>
                            {editItem.sectionId !== item.sectionId && (
                              <span className="text-xs text-amber-600 font-medium">Will move on save</span>
                            )}
                          </div>
                          <div className="flex gap-3 flex-wrap items-center">
                            <div className="flex gap-2 items-center">
                              <span className="text-sm font-medium text-slate-600">Price £</span>
                              <Input type="number" min={0} step={0.01} placeholder="0.00" value={editItem.pricePence / 100 || ""} onChange={(e) => setEditItem({ ...editItem, pricePence: Math.round(Number(e.target.value) * 100) })} className="w-24" />
                            </div>
                            <div className="flex gap-2 items-center">
                              <span className="text-sm font-medium text-slate-600">Calories</span>
                              <Input type="number" min={0} placeholder="kcal" value={editItem.calories ?? ""} onChange={(e) => setEditItem({ ...editItem, calories: e.target.value ? Number(e.target.value) : null })} className="w-20" />
                            </div>
                          </div>
                          <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Discount (optional)</p>
                            <div className="flex gap-2 flex-wrap items-center">
                              <select value={editItem.discountType} onChange={(e) => setEditItem({ ...editItem, discountType: e.target.value })}
                                className="border border-input rounded-md px-2 py-1.5 text-sm bg-background w-32">
                                <option value="">None</option>
                                <option value="percentage">% off</option>
                                <option value="fixed">£ off</option>
                              </select>
                              {editItem.discountType && (
                                <>
                                  <Input type="number" min={0} step={editItem.discountType === "percentage" ? 1 : 0.01}
                                    placeholder={editItem.discountType === "percentage" ? "e.g. 20" : "e.g. 0.50"}
                                    value={editItem.discountValue / (editItem.discountType === "fixed" ? 100 : 1) || ""}
                                    onChange={(e) => setEditItem({ ...editItem, discountValue: Math.round(Number(e.target.value) * (editItem.discountType === "fixed" ? 100 : 1)) })}
                                    className="w-24" />
                                  <Input placeholder="Label (e.g. 20% off!)" value={editItem.discountLabel}
                                    onChange={(e) => setEditItem({ ...editItem, discountLabel: e.target.value })} className="w-36" />
                                </>
                              )}
                            </div>
                          </div>
                          <div className="border border-indigo-100 rounded-lg p-3 space-y-2 bg-indigo-50/40">
                            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Multi-buy offer (optional)</p>
                            <div className="flex gap-3 flex-wrap items-center">
                              <span className="text-sm font-medium text-slate-600">Buy</span>
                              <Input type="number" min={2} step={1} placeholder="qty e.g. 3"
                                value={editItem.multiBuyQty || ""}
                                onChange={(e) => setEditItem({ ...editItem, multiBuyQty: e.target.value ? Number(e.target.value) : 0 })}
                                className="w-24" />
                              <span className="text-sm font-medium text-slate-600">for £</span>
                              <Input type="number" min={0} step={0.01} placeholder="price e.g. 2.00"
                                value={editItem.multiBuyPricePence / 100 || ""}
                                onChange={(e) => setEditItem({ ...editItem, multiBuyPricePence: Math.round(Number(e.target.value) * 100) })}
                                className="w-28" />
                              {editItem.multiBuyQty > 0 && editItem.multiBuyPricePence > 0 && (
                                <span className="bg-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                                  {editItem.multiBuyQty} for £{(editItem.multiBuyPricePence / 100).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          {user?.role === "admin" && (
                            <div className="border border-violet-200 rounded-lg p-3 space-y-2 bg-violet-50/50">
                              <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Menu uplift override · Admin only</p>
                              <p className="text-[11px] text-slate-500">Ayrshire Hub markup for this item. Leave blank to inherit the category/shop default. Precedence: item → category → shop.</p>
                              <div className="flex items-center gap-3 flex-wrap">
                                <label className="flex items-center gap-1.5 text-sm">
                                  <Input type="number" min={0} max={500} step={1} placeholder="inherit"
                                    disabled={editItem.upliftExcluded}
                                    value={editItem.upliftPercent ?? ""}
                                    onChange={(e) => setEditItem({ ...editItem, upliftPercent: e.target.value === "" ? null : Math.max(0, Math.min(500, Math.round(Number(e.target.value)))) })}
                                    className="w-24" />
                                  <span className="text-slate-500">% uplift</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input type="checkbox" checked={editItem.upliftExcluded} onChange={(e) => setEditItem({ ...editItem, upliftExcluded: e.target.checked })} className="w-4 h-4 accent-violet-600" />
                                  <span className="text-sm text-slate-700">Exclude from uplift (0%)</span>
                                </label>
                              </div>
                            </div>
                          )}
                          <ImageUpload value={editItem.imageUrl} onChange={(url) => setEditItem({ ...editItem, imageUrl: url ?? "" })} stockCategory="deal" />
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input type="checkbox" checked={editItem.isEndToEnd} onChange={(e) => setEditItem({ ...editItem, isEndToEnd: e.target.checked })} className="w-4 h-4 accent-purple-600" />
                            <span className="text-sm font-medium text-slate-700">Collect &amp; Return (E2E delivery)</span>
                            <span className="text-xs text-slate-400">When ordered, a driver will collect this item from the customer, bring it to your shop, and then a second driver will return it once ready</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer select-none rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3">
                            <input type="checkbox" checked={editItem.isHighValue} onChange={(e) => setEditItem({ ...editItem, isHighValue: e.target.checked })} className="w-4 h-4 accent-amber-600" />
                            <span className="text-sm font-semibold text-amber-900 dark:text-amber-300">High Value Item</span>
                            <span className="text-xs text-amber-700">Trusted drivers only, on every delivery leg</span>
                          </label>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => void updateItem()} disabled={!editItem.name || editItem.pricePence < 0 || Number.isNaN(editItem.pricePence)}>Save changes</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-slate-50/50 transition-colors">
                            <div
                              className="hidden sm:block cursor-grab active:cursor-grabbing text-slate-200 hover:text-slate-400 flex-shrink-0 transition-colors"
                              title="Drag to reorder"
                              onMouseDown={() => { dragAllowedRef.current = true; }}
                              onMouseUp={() => { dragAllowedRef.current = false; }}
                            >
                              <GripVertical size={14} />
                            </div>
                            {item.imageUrl && <img src={item.imageUrl} alt={item.name} loading="lazy" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className={`font-semibold text-sm ${item.available ? "text-foreground" : "text-slate-400 line-through"}`}>{item.name}</p>
                                {item.multiBuyQty && item.multiBuyPricePence && (
                                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-1.5 py-0.5 rounded">
                                    {item.multiBuyQty} for £{(item.multiBuyPricePence / 100).toFixed(2)}
                                  </span>
                                )}
                                {item.discountType && (
                                  <span className="bg-red-100 text-red-600 text-xs font-bold px-1.5 py-0.5 rounded">
                                    {item.discountLabel ?? (item.discountType === "percentage" ? `${item.discountValue}% off` : `£${((item.discountValue ?? 0) / 100).toFixed(2)} off`)}
                                  </span>
                                )}
                                {item.calories && <span className="text-xs text-slate-400">{item.calories} kcal</span>}
                                {item.isEndToEnd && <span className="bg-purple-100 text-purple-700 text-xs font-bold px-1.5 py-0.5 rounded">Collect &amp; Return</span>}
                                {item.isHighValue && <span className="bg-amber-100 text-amber-800 dark:text-amber-300 text-xs font-bold px-1.5 py-0.5 rounded">High Value</span>}
                              </div>
                              {item.description && <p className="text-xs text-slate-500 truncate">{item.description}</p>}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {discountedPence !== null ? (
                                <div className="text-right">
                                  <span className="font-bold text-primary text-sm">{pence(discountedPence)}</span>
                                  <span className="text-xs text-slate-400 line-through ml-1">{pence(item.pricePence)}</span>
                                </div>
                              ) : (
                                <span className="font-bold text-primary text-sm">{pence(item.pricePence)}</span>
                              )}
                            </div>
                            <button
                              onClick={() => setExpandedItemOptions(optionsOpen ? null : item.id)}
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium shrink-0 transition-colors ${optionsOpen ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                              title="Manage options & modifiers"
                            >
                              <Settings2 size={11} />
                              <span className="hidden sm:inline">Options</span>
                              {optionsOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                            </button>
                            <button onClick={() => void toggleAvailable(item)} className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${item.available ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                              {item.available ? "On" : "Off"}
                            </button>
                            <button
                              onClick={() => setEditItem({ id: item.id, sectionId: item.sectionId, name: item.name, description: item.description ?? "", pricePence: item.pricePence, imageUrl: item.imageUrl ?? "", discountType: item.discountType ?? "", discountValue: item.discountValue ?? 0, discountLabel: item.discountLabel ?? "", calories: item.calories ?? null, multiBuyQty: item.multiBuyQty ?? 0, multiBuyPricePence: item.multiBuyPricePence ?? 0, isEndToEnd: item.isEndToEnd ?? false, isHighValue: item.isHighValue ?? false, upliftPercent: item.upliftPercent ?? null, upliftExcluded: item.upliftExcluded ?? false })}
                              className="text-slate-300 hover:text-primary transition-colors flex-shrink-0"
                              title="Edit item"
                            >
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => void deleteItem(item.id)} className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"><Trash2 size={14} /></button>
                          </div>
                          {optionsOpen && (
                            <div className="p-3 border-t border-slate-100">
                              <ItemOptionsPanel storeId={storeId} itemId={item.id} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              </>)}
            </div>
          );
          })}

          {/* Add section */}
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-5 space-y-3">
            <p className="text-sm font-semibold text-slate-600">Add a menu section</p>
            <Input placeholder="Section name (e.g. Starters, Mains, Drinks)" value={addSectionName} onChange={(e) => setAddSectionName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void addSection()} />
            {/* Choose the heading up front — otherwise every new sub-section means
                creating it, finding it, then moving it. 0 excludes nothing. */}
            {subcategoriesEnabled && (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Sits under</span>
                <select
                  value={addSectionParentId || ""}
                  onChange={(e) => setAddSectionParentId(Number(e.target.value) || 0)}
                  className="w-full sm:max-w-xs min-h-[44px] border border-input rounded-xl px-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Nothing — a section of its own</option>
                  {eligibleParents(0).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}
            <Button className="bg-primary hover:bg-primary/90 w-full sm:w-auto min-h-[44px] gap-1.5" onClick={() => void addSection()} disabled={!addSectionName.trim()}>
              <Plus size={16} /> Add section
            </Button>
          </div>
        </div>
      )}

      {/* Orders tab */}
      {tab === "orders" && (
        <div className="space-y-3">
          {deliveryRunsError&&<p role="alert" className="text-sm text-red-700">{deliveryRunsError}</p>}
          {deliveryRuns.map(run=><RunOrderCard key={`run-${run.id}`} run={run} headers={authHeaders()} onChanged={refreshOrders}/>)}
          {orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400">
              <Package size={36} className="mx-auto mb-3 opacity-30" />
              <p>{deliveryRuns.length?"No single-drop orders":"No orders yet"}</p>
            </div>
          ) : [...orders].sort((a, b) => {
            const STATUS_PRIORITY: Record<string, number> = {
              pending: 0, confirmed: 1, preparing: 2, ready: 3,
              out_for_delivery: 4, collected: 4, at_shop: 4, ready_for_return: 4, returning: 4,
              delivered: 5, cancelled: 6,
            };
            const pa = STATUS_PRIORITY[a.status] ?? 5;
            const pb = STATUS_PRIORITY[b.status] ?? 5;
            if (pa !== pb) return pa - pb;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }).map((order) => {
            const isHubDriver = order.driverMode === "hub" && order.fulfilmentType === "delivery";
            const driverAsgn = driverAssignments[order.id];
            const waitingForDriver = isHubDriver && order.status === "ready" && !driverAsgn;
            return (
              <div key={order.id} className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-bold text-foreground">Order #{order.id}</p>
                    <p className="text-xs text-slate-500">{new Date(order.createdAt).toLocaleString("en-GB")}</p>
                    {order.isMultiOrderChild && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold">
                        🛍️ Multi-store order — driver collecting from multiple locations
                      </span>
                    )}
                    {order.customerName && <p className="text-sm text-slate-600 mt-1">{order.customerName} {order.customerPhone && `· ${order.customerPhone}`}</p>}
                    {order.deliveryAddress && <p className="text-sm text-slate-600">{order.deliveryAddress}</p>}
                    {order.scheduledFor && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Clock size={11} /> Scheduled: {new Date(order.scheduledFor).toLocaleString("en-GB")}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {(() => {
                      /* Lead with what the customer actually paid — it is the
                         number on their receipt and the one they quote on the
                         phone. The order value is kept underneath when the two
                         differ so the shop can still reconcile their payout. */
                      const paid = order.customerPaidPence ?? order.totalPence;
                      const hubFee = paid - order.totalPence;
                      return (
                        <>
                          <p className="font-bold text-primary">{pence(paid)}</p>
                          {hubFee > 0 && (
                            <p className="text-[11px] text-slate-500 mt-0.5 whitespace-nowrap">
                              Order {pence(order.totalPence)} + {pence(hubFee)} Hub fee
                            </p>
                          )}
                        </>
                      );
                    })()}
                    <Badge className={`${statusColor(order.status)} text-xs mt-1`}>{formatStatus(order.status)}</Badge>
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  {order.items.map((item) => {
                    type ParsedOpt = { name?: string; optionName?: string; groupName?: string; pricePence: number };
                    let parsedOpts: ParsedOpt[] = [];
                    try { if (item.selectedOptions) parsedOpts = JSON.parse(item.selectedOptions as string) as ParsedOpt[]; } catch { /* ignore */ }
                    const groups: Array<{ groupName: string; items: ParsedOpt[] }> = [];
                    for (const o of parsedOpts) {
                      const gn = o.groupName ?? "";
                      const last = groups[groups.length - 1];
                      if (last && last.groupName === gn) last.items.push(o);
                      else groups.push({ groupName: gn, items: [o] });
                    }
                    return (
                      <div key={item.id} className="text-sm text-slate-600">
                        <div className="flex justify-between font-medium">
                          <span>{item.quantity}× {item.name}{item.isFreeItem ? <span className="ml-1.5 text-xs bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded">FREE</span> : null}</span>
                          <span>{item.isFreeItem ? <span className="text-green-700 font-semibold">FREE</span> : pence(item.pricePence * item.quantity)}</span>
                        </div>
                        {groups.map((g, gi) => {
                          const label = g.items.map(o => o.name ?? o.optionName ?? "").join(", ");
                          return (
                            <p key={gi} className="text-xs text-slate-500 ml-4">
                              {g.groupName ? `${g.groupName}: ${label}` : `+ ${label}`}
                            </p>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Hub driver badge / panel */}
                {waitingForDriver && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg text-xs font-semibold text-amber-700">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    Waiting for a hub driver to claim…
                  </div>
                )}
                {isHubDriver && driverAsgn && (
                  <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-xl overflow-hidden">
                    <div className="p-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-blue-700 mb-1">Driver</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {driverAsgn.vehicleType === "car" ? "🚗" : driverAsgn.vehicleType === "van" ? "🚐" : driverAsgn.vehicleType === "scooter" || driverAsgn.vehicleType === "moped" ? "🛵" : "🚲"} {driverAsgn.driverName ?? "Driver"}
                        </p>
                        {driverAsgn.driverPhone && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">📞 {driverAsgn.driverPhone}</p>
                        )}
                        <p className="text-xs text-slate-500">
                          <span className={`font-semibold ${driverAsgn.deliveredAt ? "text-emerald-600" : driverAsgn.pickedUpAt ? "text-blue-600" : "text-amber-600"}`}>
                            {driverAsgn.deliveredAt ? "Delivered" : driverAsgn.pickedUpAt ? "Picked up" : "Claimed"}
                          </span>
                          {" · "}claimed {new Date(driverAsgn.claimedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 items-end flex-shrink-0">
                        <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => void openDriverChat(order.id)}>
                          <MessageSquare size={12} /> Message
                        </Button>
                        {!driverAsgn.deliveredAt && (
                          <Button size="sm" variant="outline" className="gap-1.5 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => void cancelDriverAssignment(order.id)}>
                            <X size={12} /> Reassign
                          </Button>
                        )}
                      </div>
                    </div>
                    {order.status === "out_for_delivery" && driverAsgn.driverLat != null && driverAsgn.driverLng != null && (
                      <div className="rounded-b-xl overflow-hidden">
                        <DriverLocationMap lat={driverAsgn.driverLat} lng={driverAsgn.driverLng} height={160} />
                      </div>
                    )}
                  </div>
                )}

                {/* ETA display */}
                {order.estimatedReadyAt && order.status !== "delivered" && order.status !== "cancelled" && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg text-sm text-amber-800 dark:text-amber-300 font-semibold mb-1">
                    <Clock size={14} className="text-amber-600 flex-shrink-0" />
                    {order.fulfilmentType === "delivery" ? "Est. delivery" : "Est. ready"}:{" "}
                    {new Date(order.estimatedReadyAt).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "numeric", minute: "2-digit", hour12: true })}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap items-center">
                  {order.status === "pending" && <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "confirmed")}>Confirm</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-200" onClick={() => void updateOrderStatus(order.id, "cancelled")}>Cancel</Button>
                  </>}
                  {order.status === "confirmed" && <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "preparing")}>Mark Preparing</Button>}
                  {order.status === "preparing" && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "ready")}>Mark Ready</Button>}
                  {order.status === "ready" && order.fulfilmentType === "delivery" && !isHubDriver && <Button size="sm" className="bg-purple-600 hover:bg-purple-700 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "out_for_delivery")}>Mark Out for Delivery</Button>}
                  {order.status === "ready" && order.fulfilmentType === "collection" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "delivered")}>Mark Collected</Button>}
                  {order.status === "out_for_delivery" && !isHubDriver && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => void updateOrderStatus(order.id, "delivered")}>Mark Delivered</Button>}
                  {order.status !== "delivered" && order.status !== "cancelled" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs gap-1.5 text-orange-600 border-orange-200 hover:bg-orange-50"
                      onClick={() => setExtendEtaOrderId(order.id)}
                    >
                      <Clock size={12} /> Extend time
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Extend ETA modal */}
      {extendEtaOrderId !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setExtendEtaOrderId(null)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-6 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center space-y-1.5">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3" />
              <h3 className="text-xl font-bold text-foreground">Extend Time</h3>
              <p className="text-sm text-slate-500">How much extra time do you need?<br />The customer will be notified immediately.</p>
            </div>
            <div className="space-y-3">
              {[10, 15, 20, 30].map(mins => (
                <button
                  key={mins}
                  onClick={() => void extendEta(extendEtaOrderId, mins)}
                  className="w-full flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold text-lg py-4 rounded-2xl transition-colors"
                >
                  <Clock size={20} /> + {mins} minutes
                </button>
              ))}
            </div>
            <button onClick={() => setExtendEtaOrderId(null)} className="w-full text-slate-400 text-sm py-2 hover:text-slate-600 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Analytics tab */}
      {tab === "analytics" && (
        <div className="space-y-5">
          {/* Period picker */}
          <div className="flex flex-wrap items-center gap-2">
            <TrendingUp size={16} className="text-primary" />
            <span className="font-semibold text-slate-700 text-sm">Period:</span>
            {([7, 14, 30, 90, 0] as const).map((d) => (
              <button
                key={d}
                onClick={() => setAnalyticsDays(d)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${analyticsDays === d ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-primary/40"}`}
              >
                {d === 0 ? "All time" : d === 7 ? "7 days" : d === 14 ? "14 days" : d === 30 ? "30 days" : "90 days"}
              </button>
            ))}
          </div>

          {analyticsLoading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" /></div>
          ) : !analytics ? null : (() => {
            const s = analytics.summary;
            const maxOrders = Math.max(...analytics.daily.map((d) => d.orderCount), 1);
            const maxRevenue = Math.max(...analytics.daily.map((d) => d.revenuePence), 1);
            const statusLabels: Record<string, string> = {
              pending: "Pending", confirmed: "Confirmed", preparing: "Preparing",
              ready: "Ready", out_for_delivery: "Out for delivery", delivered: "Delivered", cancelled: "Cancelled",
            };
            return (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { label: "Total Orders", value: s.totalOrders.toString(), sub: `${s.deliveredOrders} delivered · ${s.cancelledOrders} cancelled`, icon: Package, color: "text-primary" },
                    { label: "Sales (your prices)", value: pence(s.totalSalesPence ?? s.totalRevenuePence), sub: `Avg ${pence(s.avgOrderValuePence)} per order`, icon: TrendingUp, color: "text-emerald-600" },
                    { label: "You keep", value: pence(s.totalKeepPence ?? s.totalSalesPence ?? s.totalRevenuePence), sub: "After fuel & driver fees", icon: TrendingUp, color: "text-emerald-700" },
                    { label: "Customers", value: s.uniqueCustomers.toString(), sub: "Unique customers", icon: Users, color: "text-blue-600" },
                  ].map((card) => (
                    <div key={card.label} className="bg-white rounded-2xl border border-slate-100 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <card.icon size={16} className={card.color} />
                        <span className="text-xs text-slate-500 font-medium">{card.label}</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{card.value}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{card.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Status breakdown */}
                {Object.keys(analytics.statusBreakdown).length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-4">
                    <h3 className="font-semibold text-slate-800 text-sm mb-3">Order status breakdown</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(analytics.statusBreakdown).map(([status, count]) => (
                        <div key={status} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${statusColor(status)} flex items-center gap-1.5`}>
                          <span>{statusLabels[status] ?? status}</span>
                          <span className="opacity-70">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Orders per day chart */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <h3 className="font-semibold text-slate-800 text-sm mb-4">Orders per day</h3>
                  {analytics.daily.every((d) => d.orderCount === 0) ? (
                    <p className="text-slate-400 text-sm text-center py-6">No orders in this period</p>
                  ) : (
                    <div className="flex items-end gap-1 h-28 overflow-x-auto pb-6 relative">
                      {analytics.daily.map((d) => {
                        const h = maxOrders > 0 ? Math.round((d.orderCount / maxOrders) * 88) : 0;
                        const label = new Date(d.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                        return (
                          <div key={d.date} className="flex flex-col items-center gap-1 flex-1 min-w-[28px] group relative">
                            <div className="w-full relative" style={{ height: 88 }}>
                              <div
                                className="absolute bottom-0 left-0.5 right-0.5 bg-primary/80 rounded-t-sm group-hover:bg-primary transition-colors"
                                style={{ height: `${h}px` }}
                              />
                              {d.orderCount > 0 && (
                                <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {d.orderCount} orders<br />{pence(d.revenuePence)}
                                </div>
                              )}
                            </div>
                            {analyticsDays <= 14 && (
                              <span className="text-[10px] text-slate-400 rotate-0 whitespace-nowrap">{label}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Revenue per day chart */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <h3 className="font-semibold text-slate-800 text-sm mb-4">Revenue per day</h3>
                  {analytics.daily.every((d) => d.revenuePence === 0) ? (
                    <p className="text-slate-400 text-sm text-center py-6">No revenue in this period</p>
                  ) : (
                    <div className="flex items-end gap-1 h-28 overflow-x-auto pb-6 relative">
                      {analytics.daily.map((d) => {
                        const h = maxRevenue > 0 ? Math.round((d.revenuePence / maxRevenue) * 88) : 0;
                        const label = new Date(d.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                        return (
                          <div key={d.date} className="flex flex-col items-center gap-1 flex-1 min-w-[28px] group relative">
                            <div className="w-full relative" style={{ height: 88 }}>
                              <div
                                className="absolute bottom-0 left-0.5 right-0.5 bg-emerald-500/80 rounded-t-sm group-hover:bg-emerald-500 transition-colors"
                                style={{ height: `${h}px` }}
                              />
                              {d.revenuePence > 0 && (
                                <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {pence(d.revenuePence)}
                                </div>
                              )}
                            </div>
                            {analyticsDays <= 14 && (
                              <span className="text-[10px] text-slate-400 whitespace-nowrap">{label}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Top customers */}
                {analytics.topCustomers.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users size={15} className="text-blue-600" />
                      <h3 className="font-semibold text-slate-800 text-sm">Top customers</h3>
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left text-xs font-semibold text-slate-500 pb-2">Customer</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Orders</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Total spend</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Last order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.topCustomers.map((c, i) => (
                            <tr key={c.customerId} className={`border-b border-slate-50 ${i === 0 ? "bg-amber-50 dark:bg-amber-900/20/50" : ""}`}>
                              <td className="py-2 pr-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i === 0 ? "bg-amber-400 text-white" : "bg-slate-100 text-slate-500"}`}>
                                    {i + 1}
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">{c.name}</p>
                                    {c.phone && <p className="text-xs text-slate-400">{c.phone}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 text-right font-semibold text-slate-700">{c.orderCount}</td>
                              <td className="py-2 text-right font-bold text-emerald-600">{pence(c.totalSpendPence)}</td>
                              <td className="py-2 text-right text-xs text-slate-400">{new Date(c.lastOrderAt).toLocaleDateString("en-GB")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y divide-slate-50">
                      {analytics.topCustomers.map((c, i) => (
                        <div key={c.customerId} className={`flex items-center justify-between py-3 ${i === 0 ? "-mx-4 px-4 bg-amber-50" : ""}`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i === 0 ? "bg-amber-400 text-white" : "bg-slate-100 text-slate-500"}`}>
                              {i + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{c.name}</p>
                              {c.phone && <p className="text-xs text-slate-400">{c.phone}</p>}
                            </div>
                          </div>
                          <div className="text-right ml-3">
                            <p className="font-bold text-emerald-600">{pence(c.totalSpendPence)}</p>
                            <p className="text-xs text-slate-400">{c.orderCount} orders · {new Date(c.lastOrderAt).toLocaleDateString("en-GB")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All transactions */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800 text-sm">All transactions</h3>
                    <span className="text-xs text-slate-400">{analytics.transactions.length} orders</span>
                  </div>
                  {analytics.transactions.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-6">No transactions in this period</p>
                  ) : (
                    <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left text-xs font-semibold text-slate-500 pb-2">Order</th>
                            <th className="text-left text-xs font-semibold text-slate-500 pb-2">Customer</th>
                            <th className="text-left text-xs font-semibold text-slate-500 pb-2">Items</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Total</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Status</th>
                            <th className="text-right text-xs font-semibold text-slate-500 pb-2">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.transactions.map((tx) => (
                            <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                              <td className="py-2 pr-3 font-mono text-xs text-slate-500">#{tx.id}</td>
                              <td className="py-2 pr-3">
                                <p className="font-medium text-slate-800">{tx.customerName ?? "Guest"}</p>
                                {tx.customerPhone && <p className="text-xs text-slate-400">{tx.customerPhone}</p>}
                              </td>
                              <td className="py-2 pr-3 text-xs text-slate-500 max-w-[180px]">
                                {tx.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                              </td>
                              <td className="py-2 pr-3 text-right font-bold text-primary">{pence(tx.totalPence)}</td>
                              <td className="py-2 pr-3 text-right">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColor(tx.status)}`}>{formatStatus(tx.status)}</span>
                              </td>
                              <td className="py-2 text-right text-xs text-slate-400 whitespace-nowrap">
                                {new Date(tx.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                <br />
                                <span className="text-slate-300">{new Date(tx.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y divide-slate-50">
                      {analytics.transactions.map((tx) => (
                        <div key={tx.id} className="flex items-start justify-between py-3 gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="font-mono text-xs text-slate-400">#{tx.id}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${statusColor(tx.status)}`}>{formatStatus(tx.status)}</span>
                            </div>
                            <p className="font-medium text-slate-800 text-sm">{tx.customerName ?? "Guest"}</p>
                            <p className="text-xs text-slate-400 truncate">{tx.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-primary">{pence(tx.totalPence)}</p>
                            <p className="text-xs text-slate-400">{new Date(tx.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Abandoned Baskets tab */}
      {tab === "abandoned" && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Abandoned Baskets</h2>
            <p className="text-sm text-slate-500 mt-1">Customers who had items in their basket when your store was paused. Baskets are captured the moment a checkout attempt is blocked.</p>
          </div>
          {abandonedLoading ? (
            <div className="flex items-center justify-center py-12"><LoaderCircle className="animate-spin text-slate-400" size={28} /></div>
          ) : abandonedBaskets.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <ShoppingCart size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No abandoned baskets recorded</p>
              <p className="text-slate-400 text-sm mt-1">When a customer tries to order while your store is paused, their basket is saved here.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
                  <div className="text-2xl font-bold text-slate-800">{abandonedBaskets.length}</div>
                  <div className="text-xs text-slate-500 mt-1">Baskets captured</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    £{(abandonedBaskets.reduce((s, b) => s + (b.estimatedSubtotalPence ?? 0), 0) / 100).toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Est. lost subtotal</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
                  <div className="text-2xl font-bold text-slate-800">
                    £{abandonedBaskets.length > 0 ? (abandonedBaskets.reduce((s, b) => s + (b.estimatedSubtotalPence ?? 0), 0) / abandonedBaskets.length / 100).toFixed(2) : "0.00"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Avg basket value</div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                {/* Desktop table */}
                <table className="hidden sm:table w-full text-sm">
                  <thead><tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Time</th>
                    <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Customer</th>
                    <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Items</th>
                    <th className="text-right text-xs font-semibold text-slate-500 px-4 py-3">Est. Value</th>
                  </tr></thead>
                  <tbody>
                    {abandonedBaskets.map((b) => (
                      <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {new Date(b.abandonedAt).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{b.customerName ?? "Customer"}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs">
                          {Array.isArray(b.items) ? b.items.map((i: { name: string; quantity: number }) => `${i.quantity}× ${i.name}`).join(", ") : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">
                          {b.estimatedSubtotalPence ? `£${(b.estimatedSubtotalPence / 100).toFixed(2)}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile cards */}
                <div className="sm:hidden divide-y divide-slate-50">
                  {abandonedBaskets.map((b) => (
                    <div key={b.id} className="flex items-start justify-between px-4 py-3 gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 mb-0.5">{new Date(b.abandonedAt).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</p>
                        <p className="font-medium text-slate-700 text-sm">{b.customerName ?? "Customer"}</p>
                        <p className="text-xs text-slate-500 truncate">{Array.isArray(b.items) ? b.items.map((i: { name: string; quantity: number }) => `${i.quantity}× ${i.name}`).join(", ") : "—"}</p>
                      </div>
                      <p className="font-semibold text-red-600 flex-shrink-0">{b.estimatedSubtotalPence ? `£${(b.estimatedSubtotalPence / 100).toFixed(2)}` : "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Offers tab */}
      {tab === "offers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-foreground">Store Offers</h3>
              <p className="text-sm text-slate-500 mt-0.5">Discount codes and auto-applied deals shown to customers</p>
            </div>
            <Button size="sm" className="bg-primary hover:bg-primary/90 gap-1" onClick={startAddOffer}>
              <Plus size={14} /> Add Offer
            </Button>
          </div>

          {addOffer && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
              <h4 className="font-semibold text-foreground">{editingOfferId ? "Edit Offer" : "New Offer"}</h4>
              <Input placeholder="Title * (e.g. 20% off your first order)" value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} />
              <Input placeholder="Description (optional)" value={offerForm.description} onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} />
              <div className="flex gap-3 flex-wrap">
                <div>
                  <label className="text-xs font-medium text-slate-500">Type</label>
                  <select value={offerForm.type} onChange={(e) => setOfferForm({ ...offerForm, type: e.target.value })}
                    className="block w-40 border border-input rounded-md px-2 py-1.5 text-sm bg-background mt-1">
                    <option value="percentage">% off total</option>
                    <option value="fixed">£ off total</option>
                    <option value="free_delivery">Free delivery</option>
                    <option value="free_item">🎁 Free item</option>
                    <option value="bogof">🍕 Buy X, Get Cheapest Free</option>
                  </select>
                </div>
                {offerForm.type === "free_item" ? (
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Free item mode</label>
                      <select value={offerForm.freeItemMode}
                        onChange={(e) => setOfferForm({ ...offerForm, freeItemMode: e.target.value, freeItemMenuItemId: 0, freeItemCategoryId: 0, freeItemPoolItemIds: [], freeItemOptionVariant: "" })}
                        className="block w-full border border-input rounded-md px-2 py-1.5 text-sm bg-background mt-1">
                        <option value="single">Single item (one fixed item is free)</option>
                        <option value="category">Category (customer picks from a whole category)</option>
                        <option value="pool">Pick from list (customer picks from specific items)</option>
                      </select>
                    </div>
                    {offerForm.freeItemMode === "single" && (
                      <div>
                        <label className="text-xs font-medium text-slate-500">Which item is free?</label>
                        <div className="mt-1">
                          <ItemPicker
                            items={mmAllItems}
                            sections={sections}
                            optionSummary={mmItemOptions}
                            value={offerForm.freeItemMenuItemId}
                            onChange={(id) => setOfferForm({ ...offerForm, freeItemMenuItemId: id, freeItemOptionVariant: "" })}
                            loading={mmAllItemsLoading || mmAllItems.length === 0}
                          />
                        </div>
                        {offerForm.freeItemMenuItemId !== 0 && (mmItemOptions[offerForm.freeItemMenuItemId] ?? []).length > 0 && (
                          <div className="mt-2">
                            <label className="text-xs font-medium text-slate-500">Which size / variant? <span className="font-normal text-slate-400">(optional — leave blank to let customer choose)</span></label>
                            <select
                              value={offerForm.freeItemOptionVariant}
                              onChange={(e) => setOfferForm({ ...offerForm, freeItemOptionVariant: e.target.value })}
                              className="block w-full border border-input rounded-md px-2 py-1.5 text-sm bg-background mt-1"
                            >
                              <option value="">Any variant — customer chooses</option>
                              {(mmItemOptions[offerForm.freeItemMenuItemId] ?? []).flatMap(g =>
                                g.choices.map(c => (
                                  <option key={c.id} value={c.name}>{g.groupName}: {c.name}</option>
                                ))
                              )}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                    {offerForm.freeItemMode === "category" && (
                      <div>
                        <label className="text-xs font-medium text-slate-500">Which category? (customer picks any item from it)</label>
                        <select value={offerForm.freeItemCategoryId || ""}
                          onChange={(e) => setOfferForm({ ...offerForm, freeItemCategoryId: Number(e.target.value) })}
                          className="block w-full border border-input rounded-md px-2 py-1.5 text-sm bg-background mt-1">
                          <option value="">— select category —</option>
                          {sections.map((s: { id: number; name: string }) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {offerForm.freeItemMode === "pool" && (
                      <div>
                        <label className="text-xs font-medium text-slate-500">Which items can the customer choose from?</label>
                        <div className="mt-1 border border-input rounded-md flex items-center px-2 gap-1 bg-background mb-2 focus-within:ring-1 focus-within:ring-ring">
                          <Search size={13} className="text-slate-400 shrink-0" />
                          <input type="text" placeholder="Search items…" value={poolSearch}
                            onChange={e => setPoolSearch(e.target.value)}
                            className="flex-1 py-1.5 text-sm bg-transparent outline-none placeholder:text-slate-400" />
                          {poolSearch && <button type="button" onClick={() => setPoolSearch("")} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>}
                        </div>
                        {offerForm.freeItemPoolItemIds.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {offerForm.freeItemPoolItemIds.map(id => {
                              const item = mmAllItems.find(i => i.id === id);
                              if (!item) return null;
                              return (
                                <button key={id} type="button"
                                  onClick={() => setOfferForm(f => ({ ...f, freeItemPoolItemIds: f.freeItemPoolItemIds.filter(x => x !== id) }))}
                                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 border border-teal-300 text-teal-700 flex items-center gap-1">
                                  {item.name} <X size={10} />
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <div className="border border-input rounded-md max-h-48 overflow-y-auto">
                          {sections.map(section => {
                            const sectionItems = mmAllItems.filter(it =>
                              it.sectionId === section.id &&
                              !offerForm.freeItemPoolItemIds.includes(it.id) &&
                              (!poolSearch || it.name.toLowerCase().includes(poolSearch.toLowerCase()))
                            );
                            if (sectionItems.length === 0) return null;
                            return (
                              <div key={section.id}>
                                <div className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 border-b border-slate-100 sticky top-0">{section.name}</div>
                                {sectionItems.map(it => {
                                  const opts = mmItemOptions[it.id] ?? [];
                                  return (
                                    <button key={it.id} type="button"
                                      onClick={() => setOfferForm(f => ({ ...f, freeItemPoolItemIds: [...f.freeItemPoolItemIds, it.id] }))}
                                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors">
                                      <div className="text-sm font-medium text-slate-800">{it.name}</div>
                                      {opts.map(g => (
                                        <div key={g.groupName} className="text-xs text-slate-400 mt-0.5">{g.groupName}: {g.choices.join(" · ")}</div>
                                      ))}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                          {mmAllItems.filter(it => !offerForm.freeItemPoolItemIds.includes(it.id) && (!poolSearch || it.name.toLowerCase().includes(poolSearch.toLowerCase()))).length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-400">No items found</div>
                          )}
                        </div>
                        {offerForm.freeItemPoolItemIds.length === 0 && <p className="text-xs text-amber-600 mt-1">Select at least one item</p>}
                      </div>
                    )}
                    {offerForm.freeItemMode !== "single" && (
                      <div>
                        <label className="text-xs font-medium text-slate-500">How many items can the customer pick for free?</label>
                        <Input type="number" min={1} step={1}
                          value={offerForm.freeItemPickCount || 1}
                          onChange={(e) => setOfferForm({ ...offerForm, freeItemPickCount: Math.max(1, Number(e.target.value)) })}
                          className="w-20 mt-1" />
                      </div>
                    )}
                  </div>
                ) : offerForm.type !== "free_delivery" && offerForm.type !== "bogof" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">{offerForm.type === "percentage" ? "% off" : "£ off"}</label>
                    <Input type="number" min={0} step={offerForm.type === "percentage" ? 1 : 0.01}
                      value={offerForm.value / (offerForm.type === "fixed" ? 100 : 1) || ""}
                      onChange={(e) => setOfferForm({ ...offerForm, value: Math.round(Number(e.target.value) * (offerForm.type === "fixed" ? 100 : 1)) })}
                      className="w-24 mt-1" />
                  </div>
                )}
                {offerForm.type !== "bogof" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">Min order (£)</label>
                    <Input type="number" min={0} step={0.01}
                      value={offerForm.minOrderPence / 100 || ""}
                      onChange={(e) => setOfferForm({ ...offerForm, minOrderPence: Math.round(Number(e.target.value) * 100) })}
                      className="w-24 mt-1" />
                  </div>
                )}
              </div>

              {/* BOGOF unified panel */}
              {offerForm.type === "bogof" && (
                <div className="border border-teal-200 rounded-xl bg-teal-50/40 p-4 space-y-4">
                  {/* Sentence-style qty builder */}
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-2">Deal structure</p>
                    <div className="flex items-center gap-2 flex-wrap text-sm font-medium text-slate-700">
                      <span>Buy</span>
                      <Input type="number" min={1} step={1} value={offerForm.bogofBuyQty || 2}
                        onChange={(e) => setOfferForm((f) => ({ ...f, bogofBuyQty: Math.max(1, Number(e.target.value)) }))}
                        className="w-16 text-center font-bold" />
                      <span>get</span>
                      <Input type="number" min={1} step={1} value={offerForm.bogofFreeQty || 1}
                        onChange={(e) => setOfferForm((f) => ({ ...f, bogofFreeQty: Math.max(1, Number(e.target.value)) }))}
                        className="w-16 text-center font-bold" />
                      <span>cheapest free</span>
                    </div>
                    {/* Live preview */}
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-teal-100 border border-teal-300 text-teal-800 text-xs font-medium px-3 py-1 rounded-full">
                      <span>
                        {offerForm.bogofStack
                          ? `Every ${offerForm.bogofBuyQty + offerForm.bogofFreeQty} items → ${offerForm.bogofFreeQty} free (repeats)`
                          : `First ${offerForm.bogofBuyQty + offerForm.bogofFreeQty} items → ${offerForm.bogofFreeQty} free (once per basket)`}
                        {offerForm.activeDays.length > 0 && ` · ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].filter((_,i) => offerForm.activeDays.includes(i)).join(", ")} only`}
                        {offerForm.includedSectionIds.length > 0 && ` · ${sections.filter((s: {id:number;name:string}) => offerForm.includedSectionIds.includes(s.id)).map((s: {id:number;name:string}) => s.name).join(", ")} only`}
                        {offerForm.includedItemIds.length > 0 && ` · ${offerForm.includedItemIds.length} specific item${offerForm.includedItemIds.length !== 1 ? "s" : ""} only`}
                      </span>
                    </div>
                  </div>

                  {/* Repeats toggle */}
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <div
                      onClick={() => setOfferForm((f) => ({ ...f, bogofStack: !f.bogofStack }))}
                      className={`relative w-10 h-5 rounded-full transition-colors ${offerForm.bogofStack ? "bg-teal-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${offerForm.bogofStack ? "translate-x-5" : "translate-x-0"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Repeats through the basket</p>
                      <p className="text-xs text-slate-500">
                        {offerForm.bogofStack
                          ? `On — e.g. buy ${offerForm.bogofBuyQty * 2} items → ${offerForm.bogofFreeQty * 2} are free`
                          : `Off — deal applies once per order regardless of quantity`}
                      </p>
                    </div>
                  </label>

                  {/* Qualifying scope — categories OR specific items */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-600 mb-1">
                        Which items qualify?
                        <span className="font-normal text-slate-400"> (leave everything unselected = whole menu)</span>
                      </p>

                      {/* Category pills */}
                      <p className="text-xs text-slate-500 mb-1">By category</p>
                      <div className="flex flex-wrap gap-2">
                        {sections.map((s: { id: number; name: string }) => {
                          const on = offerForm.includedSectionIds.includes(s.id);
                          return (
                            <button key={s.id} type="button"
                              onClick={() => setOfferForm((f) => ({ ...f, includedSectionIds: on ? f.includedSectionIds.filter((x) => x !== s.id) : [...f.includedSectionIds, s.id] }))}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${on ? "bg-teal-500 border-teal-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-teal-300"}`}>
                              {on ? "✓ " : ""}{s.name}
                            </button>
                          );
                        })}
                      </div>

                      {/* Specific items picker */}
                      <p className="text-xs text-slate-500 mt-3 mb-1">Or specific items</p>
                      {offerForm.includedItemIds.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {offerForm.includedItemIds.map((id: number) => {
                            const item = mmAllItems.find((i: MenuItem) => i.id === id);
                            if (!item) return null;
                            return (
                              <button key={id} type="button"
                                onClick={() => setOfferForm((f) => ({ ...f, includedItemIds: f.includedItemIds.filter((x) => x !== id) }))}
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-500 text-white flex items-center gap-1 hover:bg-teal-600 transition-colors">
                                {item.name} <X size={10} />
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="border border-input rounded-md max-h-44 overflow-y-auto bg-white">
                        {sections.map((section: { id: number; name: string }) => {
                          const sectionItems = mmAllItems.filter((it: MenuItem) =>
                            it.sectionId === section.id &&
                            !offerForm.includedItemIds.includes(it.id) &&
                            (!poolSearch || it.name.toLowerCase().includes(poolSearch.toLowerCase()))
                          );
                          if (sectionItems.length === 0) return null;
                          return (
                            <div key={section.id}>
                              <div className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 border-b border-slate-100 sticky top-0">{section.name}</div>
                              {sectionItems.map((it: MenuItem) => (
                                <button key={it.id} type="button"
                                  onClick={() => setOfferForm((f) => ({ ...f, includedItemIds: [...f.includedItemIds, it.id] }))}
                                  className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-slate-50 last:border-0 transition-colors text-sm text-slate-700">
                                  + {it.name}
                                </button>
                              ))}
                            </div>
                          );
                        })}
                        {mmAllItems.filter((it: MenuItem) =>
                          !offerForm.includedItemIds.includes(it.id) &&
                          (!poolSearch || it.name.toLowerCase().includes(poolSearch.toLowerCase()))
                        ).length === 0 && <div className="px-3 py-2 text-xs text-slate-400">All items added</div>}
                      </div>
                      <div className="mt-1.5 border border-input rounded-md flex items-center px-2 gap-1 bg-white focus-within:ring-1 focus-within:ring-ring">
                        <Search size={13} className="text-slate-400 shrink-0" />
                        <input type="text" placeholder="Search items…" value={poolSearch}
                          onChange={e => setPoolSearch(e.target.value)}
                          className="flex-1 py-1.5 text-xs bg-transparent outline-none placeholder:text-slate-400" />
                        {poolSearch && <button type="button" onClick={() => setPoolSearch("")} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>}
                      </div>

                      {offerForm.includedSectionIds.length === 0 && offerForm.includedItemIds.length === 0 && (
                        <p className="text-xs text-slate-400 mt-1">All menu items qualify.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Exclude controls for non-BOGOF types */}
              {["percentage", "fixed"].includes(offerForm.type) && (
                <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-600">Exclude from discount (optional)</p>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Exclude categories</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {sections.map((s: { id: number; name: string }) => {
                        const on = offerForm.excludedSectionIds.includes(s.id);
                        return (
                          <button key={s.id} type="button"
                            onClick={() => setOfferForm((f) => ({ ...f, excludedSectionIds: on ? f.excludedSectionIds.filter((x) => x !== s.id) : [...f.excludedSectionIds, s.id] }))}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-red-100 border-red-300 text-red-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                            {on ? "✕ " : ""}{s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Exclude specific items</label>
                    <div className="flex flex-wrap gap-2 mt-1 max-h-36 overflow-y-auto pr-1">
                      {mmAllItems.filter((it: MenuItem) => !offerForm.excludedSectionIds.includes(it.sectionId)).map((it: MenuItem) => {
                        const on = offerForm.excludedItemIds.includes(it.id);
                        return (
                          <button key={it.id} type="button"
                            onClick={() => setOfferForm((f) => ({ ...f, excludedItemIds: on ? f.excludedItemIds.filter((x) => x !== it.id) : [...f.excludedItemIds, it.id] }))}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-red-100 border-red-300 text-red-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                            {on ? "✕ " : ""}{it.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {(offerForm.excludedItemIds.length > 0 || offerForm.excludedSectionIds.length > 0) && (
                    <p className="text-xs text-amber-600">Discount applies to all other items only.</p>
                  )}
                </div>
              )}

              {/* Exclude a specific OPTION (e.g. a "16 inch" pizza size) — works for % / fixed / BOGOF */}
              {["percentage", "fixed", "bogof"].includes(offerForm.type) && optionNames.length > 0 && (
                <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-600">Exclude an option from this deal (optional)</p>
                  <p className="text-xs text-slate-400">Tick a size/option (e.g. 16&quot;). Any basket line with that option won&apos;t get this offer, and customers see it marked on that option.</p>
                  <div className="flex flex-wrap gap-2 mt-1 max-h-40 overflow-y-auto pr-1">
                    {optionNames.map((o) => {
                      const on = o.optionIds.length > 0 && o.optionIds.every((id) => offerForm.excludedOptionIds.includes(id));
                      return (
                        <button key={o.name} type="button"
                          onClick={() => setOfferForm((f) => ({ ...f, excludedOptionIds: on ? f.excludedOptionIds.filter((x) => !o.optionIds.includes(x)) : Array.from(new Set([...f.excludedOptionIds, ...o.optionIds])) }))}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-red-100 border-red-300 text-red-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                          {on ? "✕ " : ""}{o.name}
                        </button>
                      );
                    })}
                  </div>
                  {offerForm.excludedOptionIds.length > 0 && (
                    <p className="text-xs text-amber-600">Lines with an excluded option won&apos;t receive this deal.</p>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-500">Active days (leave all off = runs every day)</label>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {[["Mon",1],["Tue",2],["Wed",3],["Thu",4],["Fri",5],["Sat",6],["Sun",0]].map(([label, day]) => {
                    const d = day as number;
                    const on = offerForm.activeDays.includes(d);
                    return (
                      <button key={d} type="button"
                        onClick={() => setOfferForm((f) => ({ ...f, activeDays: on ? f.activeDays.filter((x) => x !== d) : [...f.activeDays, d] }))}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-teal-100 border-teal-300 text-teal-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                        {label as string}
                      </button>
                    );
                  })}
                </div>
                {offerForm.activeDays.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">Offer only active on selected days.</p>
                )}
              </div>
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-36">
                  <label className="text-xs font-medium text-slate-500">Promo code (optional — leave blank to auto-apply)</label>
                  <Input placeholder="e.g. WELCOME20" value={offerForm.code} onChange={(e) => setOfferForm({ ...offerForm, code: e.target.value.toUpperCase() })} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Valid until (optional)</label>
                  <Input type="date" value={offerForm.validUntil} onChange={(e) => { setOfferExpiryChanged(true); setOfferForm({ ...offerForm, validUntil: e.target.value }); }} className="mt-1" />
                  <p className="mt-1 text-xs text-muted-foreground">A selected date includes that whole day, in UK time.</p>
                </div>
              </div>
              <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={offerForm.carouselFeatured} onChange={(e) => setOfferForm({ ...offerForm, carouselFeatured: e.target.checked })} />
                  {"\uD83D\uDD25"} Feature on the Ayrshire Hub homepage &ldquo;Today&rsquo;s Deals&rdquo; carousel
                </label>
                {offerForm.carouselFeatured && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-slate-500">Deal photo (optional &mdash; your store banner is used if left blank)</label>
                    <div className="mt-1">
                      <ImageUpload value={offerForm.imageUrl} onChange={(url) => setOfferForm({ ...offerForm, imageUrl: url ?? "" })} stockCategory="deal" />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => void saveOffer()} disabled={offerSaving || !offerForm.title.trim() || (offerForm.type === "free_item" && offerForm.freeItemMode === "single" && !offerForm.freeItemMenuItemId) || (offerForm.type === "free_item" && offerForm.freeItemMode === "category" && !offerForm.freeItemCategoryId) || (offerForm.type === "free_item" && offerForm.freeItemMode === "pool" && offerForm.freeItemPoolItemIds.length === 0) || (offerForm.type === "bogof" && offerForm.bogofBuyQty < 1)}>{offerSaving ? "Saving..." : editingOfferId ? "Save Changes" : "Save Offer"}</Button>
                <Button size="sm" variant="outline" onClick={closeOfferForm} disabled={offerSaving}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Mix & Match Deals */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-foreground">Mix &amp; Match Deals</h3>
                <p className="text-xs text-slate-500 mt-0.5">Let customers pick any N items from a selection for one bundle price — e.g. "Any 3 cans for £2.00"</p>
              </div>
              <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setAddMixMatch(true)}>
                <Plus size={12} /> New deal
              </Button>
            </div>
            {addMixMatch && (
              <div className="border border-teal-100 rounded-lg p-3 space-y-2 bg-teal-50/40">
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">New mix &amp; match deal</p>
                <Input placeholder='Name visible to customers (e.g. "Any 3 cans")' value={mmForm.name} onChange={(e) => setMmForm({ ...mmForm, name: e.target.value })} />
                <div className="flex gap-3 flex-wrap items-center">
                  <span className="text-sm font-medium text-slate-600">Any</span>
                  <Input type="number" min={2} step={1} placeholder="qty" value={mmForm.qty || ""} onChange={(e) => setMmForm({ ...mmForm, qty: Number(e.target.value) })} className="w-20" />
                  <span className="text-sm font-medium text-slate-600">for £</span>
                  <Input type="number" min={0} step={0.01} placeholder="price" value={mmForm.pricePence / 100 || ""} onChange={(e) => setMmForm({ ...mmForm, pricePence: Math.round(Number(e.target.value) * 100) })} className="w-24" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => void createMixMatchDeal()} disabled={!mmForm.name.trim() || !mmForm.qty || !mmForm.pricePence}>Create</Button>
                  <Button size="sm" variant="outline" onClick={() => setAddMixMatch(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {mixMatchDeals.length === 0 && !addMixMatch && (
              <p className="text-sm text-slate-400 text-center py-2">No mix &amp; match deals yet — create one above</p>
            )}
            {mixMatchDeals.map((deal) => (
              <div key={deal.id} className="border border-slate-100 rounded-xl p-4 space-y-3">
                {editingMmId === deal.id ? (
                  <div className="space-y-2">
                    <Input
                      placeholder='Name visible to customers (e.g. "Any 3 cans")'
                      value={mmEditForm.name}
                      onChange={(e) => setMmEditForm({ ...mmEditForm, name: e.target.value })}
                    />
                    <div className="flex gap-3 flex-wrap items-center">
                      <span className="text-sm font-medium text-slate-600">Any</span>
                      <Input type="number" min={2} step={1} placeholder="qty" value={mmEditForm.qty || ""} onChange={(e) => setMmEditForm({ ...mmEditForm, qty: Number(e.target.value) })} className="w-20" />
                      <span className="text-sm font-medium text-slate-600">for £</span>
                      <Input type="number" min={0} step={0.01} placeholder="price" value={mmEditForm.pricePence / 100 || ""} onChange={(e) => setMmEditForm({ ...mmEditForm, pricePence: Math.round(Number(e.target.value) * 100) })} className="w-24" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => void saveMixMatchDeal(deal.id)} disabled={!mmEditForm.name.trim() || !mmEditForm.qty || !mmEditForm.pricePence}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingMmId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{deal.name}</span>
                      <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded">
                        Any {deal.qty} for £{(deal.pricePence / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2">
                      <button
                        onClick={() => { setMmEditForm({ name: deal.name, qty: deal.qty, pricePence: deal.pricePence }); setEditingMmId(deal.id); }}
                        className="text-slate-300 hover:text-teal-500 transition-colors"
                        title="Edit deal"
                      >
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => void deleteMixMatchDeal(deal.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500">Eligible items ({deal.itemIds.length} selected) — customers can pick any combination of these</p>
                  <div className="flex flex-wrap gap-2">
                    {mmAllItems.filter((item) => deal.itemIds.includes(item.id)).map((item) => (
                      <span key={item.id} className="flex items-center gap-1 bg-teal-50 text-teal-700 text-xs font-medium px-2 py-1 rounded-lg border border-teal-100">
                        {item.name}
                        <button onClick={() => void removeItemFromMixMatch(deal.id, item.id)} className="ml-0.5 text-teal-400 hover:text-red-500"><X size={10} /></button>
                      </span>
                    ))}
                    {mmAssigning === deal.id ? (
                      <div className="relative">
                        <div className="flex items-center gap-1 border border-primary/40 rounded-lg px-2 py-1 bg-white">
                          <Search size={12} className="text-slate-400 flex-shrink-0" />
                          <input
                            type="text"
                            autoFocus
                            value={mmSearch}
                            onChange={(e) => setMmSearch(e.target.value)}
                            placeholder="Search items…"
                            className="text-xs bg-transparent outline-none w-36 text-slate-700 placeholder:text-slate-400"
                            onKeyDown={(e) => { if (e.key === "Escape") { setMmAssigning(null); setMmSearch(""); } }}
                            onBlur={() => { setTimeout(() => { setMmAssigning(null); setMmSearch(""); }, 150); }}
                          />
                        </div>
                        <div className="absolute top-full left-0 z-20 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                          <div className="max-h-52 overflow-y-auto">
                            {mmAllItemsLoading && <p className="px-3 py-2 text-xs text-slate-400 flex items-center gap-1"><LoaderCircle size={12} className="animate-spin" /> Loading items…</p>}
                            {!mmAllItemsLoading && mmAllItems.filter((item) => !deal.itemIds.includes(item.id) && (!mmSearch || item.name.toLowerCase().includes(mmSearch.toLowerCase()))).slice(0, 40).map((item) => (
                              <button
                                key={item.id}
                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between gap-2"
                                onMouseDown={(e) => { e.preventDefault(); void addItemToMixMatch(deal.id, item.id); setMmAssigning(null); setMmSearch(""); }}
                              >
                                <span className="truncate">{item.name}</span>
                                <span className="text-slate-400 flex-shrink-0">{pence(item.pricePence)}</span>
                              </button>
                            ))}
                            {!mmAllItemsLoading && mmAllItems.filter((item) => !deal.itemIds.includes(item.id) && (!mmSearch || item.name.toLowerCase().includes(mmSearch.toLowerCase()))).length === 0 && (
                              <p className="px-3 py-2 text-xs text-slate-400">No items found</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setMmAssigning(deal.id); void loadAllItemsForPickers(); }}
                        className="text-xs border border-dashed border-teal-300 rounded-lg px-2 py-1 text-teal-600 hover:bg-teal-50 transition-colors"
                      >
                        + Add item
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {offers.length === 0 && !addOffer ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400">
              <Tag size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No offers yet — add a discount code or deal to attract customers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {offers.map((offer) => (
                <div key={offer.id} className={`bg-white rounded-2xl border p-5 flex items-start gap-4 ${offer.isActive ? "border-slate-100" : "border-slate-100 opacity-60"}`}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Tag size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground">{offer.title}</p>
                      {offer.code && <span className="bg-slate-100 text-slate-700 text-xs font-mono font-bold px-2 py-0.5 rounded">{offer.code}</span>}
                    </div>
                    {offer.description && <p className="text-xs text-slate-500 mt-0.5">{offer.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span>
                        {offer.type === "percentage" ? `${offer.value}% off` : offer.type === "fixed" ? `£${(offer.value / 100).toFixed(2)} off` : offer.type === "free_item" ? `🎁 Free ${offer.freeItemMode === "category" ? "item from category" : offer.freeItemMode === "pool" ? `item (pick ${offer.freeItemPickCount})` : offer.freeItemOptionVariant ? `item (${offer.freeItemOptionVariant})` : "item"} (spend over £${(offer.minOrderPence / 100).toFixed(2)})` : offer.type === "bogof" ? `Buy ${(offer as any).bogofBuyQty ?? 2} get ${(offer as any).bogofFreeQty ?? 1} cheapest free${(offer.activeDays?.length ?? 0) > 0 ? ` (${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].filter((_,i) => offer.activeDays?.includes(i)).join(",")} only)` : ""}` : "Free delivery"}
                      </span>
                      {offer.minOrderPence > 0 && <span>Min order {pence(offer.minOrderPence)}</span>}
                      {!offer.code && <span className="text-emerald-600 font-medium">Auto-applied</span>}
                      {offer.validUntil && <span>Until {new Date(offer.validUntil).toLocaleDateString("en-GB")}</span>}
                    </div>
                    {((offer.excludedSectionIds?.length ?? 0) > 0 || (offer.excludedItemIds?.length ?? 0) > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {(offer.excludedSectionIds ?? []).map((sid) => {
                          const s = sections.find((x: { id: number; name: string }) => x.id === sid);
                          return s ? <span key={sid} className="text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5">Excl. {s.name}</span> : null;
                        })}
                        {(offer.excludedItemIds ?? []).map((iid) => {
                          const it = items.find((x: MenuItem) => x.id === iid);
                          return it ? <span key={iid} className="text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5">Excl. {it.name}</span> : null;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => startEditOffer(offer)} className="text-slate-300 hover:text-primary transition-colors" title={`Edit ${offer.title}`} aria-label={`Edit ${offer.title}`}><Pencil size={14} /></button>
                    <button onClick={() => void toggleOffer(offer)}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${offer.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {offer.isActive ? "Active" : "Paused"}
                    </button>
                    <button onClick={() => void deleteOffer(offer.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cart Recovery section */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-foreground">Cart Recovery</h3>
                <p className="text-sm text-slate-500 mt-0.5">Automatically contact customers who abandon their basket</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm font-medium text-slate-600">{recoveryConfig.enabled ? "Enabled" : "Disabled"}</span>
                <button
                  type="button"
                  onClick={() => setRecoveryConfig((c) => ({ ...c, enabled: !c.enabled }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${recoveryConfig.enabled ? "bg-primary" : "bg-slate-200"}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${recoveryConfig.enabled ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </label>
            </div>

            {recoveryLoading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Send after inactivity</label>
                    <select
                      value={recoveryConfig.delayMinutes}
                      onChange={(e) => setRecoveryConfig((c) => ({ ...c, delayMinutes: Number(e.target.value) }))}
                      className="w-full border border-input rounded-md px-2 py-1.5 text-sm bg-background"
                    >
                      {[15, 20, 30, 45, 60, 90, 120].map((m) => (
                        <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Channels</label>
                    <div className="flex gap-3 flex-wrap pt-1">
                      {(["channelPush", "channelSms", "channelEmail"] as const).map((ch) => (
                        <label key={ch} className="flex items-center gap-1.5 cursor-pointer text-sm">
                          <input type="checkbox" checked={recoveryConfig[ch]}
                            onChange={(e) => setRecoveryConfig((c) => ({ ...c, [ch]: e.target.checked }))}
                            className="accent-primary" />
                          {ch === "channelPush" ? "Push" : ch === "channelSms" ? "SMS" : "Email"}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Value-tier rules */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-slate-500">Incentive tiers</label>
                    <button
                      type="button"
                      onClick={() => setRecoveryConfig((c) => ({ ...c, rules: [...c.rules, { minBasketPence: 0, maxBasketPence: null, offerType: "reminder", offerValue: null }] }))}
                      className="text-xs text-primary font-medium hover:underline"
                    >+ Add tier</button>
                  </div>
                  {recoveryConfig.rules.length === 0 && (
                    <p className="text-xs text-slate-400 italic">No tiers yet — add one to send a discount or free item with the recovery message.</p>
                  )}
                  <div className="space-y-2">
                    {recoveryConfig.rules.map((rule, idx) => (
                      <div key={idx} className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">Basket ≥</span>
                          <MoneyInput value={rule.minBasketPence} onChange={(v) => setRecoveryConfig((c) => ({ ...c, rules: c.rules.map((r, i) => i === idx ? { ...r, minBasketPence: v } : r) }))} className="w-20 text-sm" />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">up to</span>
                          <MoneyInput value={rule.maxBasketPence ?? 0} onChange={(v) => setRecoveryConfig((c) => ({ ...c, rules: c.rules.map((r, i) => i === idx ? { ...r, maxBasketPence: v > 0 ? v : null } : r) }))} className="w-20 text-sm" placeholder="No limit" />
                        </div>
                        <select value={rule.offerType}
                          onChange={(e) => setRecoveryConfig((c) => ({ ...c, rules: c.rules.map((r, i) => i === idx ? { ...r, offerType: e.target.value, offerValue: null } : r) }))}
                          className="border border-input rounded-md px-2 py-1 text-sm bg-background">
                          <option value="reminder">Reminder only</option>
                          <option value="percentage">% discount</option>
                          <option value="fixed">£ discount</option>
                          <option value="free_delivery">Free delivery</option>
                        </select>
                        {(rule.offerType === "percentage" || rule.offerType === "fixed") && (
                          <div className="flex items-center gap-1">
                            {rule.offerType === "percentage" ? (
                              <input type="number" min={1} max={100} value={rule.offerValue ?? ""} placeholder="10"
                                onChange={(e) => setRecoveryConfig((c) => ({ ...c, rules: c.rules.map((r, i) => i === idx ? { ...r, offerValue: Number(e.target.value) } : r) }))}
                                className="w-16 border border-input rounded-md px-2 py-1 text-sm bg-background" />
                            ) : (
                              <MoneyInput value={rule.offerValue ?? 0} onChange={(v) => setRecoveryConfig((c) => ({ ...c, rules: c.rules.map((r, i) => i === idx ? { ...r, offerValue: v } : r) }))} className="w-20 text-sm" />
                            )}
                            <span className="text-xs text-slate-500">{rule.offerType === "percentage" ? "%" : "off"}</span>
                          </div>
                        )}
                        <button type="button" onClick={() => setRecoveryConfig((c) => ({ ...c, rules: c.rules.filter((_, i) => i !== idx) }))}
                          className="text-slate-300 hover:text-red-500 ml-auto"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {recoveryStats && (
                  <div className="grid grid-cols-3 gap-3 pt-1">
                    {[
                      { label: "Sent", value: recoveryStats.sent },
                      { label: "Clicked", value: recoveryStats.clicked },
                      { label: "Converted", value: recoveryStats.converted },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold text-foreground">{value}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{label} <span className="text-slate-400">(30 days)</span></p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end pt-1">
                  <Button size="sm" disabled={recoverySaving} onClick={async () => {
                    if (!storeId) return;
                    setRecoverySaving(true);
                    try {
                      await fetch(`/api/order/stores/${storeId}/recovery-config`, {
                        method: "POST",
                        headers: authHeaders(),
                        body: JSON.stringify(recoveryConfig),
                      });
                      toast({ title: "Cart recovery saved" });
                    } catch { toast({ title: "Save failed", variant: "destructive" }); }
                    finally { setRecoverySaving(false); }
                  }}>
                    {recoverySaving ? "Saving…" : "Save recovery settings"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Settings tab */}
      {tab === "settings" && (
        <div className="space-y-4">
          {store.slug === "premier-dean-street" && <WhiteLabelHomepageEditor storeId={store.id} siteUrl="/premier/" />}
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            {editing ? (
              <StoreForm initial={store} onSave={(d) => void saveStore(d as Partial<StoreData>)} onCancel={() => setEditing(false)} />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-xs text-slate-400">Name</p><p className="font-semibold">{store.name}</p></div>
                  <div><p className="text-xs text-slate-400">Categories</p><p className="font-semibold">{(store.categories?.length ? store.categories : [store.category]).join(", ")}</p></div>
                  <div><p className="text-xs text-slate-400">Town</p><p className="font-semibold">{store.town}</p></div>
                  <div><p className="text-xs text-slate-400">Address</p><p className="font-semibold">{store.address}</p></div>
                  <div><p className="text-xs text-slate-400">Delivery</p><p className="font-semibold">{store.offersDelivery ? `${store.deliveryTimeMins} mins` : "No"}</p></div>
                  <div><p className="text-xs text-slate-400">Collection</p><p className="font-semibold">{store.offersCollection ? `${store.collectionTimeMins} mins` : "No"}</p></div>
                  <div><p className="text-xs text-slate-400">Accepts cash</p><p className="font-semibold">{store.acceptsCash ? "Yes" : "No"}</p></div>
                  {(store.bagChargePence ?? 0) > 0 && <div><p className="text-xs text-slate-400">Bag charge</p><p className="font-semibold">£{((store.bagChargePence ?? 0) / 100).toFixed(2)}</p></div>}
                  {store.openingHours && (() => {
                    const DAY_LABELS: Record<string, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
                    let parsed: Record<string, { open: string; close: string } | null> | null = null;
                    try { parsed = JSON.parse(store.openingHours!); } catch { /* not JSON */ }
                    if (!parsed) return <div className="col-span-2"><p className="text-xs text-slate-400">Opening Hours</p><p className="font-semibold text-sm">{store.openingHours}</p></div>;
                    const lines = Object.entries(parsed).map(([day, val]) => ({
                      label: DAY_LABELS[day] ?? day,
                      value: val ? `${val.open}–${val.close}` : "Closed",
                    }));
                    return (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-400 mb-1">Opening Hours</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                          {lines.map(({ label, value }) => (
                            <div key={label} className="flex justify-between text-sm">
                              <span className="font-medium text-slate-600 w-10">{label}</span>
                              <span className={`font-semibold ${value === "Closed" ? "text-slate-400" : ""}`}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <Button onClick={() => setEditing(true)} className="gap-1"><Pencil size={14} /> Edit Settings</Button>
              </div>
            )}
          </div>

          {/* Hub drivers card */}
          {store.offersDelivery && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Truck size={16} className="text-blue-600" />
                <h3 className="font-bold text-foreground text-sm">Delivery Drivers</h3>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {store.useHubDrivers ? "Using Ayrshire Hub Drivers" : "Using my own drivers"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {store.useHubDrivers ? "Hub drivers are notified when orders are ready for pickup" : "You arrange your own delivery couriers"}
                  </p>
                </div>
                <button
                  onClick={() => void toggleHubDrivers()}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${store.useHubDrivers ? "bg-emerald-500" : "bg-slate-200"}`}
                  role="switch"
                  aria-checked={!!store.useHubDrivers}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ${store.useHubDrivers ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
              {store.useHubDrivers && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-lg">
                  <Truck size={13} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">Delivery fees go directly to your driver — not your Stripe balance. You don't need your own delivery couriers.</p>
                </div>
              )}
            </div>
          )}

          {/* Receipt Template — admin only */}
          {user?.role === "admin" && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                <h3 className="font-bold text-foreground text-sm">Receipt Template</h3>
              </div>
              <p className="text-xs text-slate-500">
                The receipt design (logo, header text, footer, dividers) is set globally by the site admin and applies to all stores.
              </p>
              <a
                href="/admin/receipt-design"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-semibold hover:bg-[#162d4a]"
              >
                Open Receipt Designer →
              </a>
            </div>
          )}

          {/* Delivery zones */}
          <DeliveryZonesEditor storeId={store.id} />
        </div>
      )}

      {/* Driver chat modal */}
      {driverChatOrderId !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeDriverChat} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 bg-primary rounded-t-2xl sm:rounded-t-2xl">
              <h3 className="font-bold text-white">Message Driver — Order #{driverChatOrderId}</h3>
              <button onClick={closeDriverChat} className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {driverChatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-400">
                  <MessageSquare size={32} className="opacity-30" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : driverChatMessages.map(msg => (
                <div key={msg.id} className="space-y-0.5">
                  <p className="text-xs font-semibold text-slate-500">{msg.senderName ?? "Unknown"}</p>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-sm text-slate-800"><MessageImages ids={msg.attachmentIds}/>{msg.body}</div>
                  <p className="text-[10px] text-slate-400">{new Date(msg.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              ))}
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-slate-100 flex gap-2">
              {driverChatOrderId&&driverAssignments[driverChatOrderId]&&<ChatImageComposer key={driverChatOrderId} endpoint={`/api/orders/${driverChatOrderId}/driver-messages`} recipientUserId={driverAssignments[driverChatOrderId].driverId} onSent={()=>loadDriverMessages(driverChatOrderId)}/>}
<input
                value={driverChatInput}
                onChange={(e) => setDriverChatInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendDriverMessage(); } }}
              />
              <Button onClick={() => void sendDriverMessage()} disabled={driverChatSending || !driverChatInput.trim()} className="gap-1.5">
                Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe / Payments tab */}
      {tab === "library" && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Library size={18} className="text-indigo-600" />
            <h3 className="font-bold text-foreground">Option Group Library</h3>
          </div>
          <SharedGroupLibrary storeId={storeId} />
        </div>
      )}

      {tab === "staff" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus size={18} className="text-primary" />
              <h3 className="font-bold text-foreground">Invite Staff or Manager</h3>
            </div>
            <form onSubmit={(e) => void sendInvite(e)} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                className="flex-1"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as "staff" | "manager")}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="staff">Staff — orders only</option>
                <option value="manager">Manager — full access</option>
              </select>
              <Button type="submit" disabled={inviting || !inviteEmail.trim()} className="gap-1 whitespace-nowrap">
                <Mail size={14} /> {inviting ? "Sending…" : "Send Invite"}
              </Button>
            </form>
            <p className="text-xs text-slate-400 mt-2">
              <strong>Staff</strong> can view and manage orders. <strong>Managers</strong> have full store access (menu, settings, offers) but cannot connect/disconnect Stripe.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-primary" />
                <h3 className="font-bold text-foreground">Current Team</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => void loadStaff()} className="text-slate-400 text-xs">Refresh</Button>
            </div>
            {staffLoading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : staffList.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Users size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No staff yet. Send an invite above.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {staffList.map((member) => (
                  <div key={member.id} className="flex items-center gap-3 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-800 text-sm truncate">
                          {member.name ?? member.email}
                        </p>
                        <Badge className={member.role === "manager" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}>
                          {member.role === "manager" ? <><Crown size={10} className="inline mr-0.5" />Manager</> : "Staff"}
                        </Badge>
                        {!member.inviteAcceptedAt && (
                          <Badge className="bg-slate-100 text-slate-500 text-xs">Invite pending</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {member.role === "staff" ? (
                        <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:bg-amber-900/20"
                          onClick={() => void changeStaffRole(member.id, "manager")}>
                          → Manager
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:bg-blue-900/20"
                          onClick={() => void changeStaffRole(member.id, "staff")}>
                          → Staff
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => void removeStaff(member.id)}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "sms" && (
        <div className="space-y-5">
          {smsLoading ? (
            <div className="text-sm text-slate-400">Loading SMS settings…</div>
          ) : (
            <>
              {/* Settings card */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-foreground">SMS Notifications</h3>
                    <p className="text-xs text-slate-400 mt-0.5">Merchant alerts, customer updates and campaigns. {smsSellPricePence}p per billable SMS segment.</p>
                  </div>
                  <button
                    onClick={() => setSmsSettings(s => ({ ...s, enabled: !s.enabled }))}
                    className={`text-2xl transition-colors ${smsSettings.enabled ? "text-primary" : "text-slate-300"}`}
                  >
                    {smsSettings.enabled ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 8H7m10 0a5 5 0 0 1 0 8H7m10-8V7a5 5 0 0 0-10 0v1m10 8v1a5 5 0 0 1-10 0v-1m3-4h4" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 8H7m10 0a5 5 0 0 1 0 8H7m10-8V7a5 5 0 0 0-10 0v1m10 8v1a5 5 0 0 1-10 0v-1" /></svg>
                    )}
                  </button>
                </div>

                {smsSettings.enabled && (
                  <div className="space-y-3 pt-1">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="text-sm text-slate-700">Merchant order alerts
                        <select className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={smsSettings.merchantNotificationMode} onChange={e => setSmsSettings(s => ({ ...s, merchantNotificationMode: e.target.value }))}>
                          <option value="off">Off</option><option value="always">Always send SMS</option><option value="push_failed">Only when push fails</option>
                        </select>
                      </label>
                      <label className="text-sm text-slate-700">Customer order updates
                        <select className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={smsSettings.customerNotificationMode} onChange={e => setSmsSettings(s => ({ ...s, customerNotificationMode: e.target.value }))}>
                          <option value="off">Off</option><option value="always">Always send SMS</option><option value="push_failed">Only when push fails</option>
                        </select>
                      </label>
                    </div>
                    <label className="block text-sm text-slate-700">Merchant alert mobile number
                      <Input className="mt-1" type="tel" inputMode="tel" autoComplete="tel" placeholder="07…" value={smsSettings.alertPhoneE164 ?? ""} onChange={e => setSmsSettings(s => ({ ...s, alertPhoneE164: e.target.value }))} />
                    </label>
                    {[
                      { key: "orderConfirmationEnabled" as const, label: "Order confirmation", desc: "Send SMS when order is placed" },
                      { key: "orderStatusUpdatesEnabled" as const, label: "Status updates", desc: "Ready, out for delivery, delivered" },
                      { key: "marketingEnabled" as const, label: "Marketing campaigns", desc: "Allow marketing blasts to your customers" },
                    ].map(({ key, label, desc }) => (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                        <div>
                          <div className="text-sm font-medium text-slate-700">{label}</div>
                          <div className="text-xs text-slate-400">{desc}</div>
                        </div>
                        <input
                          type="checkbox" checked={smsSettings[key] as boolean}
                          onChange={e => setSmsSettings(s => ({ ...s, [key]: e.target.checked }))}
                          className="w-4 h-4 accent-primary"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-1">
                  <div className="text-sm text-slate-500">
                    Balance: <span className={`font-semibold ${smsSettings.smsBalancePence < 100 ? "text-red-500" : "text-slate-800"}`}>
                      £{(smsSettings.smsBalancePence / 100).toFixed(2)}
                    </span>
                    <span className="block text-xs text-slate-400">Debt limit: £{(smsMaximumDebtPence / 100).toFixed(2)}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      setSmsSaving(true);
                      const res = await fetch(`/api/order/stores/${storeId}/sms-settings`, {
                        method: "PUT",
                        headers: authHeaders(),
                        body: JSON.stringify({ ...smsSettings, alertPhone: smsSettings.alertPhoneE164 }),
                      });
                      setSmsSaving(false);
                      if (res.ok) {
                        const d = await res.json() as { settings: typeof smsSettings };
                        setSmsSettings(d.settings);
                        toast({ title: "SMS settings saved" });
                      } else {
                        toast({ title: "Failed to save", variant: "destructive" });
                      }
                    }}
                    disabled={smsSaving}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    {smsSaving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
                  <h3 className="font-bold text-foreground">Buy SMS credit</h3>
                  <SmsTopup storeId={storeId} minimumPence={smsMinimumTopupPence} onComplete={() => { setSmsRefresh(v => v + 1); toast({ title: "SMS credit added" }); }} />
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
                  <h3 className="font-bold text-foreground">Sender ID</h3>
                  <p className="text-xs text-slate-500">Messages currently send as <strong>{smsActiveSenderId ?? "the platform default"}</strong>. Request a 3-11 character business sender ID for admin approval.</p>
                  <div className="flex gap-2"><Input maxLength={11} placeholder="YourBrand" value={smsSenderRequest} onChange={e => setSmsSenderRequest(e.target.value)} /><Button onClick={async () => {
                    const response = await fetch(`/api/order/stores/${storeId}/sms-sender-ids`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ senderId: smsSenderRequest }) });
                    const data = await response.json() as { error?: string };
                    if (!response.ok) { toast({ title: data.error ?? "Request failed", variant: "destructive" }); return; }
                    setSmsSenderRequest(""); setSmsRefresh(v => v + 1); toast({ title: "Sender ID requested" });
                  }}>Request</Button></div>
                  <div className="space-y-1">{smsSenderItems.map(item => <div key={item.id} className="flex justify-between text-xs"><span>{item.senderId}</span><span className="capitalize text-slate-500">{item.status.replace(/_/g, " ")}{item.rejectionReason ? `: ${item.rejectionReason}` : ""}</span></div>)}</div>
                </div>
              </div>

              {/* Campaign section */}
              {smsSettings.enabled && smsSettings.marketingEnabled && (
                <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
                  <h3 className="font-bold text-foreground">Marketing Campaigns</h3>
                  <div className="space-y-3">
                    <Input
                      placeholder="Campaign title"
                      value={newSmsTitle}
                      onChange={e => setNewSmsTitle(e.target.value)}
                    />
                    <Textarea
                      rows={3}
                      placeholder="SMS message (160 chars = 1 credit)"
                      value={newSmsMsg}
                      onChange={e => setNewSmsMsg(e.target.value)}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">{newSmsMsg.length}/160</span>
                      <Button
                        size="sm"
                        disabled={!newSmsTitle.trim() || !newSmsMsg.trim() || creatingSmscamp}
                        className="bg-primary hover:bg-primary/90 text-white"
                        onClick={async () => {
                          setCreatingSmscamp(true);
                          const res = await fetch(`/api/order/stores/${storeId}/sms-campaigns`, {
                            method: "POST",
                            headers: authHeaders(),
                            body: JSON.stringify({ title: newSmsTitle.trim(), message: newSmsMsg.trim() }),
                          });
                          setCreatingSmscamp(false);
                          if (res.ok) {
                            setNewSmsTitle(""); setNewSmsMsg("");
                            const campsRes = await fetch(`/api/order/stores/${storeId}/sms-campaigns`, { headers: authHeaders() });
                            if (campsRes.ok) { const d = await campsRes.json() as { items: typeof smsCampaigns }; setSmsCampaigns(d.items ?? []); }
                            toast({ title: "Campaign created" });
                          } else {
                            const d = await res.json() as { error?: string };
                            toast({ title: d.error ?? "Failed to create", variant: "destructive" });
                          }
                        }}
                      >
                        {creatingSmscamp ? "Creating…" : "Create Draft"}
                      </Button>
                    </div>
                  </div>

                  {smsCampaignsLoading ? <div className="text-sm text-slate-400">Loading campaigns…</div> : (
                    <div className="space-y-2">
                      {smsCampaigns.map(c => (
                        <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div>
                            <div className="text-sm font-medium text-slate-800">{c.title}</div>
                            <div className="text-xs text-slate-400 truncate max-w-[220px]">{c.message}</div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              <span className={`font-medium ${c.status === "sent" ? "text-green-600" : c.status === "sending" ? "text-blue-600" : "text-slate-500"}`}>{c.status}</span>
                              {c.status === "sent" && <> · {c.totalSent} sent · £{(c.actualCostPence / 100).toFixed(2)}</>}
                            </div>
                          </div>
                          {c.status === "draft" && (
                            <Button
                              size="sm"
                              disabled={sendingSmsCampId === c.id}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                              onClick={async () => {
                                if (!confirm("Send this campaign to all your customers now?")) return;
                                setSendingSmsCampId(c.id);
                                const res = await fetch(`/api/order/stores/${storeId}/sms-campaigns/${c.id}/send`, { method: "POST", headers: authHeaders() });
                                setSendingSmsCampId(null);
                                if (res.ok) {
                                  const d = await res.json() as { recipientCount: number };
                                  toast({ title: `Sending to ${d.recipientCount} recipients` });
                                  const campsRes = await fetch(`/api/order/stores/${storeId}/sms-campaigns`, { headers: authHeaders() });
                                  if (campsRes.ok) { const d2 = await campsRes.json() as { items: typeof smsCampaigns }; setSmsCampaigns(d2.items ?? []); }
                                } else {
                                  const d = await res.json() as { error?: string };
                                  toast({ title: d.error ?? "Failed to send", variant: "destructive" });
                                }
                              }}
                            >
                              {sendingSmsCampId === c.id ? "Sending…" : "Send Now"}
                            </Button>
                          )}
                        </div>
                      ))}
                      {smsCampaigns.length === 0 && <p className="text-sm text-slate-400">No campaigns yet</p>}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "stripe" && (
        <div className="space-y-4">
          {/* Ayrshire Hub account statement — the shop's running balance with us */}
          <AccountStatementPanel store={store} />
          {/* Not connected */}
          {!store.stripeOnboardingComplete && (
            staffRole !== "owner" ? (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                <p className="font-semibold mb-1">Stripe not yet connected</p>
                <p>Only the store owner can connect a Stripe account. Ask them to visit this tab to complete setup.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
                <h3 className="font-bold text-foreground">Connect Stripe to enable payments &amp; reporting</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  <p className="font-semibold mb-1">Connect your Stripe account to receive payments</p>
                  <p>You'll be redirected to Stripe to connect your existing account or create a new one. Your existing payment routing and agreed deductions remain in place. Your business keeps its protected menu price entitlement before separately agreed adjustments.</p>
                </div>
                <Button className="bg-[#635bff] hover:bg-[#5851e0] text-white gap-2 font-semibold" onClick={() => void connectStripe()} disabled={stripeConnectLoading}>
                  <Link2 size={16} /> {stripeConnectLoading ? "Redirecting..." : "Connect Stripe Account"}
                </Button>
                <p className="text-xs text-slate-400">You need a Stripe account. It's free to create one at stripe.com</p>
              </div>
            )
          )}

          {store.stripeOnboardingComplete && (
            <>
              {/* ── Live Balance ── */}
              <div className="bg-white rounded-2xl border border-slate-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-700 text-sm">Live Balance</h4>
                  <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <CheckCircle2 size={11} /> Stripe Connected
                  </span>
                </div>
                {stripeOverviewLoading ? (
                  <div className="flex items-center gap-2 py-4 text-slate-400 text-sm"><LoaderCircle size={16} className="animate-spin" /> Loading…</div>
                ) : stripeOverview ? (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                      <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Available</p>
                      <p className="text-xl font-bold text-emerald-700">{stripeOverview.balance.available.map((b) => pence(b.amount)).join(", ") || "£0.00"}</p>
                      <p className="text-xs text-emerald-500 mt-0.5">Ready to pay out</p>
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                      <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Pending</p>
                      <p className="text-xl font-bold text-amber-600">{stripeOverview.balance.pending.map((b) => pence(b.amount)).join(", ") || "£0.00"}</p>
                      <p className="text-xs text-amber-500 mt-0.5">In transit</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Next Payout</p>
                      {(() => {
                        const next = stripeOverview.payouts.find((p) => p.status === "in_transit" || p.status === "pending");
                        return next ? (
                          <>
                            <p className="text-xl font-bold text-slate-700">{pence(next.amount)}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{new Date(next.arrival_date * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                          </>
                        ) : <p className="text-sm text-slate-400 mt-1">—</p>;
                      })()}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* ── Recent Payouts (expandable) ── */}
              {stripeOverview && stripeOverview.payouts.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-slate-700 text-sm">Recent Payouts</p>
                    <p className="text-[11px] text-slate-400">{stripeOverview.payouts.length} total</p>
                  </div>
                  <p className="text-[11px] text-slate-400 mb-3">Click any row to see the full charge breakdown with all fees.</p>
                  <div className="space-y-1.5">
                    {stripeOverview.payouts.slice(payoutPage * PAYOUT_PAGE_SIZE, (payoutPage + 1) * PAYOUT_PAGE_SIZE).map((p) => {
                      const isEx = expandedRecentPayoutId === p.id;
                      const detail = recentPayoutDetails.get(p.id);
                      const loading = recentPayoutLoading === p.id;
                      const arrDate = new Date(p.arrival_date * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                      const sc = p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "in_transit" ? "bg-blue-100 text-blue-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
                      const sl = p.status === "in_transit" ? "In Transit" : p.status === "paid" ? "Paid" : p.status === "pending" ? "Scheduled" : p.status;
                      return (
                        <div key={p.id} className="border border-slate-100 rounded-xl overflow-hidden">
                          <button
                            className="w-full flex items-center gap-3 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                            onClick={() => {
                              if (isEx) { setExpandedRecentPayoutId(null); return; }
                              setExpandedRecentPayoutId(p.id);
                              void loadRecentPayoutDetail(p.id);
                            }}
                          >
                            <span className="text-xs text-slate-600 flex-1">{arrDate}{p.description ? <span className="text-slate-400 ml-1.5">· {p.description}</span> : null}</span>
                            <span className="text-sm font-bold font-mono text-slate-800">{pence(p.amount)}</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc}`}>{sl}</span>
                            <span className="font-mono text-slate-300 text-[10px]">{p.id.slice(-10)}</span>
                            {loading ? <LoaderCircle size={13} className="animate-spin text-slate-400 flex-shrink-0" /> : isEx ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
                          </button>
                          {isEx && (
                            loading ? (
                              <div className="px-4 py-3 text-xs text-slate-400 flex items-center gap-2"><LoaderCircle size={13} className="animate-spin" /> Loading charge breakdown…</div>
                            ) : detail ? (
                              <div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-3 bg-white border-b border-slate-100">
                                  {[
                                    { label: "Gross", val: detail.totalGrossPence, c: "text-slate-800" },
                                    { label: "Stripe Fee", val: detail.totalStripeProcessingFeePence, c: "text-red-500" },
                                    { label: "AH/Driver Fees", val: detail.totalApplicationFeePence, c: "text-red-500" },
                                    { label: "Net to Bank", val: detail.totalNetToStorePence, c: "text-emerald-600 font-bold" },
                                  ].map(({ label, val, c }) => (
                                    <div key={label} className="text-center">
                                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                                      <p className={`text-xs font-mono mt-0.5 ${c}`}>{pence(val)}</p>
                                    </div>
                                  ))}
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs min-w-[560px]">
                                    <thead>
                                      <tr className="bg-slate-700 text-white">
                                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                                        <th className="text-left px-2 py-2 font-semibold">Customer</th>
                                        <th className="text-left px-2 py-2 font-semibold">Ref</th>
                                        <th className="text-right px-2 py-2 font-semibold">Gross</th>
                                        <th className="text-right px-2 py-2 font-semibold">Stripe Fee</th>
                                        <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                                        <th className="text-right px-2 py-2 font-semibold">Net to Store</th>
                                        <th className="text-right px-3 py-2 font-semibold">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.charges.length === 0 ? (
                                        <tr><td colSpan={8} className="px-4 py-3 text-slate-400 text-center">No charges</td></tr>
                                      ) : detail.charges.map((c, ci) => {
                                        const dt = c.order ? new Date(c.order.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                                        const osl = c.order?.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : c.order?.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : c.order ? <span className="text-blue-600 capitalize">{c.order.status}</span> : <span className="text-amber-600">No match</span>;
                                        return (
                                          <tr key={c.chargeId} className={`border-b border-slate-50 last:border-0 ${!c.order ? "bg-amber-50" : ci % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                                            <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{dt}</td>
                                            <td className="px-2 py-1.5 text-slate-700 max-w-[100px] truncate">{c.order?.customerName ?? "—"}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-300 text-[10px]">{(c.paymentIntentId ?? c.chargeId).slice(-10)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(c.grossPence)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-red-400">−{pence(c.stripeProcessingFeePence)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-red-400">{c.applicationFeePence > 0 ? `−${pence(c.applicationFeePence)}` : "—"}</td>
                                            <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(c.netToStorePence)}</td>
                                            <td className="px-3 py-1.5 text-right">{osl}</td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                        <td colSpan={3} className="px-3 py-2 text-right text-slate-500">Payout Total</td>
                                        <td className="px-2 py-2 text-right font-mono text-slate-800">{pence(detail.totalGrossPence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(detail.totalStripeProcessingFeePence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(detail.totalApplicationFeePence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(p.amount)}</td>
                                        <td></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {stripeOverview.payouts.length > PAYOUT_PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <button
                        onClick={() => setPayoutPage((p) => Math.max(0, p - 1))}
                        disabled={payoutPage === 0}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >← Newer</button>
                      <p className="text-[11px] text-slate-400">
                        {payoutPage * PAYOUT_PAGE_SIZE + 1}–{Math.min((payoutPage + 1) * PAYOUT_PAGE_SIZE, stripeOverview.payouts.length)} of {stripeOverview.payouts.length}
                      </p>
                      <button
                        onClick={() => setPayoutPage((p) => p + 1)}
                        disabled={(payoutPage + 1) * PAYOUT_PAGE_SIZE >= stripeOverview.payouts.length}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >Older →</button>
                    </div>
                  )}
                </div>
              )}

              {/* ── Period Report ── */}
              <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-700 text-sm">Reconciliation Report</h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">Full breakdown from Stripe payouts matched to your orders. Shows exactly what Stripe kept and what Ayrshire Hub/driver collected, so you can see precisely what lands in your bank.</p>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">From</label>
                    <input type="date" value={stripeReportFrom} onChange={(e) => setStripeReportFrom(e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-primary/40" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">To</label>
                    <input type="date" value={stripeReportTo} onChange={(e) => setStripeReportTo(e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-primary/40" />
                  </div>
                  <Button variant="outline" className="gap-2" onClick={() => void loadStripeReport()} disabled={stripeReportLoading || !stripeReportFrom || !stripeReportTo}>
                    {stripeReportLoading ? <><LoaderCircle size={14} className="animate-spin" /> Loading…</> : "Load Report"}
                  </Button>
                  <Button className="gap-2 bg-primary hover:bg-primary/90 text-white" onClick={() => void downloadStripeReport()} disabled={stripeReportDownloading || !stripeReportFrom || !stripeReportTo}>
                    {stripeReportDownloading ? <><LoaderCircle size={14} className="animate-spin" /> Generating…</> : <><FileText size={13} /> Download PDF</>}
                  </Button>
                </div>

                {stripeReportData && (
                  <div className="space-y-5">
                    {/* KPI summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
                      {[
                        { label: "Gross Charged", value: stripeReportData.grandTotalGrossPence, color: "text-slate-800", sub: "Payments in arrived payouts" },
                        { label: "Stripe Fee", value: stripeReportData.grandTotalStripeProcessingFeePence, color: "text-red-500", sub: "Card processing" },
                        { label: "AH/Driver Fees", value: stripeReportData.grandTotalApplicationFeePence, color: "text-red-500", sub: "Hub & driver costs" },
                        { label: "Net to Your Bank", value: stripeReportData.grandTotalPayoutsPence, color: "text-emerald-600", sub: "Actual payout deposits" },
                        { label: "Food Revenue (DB)", value: stripeReportData.grandPeriodRevenuePence, color: "text-slate-700", sub: "Orders placed in period" },
                      ].map(({ label, value, color, sub }) => (
                        <div key={label} className="bg-slate-50 rounded-xl border border-slate-100 p-2.5 text-center">
                          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1 leading-tight">{label}</p>
                          <p className={`text-sm font-bold font-mono ${color}`}>{pence(value)}</p>
                          <p className="text-[9px] text-slate-400 mt-0.5">{sub}</p>
                        </div>
                      ))}
                    </div>

                    {/* Payouts detail */}
                    {stripeReportData.payouts.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">No payouts found for this period.</p>
                    ) : (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Payout Breakdown ({stripeReportData.payouts.length} payouts)</p>
                        <div className="space-y-2">
                          {stripeReportData.payouts.map((p, pi) => {
                            const isExpanded = expandedPayoutId === p.id;
                            const sc = p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "in_transit" ? "bg-blue-100 text-blue-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
                            const sl = p.status === "in_transit" ? "In Transit" : p.status === "paid" ? "Paid" : p.status;
                            const otherFees = p.totalHubDriverFeePence + p.totalFuelSupplementPence;
                            return (
                              <div key={p.id} className="border border-slate-100 rounded-xl overflow-hidden">
                                <button
                                  className="w-full flex items-center gap-2 px-3 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                                  onClick={() => setExpandedPayoutId(isExpanded ? null : p.id)}
                                >
                                  <span className="text-xs font-bold text-slate-400 w-5 flex-shrink-0">#{pi + 1}</span>
                                  <span className="text-xs text-slate-600 flex-1 min-w-0">{new Date(p.arrivalDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}{p.description ? <span className="text-slate-400"> · {p.description}</span> : null}</span>
                                  <span className="text-[10px] text-slate-400 flex-shrink-0">{p.charges.length}ch</span>
                                  <span className="text-[10px] font-mono text-slate-500 flex-shrink-0 hidden sm:block">Gross {pence(p.totalGrossPence)}</span>
                                  <span className="text-[10px] font-mono text-red-400 flex-shrink-0">Stripe −{pence(p.totalStripeProcessingFeePence)}</span>
                                  {p.totalApplicationFeePence > 0 && <span className="text-[10px] font-mono text-red-400 flex-shrink-0 hidden md:block">AH −{pence(p.totalApplicationFeePence)}</span>}
                                  <span className="text-sm font-bold font-mono text-slate-800 flex-shrink-0">{pence(p.amountPence)}</span>
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${sc}`}>{sl}</span>
                                  {p.unmatchedCount > 0 && <span className="text-[10px] font-semibold text-amber-600 flex-shrink-0">⚠{p.unmatchedCount}</span>}
                                  {isExpanded ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
                                </button>

                                {isExpanded && (
                                  <div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-2.5 bg-white border-b border-slate-100">
                                      {[
                                        { label: "Gross", val: p.totalGrossPence, c: "text-slate-800" },
                                        { label: "Stripe Fee", val: p.totalStripeProcessingFeePence, c: "text-red-500" },
                                        { label: "AH/Driver Fees", val: p.totalApplicationFeePence, c: "text-red-500" },
                                        { label: "Net to Bank", val: p.amountPence, c: "text-emerald-600 font-bold" },
                                      ].map(({ label, val, c }) => (
                                        <div key={label} className="text-center">
                                          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                                          <p className={`text-xs font-mono mt-0.5 ${c}`}>{pence(val)}</p>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs min-w-[560px]">
                                        <thead>
                                          <tr className="bg-slate-700 text-white text-[10px]">
                                            <th className="text-left px-3 py-2 font-semibold">Date &amp; Time</th>
                                            <th className="text-left px-2 py-2 font-semibold">Customer</th>
                                            <th className="text-left px-2 py-2 font-semibold">Ref</th>
                                            <th className="text-right px-2 py-2 font-semibold">Gross</th>
                                            <th className="text-right px-2 py-2 font-semibold">Stripe Fee</th>
                                            <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                                            <th className="text-right px-2 py-2 font-semibold">Net to Store</th>
                                            <th className="text-right px-3 py-2 font-semibold">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {p.charges.length === 0 ? (
                                            <tr><td colSpan={8} className="px-4 py-3 text-slate-400 text-center">No charges</td></tr>
                                          ) : p.charges.map((c, ci) => {
                                            const dt = c.order ? new Date(c.order.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                                            const osl = c.order?.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : c.order?.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : c.order ? <span className="text-blue-600 capitalize">{c.order.status}</span> : <span className="text-amber-600">No match</span>;
                                            return (
                                              <tr key={c.chargeId} className={`border-b border-slate-50 last:border-0 ${!c.order ? "bg-amber-50" : ci % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                                                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{dt}</td>
                                                <td className="px-2 py-1.5 text-slate-700 max-w-[100px] truncate">{c.order?.customerName ?? "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-300 text-[10px]">{(c.paymentIntentId ?? c.chargeId).slice(-10)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(c.grossPence)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono text-red-400">−{pence(c.stripeProcessingFeePence)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono text-red-400">{c.applicationFeePence > 0 ? `−${pence(c.applicationFeePence)}` : "—"}</td>
                                                <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(c.netToStorePence)}</td>
                                                <td className="px-3 py-1.5 text-right">{osl}</td>
                                              </tr>
                                            );
                                          })}
                                          <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                            <td colSpan={3} className="px-3 py-2 text-right text-slate-500">Payout Total</td>
                                            <td className="px-2 py-2 text-right font-mono text-slate-800">{pence(p.totalGrossPence)}</td>
                                            <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(p.totalStripeProcessingFeePence)}</td>
                                            <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(p.totalApplicationFeePence)}</td>
                                            <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(p.amountPence)}</td>
                                            <td></td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Order statement */}
                    {stripeReportData.weeks.length > 0 && (() => {
                    /* The payout data already carries the true Stripe fee and the
                       net that reached the bank, per charge — order_orders'
                       stripe_processing_fee_pence is written by multi-order
                       checkout only and is set on 2 of 620 orders, so it cannot be
                       used. Index the charges by order id and read them here. */
                    const chargeByOrder = new Map<number, { stripeFeePence: number; netPence: number; payoutId: string; arrival: string; status: string }>();
                    for (const p of stripeReportData.payouts) {
                      for (const c of p.charges) {
                        if (c.order) chargeByOrder.set(c.order.id, {
                          stripeFeePence: c.stripeProcessingFeePence,
                          netPence: c.netToStorePence,
                          payoutId: p.id, arrival: p.arrivalDate, status: p.status,
                        });
                      }
                    }
                    return (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Order Statement</p>
                        <p className="text-[11px] text-slate-400 mb-2">Orders placed by week (Mon–Sun). Food Revenue = subtotal + bag charge — what the shop earns. AH/Driver Fees = delivery, driver, and Ayrshire Hub charges collected on your behalf.</p>
                        {/* overflow-x-auto, not hidden. A 600px table inside an
                            overflow-hidden box is clipped on a phone, so Food
                            Revenue and Status simply could not be reached. */}
                        <div className="border border-slate-100 rounded-xl overflow-x-auto">
                          <table className="w-full text-xs min-w-[600px]">
                            <thead>
                              <tr className="bg-slate-800 text-white text-[10px]">
                                <th className="text-left px-3 py-2 font-semibold">Date &amp; Time</th>
                                <th className="text-left px-2 py-2 font-semibold">Customer</th>
                                <th className="text-right px-2 py-2 font-semibold">Subtotal</th>
                                <th className="text-right px-2 py-2 font-semibold">Bag</th>
                                <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                                <th className="text-right px-2 py-2 font-semibold">Gross</th>
                                <th className="text-right px-2 py-2 font-semibold text-emerald-300">Food Revenue</th>
                                <th className="text-right px-3 py-2 font-semibold">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stripeReportData.weeks.map((w) => (
                                <>
                                  <tr key={`wk-${w.periodStart}`} className="bg-slate-700">
                                    <td colSpan={8} className="px-3 py-2 text-white font-semibold text-xs">
                                      {new Date(w.periodStart + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(w.periodEnd + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                      <span className="text-slate-300 ml-2 font-normal">· {w.orders.length} order{w.orders.length !== 1 ? "s" : ""} · Food revenue: {pence(w.totalFoodRevenuePence)}</span>
                                    </td>
                                  </tr>
                                  {w.orders.length === 0 ? (
                                    <tr><td colSpan={8} className="px-3 py-2 text-slate-400 text-center">No orders this week</td></tr>
                                  ) : w.orders.map((o, oi) => {
                                    const food = o.subtotalPence + (o.bagChargePence ?? 0);
                                    const ahFees = o.deliveryFeePence + (o.leg2DeliveryFeePence ?? 0) + (o.customerPlatformFeePence ?? 0) + (o.customerHubDriverFeePence ?? 0) + (o.driverFuelSupplementPence ?? 0);
                                    const gross = food + ahFees;
                                    const osl = o.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : o.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : <span className="text-blue-600 capitalize font-semibold">{o.status}</span>;
                                    const isOpen = expandedReportOrderId === o.id;
                                    /* total_pence is the MERCHANT total and deliberately excludes the
                                       Ayrshire Hub fee. The customer is charged that fee as its own
                                       line item on the checkout — order.ts:3366 pushes "Ayrshire Hub
                                       fee" into lineItems, and the cash and hub-driver paths both
                                       charge serverTotalPence + customerHubFeePence. So every customer
                                       pays it, on every order, and what they actually paid is
                                       total_pence PLUS those fees. Reading total_pence as
                                       "what the customer paid" understates it by exactly the fee. */
                                    const hubFees = (o.customerPlatformFeePence ?? 0) + (o.customerHubDriverFeePence ?? 0);
                                    const customerPaid = o.totalPence + hubFees;
                                    const line = (label: string, v: number, tone = "text-slate-600") =>
                                      v > 0 ? (
                                        <div className="flex justify-between gap-4 py-1">
                                          <span className="text-slate-500">{label}</span>
                                          <span className={`font-mono tabular-nums ${tone}`}>{pence(v)}</span>
                                        </div>
                                      ) : null;
                                    return (
                                      <React.Fragment key={o.id}>
                                      <tr
                                        onClick={() => setExpandedReportOrderId(isOpen ? null : o.id)}
                                        className={`border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-100 ${isOpen ? "bg-slate-100" : oi % 2 === 1 ? "bg-slate-50/50" : ""}`}
                                      >
                                        <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">
                                          <span className="inline-flex items-center gap-1">
                                            {isOpen ? <ChevronUp size={11} className="text-slate-400" /> : <ChevronDown size={11} className="text-slate-400" />}
                                            {new Date(o.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                          </span>
                                        </td>
                                        <td className="px-2 py-1.5 text-slate-700 max-w-[120px] truncate">{o.customerName ?? "Unknown"}</td>
                                        <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(o.subtotalPence)}</td>
                                        <td className="px-2 py-1.5 text-right font-mono text-slate-500">{o.bagChargePence ? pence(o.bagChargePence) : "—"}</td>
                                        <td className="px-2 py-1.5 text-right font-mono text-slate-500">{ahFees > 0 ? pence(ahFees) : "—"}</td>
                                        <td className="px-2 py-1.5 text-right font-mono text-slate-600">{pence(gross)}</td>
                                        <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(food)}</td>
                                        <td className="px-3 py-1.5 text-right">{osl}</td>
                                      </tr>
                                      {isOpen && (
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                          <td colSpan={8} className="p-0">
                                            {/* sticky left keeps the breakdown on screen even when the
                                                wide table is scrolled sideways on a phone — otherwise it
                                                sits off to the right where nobody will find it. */}
                                            <div className="sticky left-0 w-screen max-w-[100vw] sm:w-auto px-3 py-3 grid gap-4 sm:grid-cols-2 sm:max-w-2xl text-[13px]">
                                              <div>
                                                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5 pb-1 border-b border-slate-200">What the customer paid</p>
                                                {line("Food", o.subtotalPence, "text-slate-800")}
                                                {line("Bag charge", o.bagChargePence ?? 0)}
                                                {line("Service charge", o.serviceChargePence ?? 0)}
                                                {line("Delivery fee", o.deliveryFeePence)}
                                                {line("Return leg delivery", o.leg2DeliveryFeePence ?? 0)}
                                                {(o.offerDiscountPence ?? 0) > 0 && (
                                                  <div className="flex justify-between gap-4 py-1">
                                                    <span className="text-slate-500">Discount</span>
                                                    <span className="font-mono tabular-nums text-emerald-600">−{pence(o.offerDiscountPence ?? 0)}</span>
                                                  </div>
                                                )}
                                                {line("Ayrshire Hub service fee", o.customerPlatformFeePence ?? 0)}
                                                {line("Driver fee", o.customerHubDriverFeePence ?? 0)}
                                                <div className="flex justify-between gap-4 border-t border-slate-300 mt-1.5 pt-1.5 font-semibold text-[14px]">
                                                  <span className="text-slate-700">Customer paid</span>
                                                  <span className="font-mono text-slate-800">{pence(customerPaid)}</span>
                                                </div>
                                              </div>
                                              <div>
                                                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5 pb-1 border-b border-slate-200">What you keep</p>
                                                {line("Food", o.subtotalPence, "text-slate-800")}
                                                {line("Bag charge", o.bagChargePence ?? 0)}
                                                {/* The fuel supplement comes out of the SHOP's money, not the
                                                    customer's — the customer total above never includes it. It
                                                    was previously listed as customer-paid, which overstated what
                                                    the shop keeps by exactly that amount. */}
                                                {(o.driverFuelSupplementPence ?? 0) > 0 && (
                                                  <div className="flex justify-between gap-4 py-1">
                                                    <span className="text-slate-500">Driver fuel supplement</span>
                                                    <span className="font-mono tabular-nums text-red-500">−{pence(o.driverFuelSupplementPence ?? 0)}</span>
                                                  </div>
                                                )}
                                                <div className="flex justify-between gap-4 border-t border-slate-300 mt-1.5 pt-1.5 font-semibold text-[14px]">
                                                  <span className="text-slate-700">Your revenue</span>
                                                  <span className="font-mono text-emerald-700">
                                                    {pence(o.storeGrossProceedsPence ?? (food - (o.driverFuelSupplementPence ?? 0)))}
                                                  </span>
                                                </div>
                                                {/* Whose the delivery fee is depends entirely on who drove.
                                                    On a hub order we take it and pay our driver; on the shop's
                                                    own delivery they keep it, and telling them it "was never
                                                    yours" would be plainly false. */}
                                                {o.deliveryFeePence > 0 && (
                                                  o.driverMode === "hub" ? (
                                                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                                      The <strong className="text-slate-500">{pence(o.deliveryFeePence + (o.leg2DeliveryFeePence ?? 0))}</strong> delivery
                                                      fee was charged to the customer on top of your food price and pays the
                                                      Ayrshire Hub driver. It was never yours and has not been taken off your revenue.
                                                    </p>
                                                  ) : (
                                                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                                      The <strong className="text-slate-500">{pence(o.deliveryFeePence + (o.leg2DeliveryFeePence ?? 0))}</strong> delivery
                                                      fee is yours — you delivered this order with your own driver, so it is
                                                      included in your revenue above.
                                                    </p>
                                                  )
                                                )}
                                                {(() => {
                                                  const ch = chargeByOrder.get(o.id);
                                                  if (!ch) return (
                                                    <p className="text-[11px] text-slate-400 mt-2">
                                                      Not yet matched to a Stripe payout — the fee and bank amount appear once it settles.
                                                    </p>
                                                  );
                                                  return (
                                                    <div className="mt-3 pt-2 border-t border-slate-200">
                                                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">To your bank</p>
                                                      <div className="flex justify-between gap-4 py-1">
                                                        <span className="text-slate-500">Stripe processing fee</span>
                                                        <span className="font-mono tabular-nums text-red-500">−{pence(ch.stripeFeePence)}</span>
                                                      </div>
                                                      <div className="flex justify-between gap-4 border-t border-slate-300 mt-1 pt-1.5 font-semibold text-[14px]">
                                                        <span className="text-slate-700">Paid to your bank</span>
                                                        <span className="font-mono text-emerald-700">{pence(ch.netPence)}</span>
                                                      </div>
                                                      <p className="text-[11px] text-slate-400 mt-1.5">
                                                        {ch.status === "paid" ? "Paid" : ch.status} · {new Date(ch.arrival).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                                      </p>
                                                    </div>
                                                  );
                                                })()}
                                                <p className="text-[11px] text-slate-400 mt-2">Order #{o.id}</p>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                      </React.Fragment>
                                    );
                                  })}
                                  <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                    <td colSpan={2} className="px-3 py-2 text-right text-slate-500">Week Total</td>
                                    <td className="px-2 py-2 text-right font-mono text-slate-700">{pence(w.orders.reduce((s, o) => s + o.subtotalPence, 0))}</td>
                                    <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(w.orders.reduce((s, o) => s + (o.bagChargePence ?? 0), 0))}</td>
                                    <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(w.totalDeliveryFeePence + w.totalPlatformFeePence + w.orders.reduce((s, o) => s + (o.customerHubDriverFeePence ?? 0) + (o.driverFuelSupplementPence ?? 0), 0))}</td>
                                    <td className="px-2 py-2 text-right font-mono text-slate-600"></td>
                                    <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(w.totalFoodRevenuePence)}</td>
                                    <td></td>
                                  </tr>
                                </>
                              ))}
                              <tr className="bg-slate-200 font-bold text-xs border-t-2 border-slate-300">
                                <td colSpan={2} className="px-3 py-2 text-right text-slate-700">Period Total</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-700">{pence(stripeReportData.weeks.reduce((s, w) => s + w.orders.reduce((ss, o) => ss + o.subtotalPence, 0), 0))}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(stripeReportData.weeks.reduce((s, w) => s + w.orders.reduce((ss, o) => ss + (o.bagChargePence ?? 0), 0), 0))}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(stripeReportData.grandTotalDeliveryFeePence + stripeReportData.grandTotalPlatformFeePence + stripeReportData.grandTotalHubDriverFeePence + stripeReportData.grandTotalFuelSupplementPence)}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-600"></td>
                                <td className="px-2 py-2 text-right font-mono text-emerald-700 text-sm">{pence(stripeReportData.grandPeriodRevenuePence)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Store List / My Stores ────────────────────────────────────────────────────
interface LinkableBiz { id: number; name: string; town: string; address: string; phone: string | null; email: string | null; }

export default function BusinessOrderStore() {
  const [, params] = useRoute("/business/order-store/:id");
  const storeId = params?.id ? Number(params.id) : null;
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [linkableBizzes, setLinkableBizzes] = useState<LinkableBiz[]>([]);
  const [selectedLinkBiz, setSelectedLinkBiz] = useState<LinkableBiz | null>(null);
  const [prefillData, setPrefillData] = useState<Partial<StoreData> | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [storeRes, bizRes] = await Promise.all([
        fetch("/api/order/my-stores", { headers: authHeaders() }),
        fetch("/api/my-businesses", { headers: authHeaders() }),
      ]);
      const storeData = await storeRes.json() as { items: StoreData[] };
      const bizData = await bizRes.json() as { items: Array<{ id: number; name: string; town: string; address: string; phone: string | null; email: string | null; approved: boolean; wantsOrdering?: boolean }> };
      const storeList = storeData.items ?? [];
      setStores(storeList);
      const linkedIds = new Set(storeList.map(s => s.businessId).filter(Boolean));
      const unlinked = (bizData.items ?? []).filter(b => b.approved && b.wantsOrdering && !linkedIds.has(b.id));
      setLinkableBizzes(unlinked);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function applyPrefill(biz: LinkableBiz) {
    setSelectedLinkBiz(biz);
    setPrefillData({ name: biz.name, town: biz.town, address: biz.address, phone: biz.phone ?? undefined, email: biz.email ?? undefined });
  }

  async function createStore(d: Partial<StoreData>) {
    const payload = { ...d, ...(selectedLinkBiz ? { businessId: selectedLinkBiz.id } : {}) };
    const res = await fetch("/api/order/stores", { method: "POST", headers: authHeaders(), body: JSON.stringify(payload) });
    if (!res.ok) { toast({ title: "Failed to create store", variant: "destructive" }); return; }
    const store = await res.json() as StoreData;
    toast({ title: "Store registered! It will be reviewed by an admin." });
    setCreating(false);
    setPrefillData(undefined);
    setSelectedLinkBiz(null);
    navigate(`/business/order-store/${store.id}`);
  }

  if (!user) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Please <a href="/login" className="underline font-semibold">log in</a> to manage your store.</p></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {storeId && <div className="mx-auto max-w-6xl px-4"><AgencyAgreementPanel storeId={storeId}/></div>}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-center gap-3">
            {storeId && (
              <button onClick={() => navigate("/business/order-store")} className="text-slate-400 hover:text-slate-700"><ArrowLeft size={20} /></button>
            )}
            <div className="flex-1">
              <p className="text-xs text-slate-500">Business Portal</p>
              <h1 className="text-2xl font-bold text-primary">My Shop</h1>
            </div>
            {!storeId && !creating && (
              <Button className="bg-primary hover:bg-primary/90 gap-1" onClick={() => setCreating(true)}>
                <Plus size={16} /> Register Shop
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {storeId ? (
          <StorePanel storeId={storeId} staffRole={stores.find(s => s.id === storeId)?.staffRole ?? "owner"} />
        ) : creating ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-bold text-foreground text-lg mb-4">Register your shop</h2>
            <p className="text-sm text-slate-500 mb-6">Your shop will be reviewed by our team before going live. KEEP 100% OF YOUR AGREED MENU PRICE — GUARANTEED. Existing separately agreed adjustments still apply. Commission arrangements follow your applicable agreement; revised agency terms require approval and explicit acceptance.</p>
            {linkableBizzes.length > 0 && (
              <div className="mb-5 rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-2">
                <p className="text-sm font-semibold text-orange-800">You opted in for online ordering on your business listing{linkableBizzes.length === 1 ? ` (${linkableBizzes[0]?.name})` : ""}.</p>
                <p className="text-xs text-orange-600">Pre-fill the form and link this shop to your directory listing so customers can order directly from it.</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {linkableBizzes.map(biz => (
                    <button
                      key={biz.id}
                      type="button"
                      onClick={() => applyPrefill(biz)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedLinkBiz?.id === biz.id ? "bg-orange-500 text-white border-orange-500" : "bg-white text-orange-700 border-orange-300 hover:bg-orange-100"}`}
                    >
                      {selectedLinkBiz?.id === biz.id ? "✓ " : ""}Pre-fill from {biz.name}
                    </button>
                  ))}
                  {selectedLinkBiz && (
                    <button type="button" onClick={() => { setSelectedLinkBiz(null); setPrefillData(undefined); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 border border-slate-200 hover:bg-slate-100 transition-all">
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
            <StoreForm initial={prefillData} onSave={(d) => void createStore(d as Partial<StoreData>)} onCancel={() => setCreating(false)} />
          </div>
        ) : loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
        ) : stores.length === 0 ? (
          <div className="text-center py-20">
            <Store size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="font-semibold text-slate-600 mb-1">No shops registered</h3>
            <p className="text-sm text-slate-400 mb-5">Register your shop to start receiving orders on Ayrshire Hub</p>
            <Button className="bg-primary hover:bg-primary/90 gap-1" onClick={() => setCreating(true)}><Plus size={16} /> Register a Shop</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {stores.map((store) => (
              <button key={store.id} onClick={() => navigate(`/business/order-store/${store.id}`)}
                className="w-full bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4 hover:border-primary/30 hover:shadow-sm transition-all text-left">
                {store.imageUrl && <img src={store.imageUrl} alt={store.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground">{store.name}</p>
                  <p className="text-sm text-slate-500">{(store.categories?.length ? store.categories : [store.category]).join(" · ")} · {store.town}</p>
                </div>
                <div className="flex gap-1">
                  <Badge className={store.approved ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"}>
                    {store.approved ? "Approved" : "Pending"}
                  </Badge>
                  <Badge className={store.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                    {store.isActive ? "Live" : "Inactive"}
                  </Badge>
                </div>
                <ChevronRight size={18} className="text-slate-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
